import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { DashboardContext } from '../context/DashboardContext'
import { useToast } from '../context/ToastContext'
import useAuthStore from '../store/auth-store'
import { AppHeaderShell } from '../components/AppHeaderShell'
import { AdminSignInModal } from '../components/dashboard/AdminSignInModal'
import { EditModeGate } from '../components/dashboard/EditModeGate'
import { resolveIanaTz, wallClockPartsInTz, formatMediaTimestamp } from '../lib/tz'

// ─── Persisted sidebar selection ──────────────────────────────────────────────
// Sidebar parameter checkboxes survive page reload AND logout/login on the
// same browser.  Scoped by (userId, projectId) so different users sharing a
// browser don't see each other's selections, and switching projects doesn't
// leak selections across them.  Storage isn't cleared by sessionExpiry's
// clearAuthStorage() — only auth_token / refresh_token / auth_user are
// removed on logout, so wq_selparams_* keys persist as required.
const SELPARAMS_KEY = (userId, projectId) =>
  `wq_selparams_u${userId || 'anon'}_p${projectId || 'noproject'}`

function loadSelParams(userId, projectId) {
  if (!userId || !projectId) return null
  try {
    const raw = localStorage.getItem(SELPARAMS_KEY(userId, projectId))
    if (!raw) return null
    const v = JSON.parse(raw)
    if (!v || !Array.isArray(v.selectedParameters)) return null
    return {
      selectedParameters: v.selectedParameters,
      checkedItems: v.checkedItems && typeof v.checkedItems === 'object'
        ? v.checkedItems
        : Object.fromEntries(v.selectedParameters.map(p => [p.id, true])),
    }
  } catch { return null }
}

function saveSelParams(userId, projectId, selectedParameters, checkedItems) {
  if (!userId || !projectId) return
  try {
    localStorage.setItem(
      SELPARAMS_KEY(userId, projectId),
      JSON.stringify({ selectedParameters, checkedItems })
    )
  } catch { /* quota / private-mode — best effort */ }
}

// Consistent "coming soon" scaffold for Settings sub-pages that
// exist in the menu but don't have real content yet (Public Portal,
// Embedded Data Applet, RSS Feed). Keeps the router surface stable so
// deep links + localStorage-driven index restore never miss a page.
function SettingsPlaceholder({ title, subtitle }) {
  return (
    <div className="max-w-4xl w-full mx-auto bg-white border border-gray-200 rounded-lg text-center">
    <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
      <h2 className="common-head25 text-lg font-bold text-[#4D4D4D]">{title}</h2>
    </div>
    <div className="px-6 py-4 overflow-y-auto">
      {subtitle && <p className="common-size15 font-semibold text-xs text-[#4D4D4D] mb-4">{subtitle}</p>}
      <p className="common-size15 font-semibold text-xs text-[#4D4D4D] mb-1 uppercase tracking-wide">Coming soon</p>
    </div>
    </div>
  )
}
import { ChevronDown, ChevronRight, Settings, Bell, Eye, Edit2, Search, Loader, GripVertical, X, Plus, RefreshCw, Clock } from 'lucide-react'
import { createPortal } from 'react-dom'
import { projectsApi } from '../lib/projectsApi'
import { dashboardApi } from '../lib/dashboardApi'
import widgetApi from '../lib/widgetApi'
import { NoActiveProject } from '../components/NoActiveProject'
import { DynamicGraph } from '../components/DynamicGraph'
import { CurrentConditionsView } from '../components/CurrentConditionsView'
import { SystemPerformanceView } from '../components/SystemPerformanceView'
import { DynamicGraphCard } from '../components/DynamicGraphCard'
import { TemplateDashboardGrid } from '../components/dashboard/TemplateDashboardGrid'
import PresentationSettingsModal from '../components/dashboard/PresentationSettingsModal'
import ConfigDashboardsModal from '../components/dashboard/ConfigDashboardsModal'
import DataAnalyticsView from '../components/dashboard/DataAnalytics/DataAnalyticsView'
import { ParameterConfigModal } from '../components/dashboard/ParameterConfigModal'
import AllDataView from '../components/dashboard/AllDataView'
import ReportView, { ReportListModal } from '../components/dashboard/ReportView'
import ApiKeysPanel from '../components/dashboard/settings/ApiKeysPanel'
import StreamingMethodPanel from '../components/dashboard/settings/StreamingMethodPanel'
import PublicPortalPanel from '../components/dashboard/settings/PublicPortalPanel'
import EmbeddedDataAppletPanel from '../components/dashboard/settings/EmbeddedDataAppletPanel'
import { listReports } from '../lib/reportApi'
import DataLoggerTab from './live/DataLoggerTab'
import CommunicationsTab from './live/CommunicationsTab'
import SensorReadingsTab from './live/SensorReadingsTab'
import IridiumMessagesTab from './live/IridiumMessagesTab'
import LiveIconTabs from './live/LiveIconTabs'
import {
  getUnassignedDevicesStorageKey,
  getUnassignedDevicesFromStorage,
  saveUnassignedDevicesToStorage,
  createUnassignedDevice,
  extractUnassignedDevices
} from '../utils/unassignedDeviceHelpers'
import { SecureInput, SecureTextarea } from '../components/SecureInput'
import { scanInput } from '../lib/inputSecurity'
import {
  primeProject as primeWidgetConfigs,
  cacheSet as setWidgetConfig,
  PUBLIC_SELECTION_PREFIX,
  PUBLIC_SELECTION_WIDGET_ID,
} from '../lib/widgetConfigStore'
import {
  WindRoseEditorModal,
  GaugeSeriesEditorModal,
  BulletGraphEditorModal,
  SolidGaugeEditorModal,
  ChangeProjectPhotoModal,
  ChangeWebcamModal,
  FieldNotesPreAddModal,
  NexSensBanner,
} from '../components/dashboard/DashboardWidgets'
import { GalleryEditorModal, SupportProjectEditorModal } from '../components/dashboard/GalleryAndSupportEditors'
import PhotoLocationMap from '../components/dashboard/PhotoLocationMap'
import { RichContentPreAddModal } from '../components/dashboard/WidgetConfigModal'

// Last-contact clock shown beside each logger in the "All Sites" sidebar.
// `status` ('green'/'yellow'/'red'/'unknown') is computed server-side from the
// device's last contact vs its transmit interval (see project_sites_api). Hover
// shows the last contact time; the tooltip is portalled to <body> so the
// sidebar's overflow-y-auto can't clip it.
const _clkPad = (n) => String(n).padStart(2, '0')
function LastContactClock({ lastContact, status, tz }) {
  const [hover, setHover] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0 })
  const ref = useRef(null)
  const color = status === 'green' ? 'text-green-600'
    : status === 'yellow' ? 'text-yellow-500'
    : status === 'red' ? 'text-red-600'
    : 'text-gray-400'
  const label = (() => {
    if (!lastContact) return 'No contact recorded'
    const d = new Date(lastContact)
    if (isNaN(d.getTime())) return String(lastContact)
    const p = wallClockPartsInTz(d, tz)
    return `${p.year}-${_clkPad(p.month)}-${_clkPad(p.day)} ${_clkPad(p.hour)}:${_clkPad(p.minute)}:${_clkPad(p.second)}`
  })()
  const show = () => {
    const r = ref.current?.getBoundingClientRect()
    if (r) setPos({ top: r.bottom + 6, left: r.left + r.width / 2 })
    setHover(true)
  }
  return (
    <span
      ref={ref}
      className="inline-flex items-center flex-shrink-0 cursor-default"
      onMouseEnter={show}
      onMouseLeave={() => setHover(false)}
      aria-label={`Last contact: ${label}`}
    >
      <Clock className={`w-4 h-4 ${color}`} />
      {hover && createPortal(
        <div
          style={{ position: 'fixed', top: pos.top, left: pos.left, transform: 'translateX(-50%)', zIndex: 10000 }}
          className="pointer-events-none whitespace-nowrap bg-white border border-gray-200 rounded-md shadow-lg px-3 py-2"
        >
          <div className="text-[10px] text-gray-400 leading-tight">Last Contact Time</div>
          <div className="text-xs text-[#4D4D4D] leading-tight mt-0.5">{label}</div>
        </div>,
        document.body,
      )}
    </span>
  )
}

// Per-chart line colors for the Parameter Comparison view (cycled by index)
const PARAM_CHART_COLORS = ['#f59e0b', '#10b981', '#8b5cf6', '#3b82f6', '#ef4444', '#06b6d4']

// ─── Gallery media ──────────────────────────────────────────────────────────
// One /api/photo/photo/ row → the shape the gallery grid and info panel use.
// Shared by the "All Photos" fetch and the per-album fetch: they used to carry
// separate copies of this mapping, which is how fields silently drifted apart.
//
// The field set mirrors the Symfony info panel
// (ProjectBundle/Resources/views/Gallery/v2/media-content.html.twig). The
// human-readable sizes and the "Site - x" location label are pre-formatted by
// PhotoSerializer so both UIs read identically; the DATE is formatted here
// instead — see the `date` field below.
function mapApiPhoto(p, i) {
    const location = p.location_info || {}
    // Formatted client-side from the raw instant, in the account's timezone.
    // The stored timezone may be a Windows-style label ("(UTC-05:00) Eastern
    // Time (US & Canada)"); resolveIanaTz maps it to a real zone so Intl applies
    // daylight saving. The server's own `date_display` reads the literal -05:00
    // out of that label, which renders Eastern an hour early all summer, so it
    // is only a fallback here.
    const accountTz = useAuthStore.getState().user?.timezone
    return {
        id: p.id || i,
        _fromApi: true,
        src: p.photo_url || p.thumbnail || p.image || p.file_url || '',
        largeSrc: p.photo_url || p.image || p.file_url || '',
        category: p.upload_type || p.category || 'Photo',
        title: p.name || p.title || p.original_filename || 'Untitled',
        description: p.notes || p.description || '',
        originalFilename: p.original_filename || '',
        cameraSN: p.device_serial_number || 'N/A',
        tags: Array.isArray(p.tags) ? p.tags.join(', ') : (p.tags || ''),
        album: p.album || '',
        uploadedBy: p.author || p.uploaded_by || '',
        // Symfony's "Date" is when the shot was taken, falling back to the
        // upload time. Rendered as "08/12/2026 13:50 EDT".
        date: formatMediaTimestamp(p.timestamp || p.date_uploaded || p.date, accountTz)
            || p.date_display || p.date_uploaded || p.date || '',
        // Machine-readable twin of `date` — the display string is localised and
        // not Date.parse-able, so sorting/filtering reads this instead.
        timestamp: p.timestamp || p.date_uploaded || '',
        dimensions: (p.width && p.height) ? `${p.width} x ${p.height}` : '',
        size: p.size_display || '',
        totalSize: p.total_size || '',
        location: location.name || '',
        latitude: location.latitude ?? null,
        longitude: location.longitude ?? null,
        // Only sites are selectable in the edit form; a photo pinned to a
        // legacy Location keeps its label but has no id to preselect.
        siteId: location.is_site ? location.id : null,
    }
}

// One /api/photo/video/ row → the same gallery item shape as mapApiPhoto, but
// flagged mediaType:'video' with a videoUrl so the grid renders a <video>
// player (with a play badge) instead of an <img>. Camera clips (EWC albums)
// come through here alongside their sibling photos.
function mapApiVideo(v, i) {
    const accountTz = useAuthStore.getState().user?.timezone
    const url = v.video_url || v.url || ''
    return {
        // Namespaced so a photo and a video that share a numeric id never
        // collide in the merged grid / lightbox lookup pool.
        id: `v_${v.id ?? i}`,
        videoId: v.id,
        _fromApi: true,
        mediaType: 'video',
        // Videos rarely carry a poster; leave src empty so the tile renders the
        // <video> element itself (its first frame) rather than a broken <img>.
        src: '',
        largeSrc: url,
        videoUrl: url,
        category: 'Video',
        title: v.name || v.original_filename || 'Untitled',
        description: v.notes || v.description || '',
        originalFilename: v.original_filename || '',
        cameraSN: v.device_serial_number || 'N/A',
        tags: Array.isArray(v.tags) ? v.tags.join(', ') : (v.tags || ''),
        album: v.album || '',
        uploadedBy: v.author || '',
        date: formatMediaTimestamp(v.date_uploaded || v.date, accountTz)
            || v.date_uploaded || v.date || '',
        timestamp: v.date_uploaded || '',
        dimensions: (v.width && v.height) ? `${v.width} x ${v.height}` : '',
        size: v.size_display || '',
        totalSize: '',
        location: '',
        latitude: null,
        longitude: null,
        siteId: null,
    }
}

// Widget types that get a "configure before placing" flow — same pattern as
// Wind Rose (see pendingWindRoseWidget below): clicking the tile in Add
// Widgets opens that widget's settings modal FIRST; it's only placed on the
// grid after the user saves. Maps widget_type -> the scoped-config prefix
// each widget's own component reads via lsGetScoped/lsSetScoped (null for
// the rich-content types, which store their config directly on the widget
// object instead — see commitRichContentWidget).
const PRE_ADD_CONFIG_PREFIX = {
  gauge_series: 'wq_gauge',
  bullet_graph: 'wq_bullet',
  solid_gauge: 'wq_solid',
  data_box: 'wq_databox',
  support_project: 'wq_support',
  donation: 'wq_support', // alias of support_project in WidgetRenderer's component map
  project_photo: 'wq_photo',
  gallery: 'wq_gallery',
  webcam: 'wq_webcam',
  field_notes: 'wq_fieldnotes_settings',
  custom_html: null,
  project_description: null,
  disclaimer: null,
}
const RICH_CONTENT_PRE_ADD_TYPES = new Set(['custom_html', 'project_description', 'disclaimer'])

export function DashboardPage() {
    const { projectId } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const toast = useToast()
    // Stash the currently-viewed project id so pages outside the dashboard
    // (Message Center, etc.) can default to it without re-routing through
    // the dashboard URL.
    useEffect(() => {
        if (!projectId) return
        try { localStorage.setItem('wq_last_project_id', String(projectId)) } catch {}
    }, [projectId])
    // Prime the shared widget-config cache so every viewer (owner,
    // collaborators, anonymous public portal) sees the same picks
    // saved on the server — see wqlive-frontend/src/lib/widgetConfigStore.js.
    useEffect(() => {
        if (!projectId) return
        primeWidgetConfigs(projectId)
    }, [projectId])
    // userId is read once at mount for the selection-persistence scope key.
    // auth-store hydrates synchronously from localStorage (`auth_user`), so
    // this is available before useState initializers run.
    const persistedUserId = useAuthStore(s => s.user?.id)
    const currentUserEmail = useAuthStore(s => s.user?.email)
    const [projects, setProjects] = useState([])
    const [loadingProjects, setLoadingProjects] = useState(true)
    const [currentProject, setCurrentProject] = useState(null)
    // Mirror the active project's name into localStorage + a window event so the
    // shared blue top bar (Navigation) can show "Project: <name>" on the left
    // (XD design). Cleared when no project is active.
    useEffect(() => {
        try {
            const nm = currentProject?.name || ''
            if (nm) localStorage.setItem('wq_last_project_name', nm)
            else localStorage.removeItem('wq_last_project_name')
            window.dispatchEvent(new CustomEvent('wq_project_changed', { detail: { name: nm } }))
        } catch {}
    }, [currentProject?.name])
    const [templates, setTemplates] = useState([])
    const [loadingTemplates, setLoadingTemplates] = useState(false)
    // Canonical backend-defined templates for the Add Dashboard popup
    // (separate from `templates`, which is the project's full list and
    // includes customer-added clones).
    const [predefinedTemplates, setPredefinedTemplates] = useState([])
    const [loadingPredefinedTemplates, setLoadingPredefinedTemplates] = useState(false)
    
    const [expandedMenus, setExpandedMenus] = useState(['land-based', 'pond-buoy'])
    // localStorage (not sessionStorage) so the active tab survives a full
    // browser restart, not just a same-tab reload. Falls back to sessionStorage
    // for users that landed mid-session with old state, then to 'Dashboard'.
    const [activeTab, setActiveTab] = useState(() => {
        try {
            return localStorage.getItem('wq_activeTab')
                || sessionStorage.getItem('activeTab')
                || 'Dashboard'
        } catch { return 'Dashboard' }
    })
    // Remember the previously-active tab so Cancel on Create-Report can
    // bounce the user back to where they came from when there's nothing
    // saved to fall back to.
    const previousActiveTabRef = useRef('Dashboard')
    useEffect(() => {
        return () => { previousActiveTabRef.current = activeTab }
    }, [activeTab])
    // Hydrate from localStorage so the sidebar checkbox state survives
    // page reload and logout/login (same browser + same user).  Scoped by
    // (userId, projectId) — see SELPARAMS_KEY at the top of file.
    const [checkedItems, setCheckedItems] = useState(
      () => loadSelParams(persistedUserId, projectId)?.checkedItems || {}
    )
    const [searchQuery, setSearchQuery] = useState('')
    const [hoveredTab, setHoveredTab] = useState(null)
    // Grace timer for the header dropdowns: leaving a tab closes its menu after
    // a short delay instead of instantly, so the cursor can cross the small gap
    // between the tab and the menu (and move within the menu) without it
    // collapsing — which made items "sometimes not clickable".
    const headerCloseTimer = useRef(null)
    const openHeaderMenu = (tab) => {
        if (headerCloseTimer.current) { clearTimeout(headerCloseTimer.current); headerCloseTimer.current = null }
        setHoveredTab(tab)
    }
    const closeHeaderMenu = (tab) => {
        if (headerCloseTimer.current) clearTimeout(headerCloseTimer.current)
        headerCloseTimer.current = setTimeout(() => {
            setHoveredTab(null)
            if (tab === 'Live') setLiveMenuFocused(false)
        }, 150)
    }
    const [liveMenuFocused, setLiveMenuFocused] = useState(false)
    // Generic sub-index restore helper. Reads localStorage first (survives
    // browser restart), falls back to sessionStorage (legacy key), then to 0.
    const _restoreIndex = (lsKey, ssKey = null) => {
        try {
            const ls = parseInt(localStorage.getItem(lsKey), 10)
            if (Number.isFinite(ls) && ls >= 0) return ls
            if (ssKey) {
                const ss = parseInt(sessionStorage.getItem(ssKey), 10)
                if (Number.isFinite(ss) && ss >= 0) return ss
            }
        } catch { /* private mode */ }
        return 0
    }

    const [currentMenuIndex, setCurrentMenuIndex] = useState(
        () => _restoreIndex('wq_currentMenuIndex', 'currentMenuIndex'))
    // Bumped by the header's green Refresh button to re-fetch the CURRENT
    // dashboard slide's data in place (its content remounts via `key`) — instead
    // of a full page reload, which was dropping the user onto a different slide.
    const [dashRefreshTick, setDashRefreshTick] = useState(0)
    // Data-only refresh signal: bumped on every refresh (manual + auto). Widgets
    // that show a "latest value" (DataBoxWidget tiles, the line chart) include it
    // in their fetch deps, so they re-fetch WITHOUT remounting the grid. This lets
    // auto-refresh work even in Edit Mode, where a full remount (dashRefreshTick)
    // would discard the user's unsaved layout changes.
    const [dataRefreshNonce, setDataRefreshNonce] = useState(0)
    // Epoch-ms of the next scheduled auto-refresh (null when auto-refresh is off
    // / paused). Drives the "next refresh in M:SS" countdown in the header so the
    // user can see exactly when it will fire.
    const [nextAutoRefreshAt, setNextAutoRefreshAt] = useState(null)
    // Holds the latest dashboard-refresh routine. The idle timer effect below
    // doesn't re-run on every render, so it calls through this ref to always
    // get the current logic (and the current activePeriod) — no stale closure.
    const refreshDashboardRef = useRef(() => {})
    const [currentDataAnalyticsIndex, setCurrentDataAnalyticsIndex] =
        useState(() => _restoreIndex('wq_currentDataAnalyticsIndex'))
    const [currentReportIndex,   setCurrentReportIndex]   = useState(
        () => _restoreIndex('wq_currentReportIndex'))
    // ── Reports state ───────────────────────────────────────────────────────
    // savedReports: this user's UserReport rows for the current project.
    // Drives the dropdown items in the header AND the View-All-Reports grid.
    // reportMode + reportId track which Report screen the tab is showing.
    const [savedReports, setSavedReports] = useState([])
    // reportMode: null (empty Report tab) | 'view' | 'edit' | 'create'.
    // 'list' lives in its own modal flag now (showReportList) so opening
    // "View All Reports" doesn't blow away whatever report the user was
    // already viewing underneath.
    // Persist the currently-VIEWED report across reload (like the dashboard
    // selection) so a refresh keeps you on the same report instead of the empty
    // Report tab. Only 'view' is restored — not 'create'/'edit', to avoid
    // reopening a half-finished editor with lost state.
    const [reportMode,   setReportMode]   = useState(() => {
        try { return localStorage.getItem('wq_reportMode') === 'view' ? 'view' : null } catch { return null }
    })
    const [reportId,     setReportId]     = useState(() => {
        try { const v = parseInt(localStorage.getItem('wq_reportId'), 10); return Number.isFinite(v) ? v : null } catch { return null }
    })
    useEffect(() => {
        try {
            if (reportMode === 'view' && reportId != null) {
                localStorage.setItem('wq_reportMode', 'view')
                localStorage.setItem('wq_reportId', String(reportId))
            } else {
                localStorage.removeItem('wq_reportMode')
                localStorage.removeItem('wq_reportId')
            }
        } catch { /* private mode */ }
    }, [reportMode, reportId])
    const [reportsReloadTick, setReportsReloadTick] = useState(0)
    const [showReportList, setShowReportList] = useState(false)
    // Dropdown items for the Report tab. Built up here (with the other
    // top-level state) so the hook runs BEFORE the `return <NoActiveProject />`
    // early-return paths — putting the useMemo further down skipped it on
    // the empty-projects render and crashed React with error #310.
    // Order: [saved reports …, divider, 'View All Reports', '+ Create Report']
    const reportMenuItems = useMemo(() => {
        const items = savedReports.map((r) => ({ kind: 'report', id: r.id, label: r.name }))
        return [
            ...items,
            { kind: 'divider' },
            { kind: 'action', action: 'list',   label: 'View All Reports' },
            { kind: 'action', action: 'create', label: '+ Create Report' },
        ]
    }, [savedReports])
    const [currentSettingsIndex, setCurrentSettingsIndex] = useState(() => {
        // Migrate: indexes 4 (Public Portal) and 5 (Embedded Data
        // Applet) are now modal-only — an old localStorage value
        // would leave the user on a blank sub-page. Snap back to 0.
        const raw = _restoreIndex('wq_currentSettingsIndex')
        return raw === 4 || raw === 5 ? 0 : raw
    })
    // Modal-only Settings entries — open as overlays without changing
    // the active settings sub-page underneath.
    const [publicPortalOpen,   setPublicPortalOpen]   = useState(false)
    const [embeddedAppletOpen, setEmbeddedAppletOpen] = useState(false)
    // Settings → Users (live data from the project users API)
    const [settingsUsers, setSettingsUsers] = useState([])
    const [settingsUsersLoading, setSettingsUsersLoading] = useState(false)
    const [settingsUsersError, setSettingsUsersError]   = useState('')
    const [settingsUsersBusy, setSettingsUsersBusy]     = useState(false)
    const [newUserEmail, setNewUserEmail] = useState('')
    const [newUserAccess, setNewUserAccess] = useState('Admin')
    // Only the project Owner may add/remove users. Admins still see the list
    // (read-only). Derived from the loaded members: the Owner row's email
    // matches the logged-in account. Backend enforces this too — see
    // project_user_add_api / project_user_remove_api (_require_owner).
    const isProjectOwner = settingsUsers.some(
        u => u.access === 'Owner' && u.email && currentUserEmail &&
             u.email.toLowerCase() === currentUserEmail.toLowerCase()
    )
    const [currentLiveIndex, setCurrentLiveIndex] = useState(
        () => _restoreIndex('wq_currentLiveIndex'))
    // Header slot node (in the shared LiveIconTabs row) that the Sensor Readings
    // tab portals its Start/Stop + Sample # controls into, per the XD layout.
    const [liveStreamSlot, setLiveStreamSlot] = useState(null)


    // Write-through to localStorage so the active tab + sub-index survive
    // reload AND full browser restart (one effect per index to keep updates
    // independent — none of these change on render, so the cost is trivial).
    useEffect(() => {
        try {
            localStorage.setItem('wq_currentMenuIndex',          String(currentMenuIndex))
            localStorage.setItem('wq_currentDataAnalyticsIndex', String(currentDataAnalyticsIndex))
            localStorage.setItem('wq_currentReportIndex',        String(currentReportIndex))
            localStorage.setItem('wq_currentSettingsIndex',      String(currentSettingsIndex))
            localStorage.setItem('wq_currentLiveIndex',          String(currentLiveIndex))
        } catch { /* private mode */ }
    }, [currentMenuIndex, currentDataAnalyticsIndex, currentReportIndex, currentSettingsIndex, currentLiveIndex])

    // Persist the SELECTED DASHBOARD by its stable id (not just the positional
    // index above) so a reload restores the exact dashboard — see the id-based
    // restore in the templates-fetch effect. Only writes once templates are
    // loaded and the index points at a real template.
    useEffect(() => {
        const tpl = templates[currentMenuIndex]
        if (tpl && tpl.id != null) {
            try { localStorage.setItem('wq_currentDashboardId', String(tpl.id)) } catch { /* private mode */ }
        }
    }, [currentMenuIndex, templates])
    const liveMenuItemsRefs = useRef([])
    const gridRef = useRef(null)
    // On the Dashboard tab the page scrolls as a normal document (browser
    // scrollbar) instead of an inner content panel — see the root/sidebar below.
    // The header is sticky, so the sticky sidebar must sit BELOW it; measure the
    // header's live height and expose it as --dash-header-h for the offset.
    const dashHeaderRef = useRef(null)
    const [dashHeaderH, setDashHeaderH] = useState(0)
    useEffect(() => {
        const el = dashHeaderRef.current
        if (!el || typeof ResizeObserver === 'undefined') return
        const update = () => setDashHeaderH(el.offsetHeight || 0)
        update()
        const ro = new ResizeObserver(update)
        ro.observe(el)
        return () => ro.disconnect()
    }, [activeTab])
    const galleryFetchedProjectRef = useRef(null)
    const [gridContainerWidth, setGridContainerWidth] = useState(0)
    const gridRoRef = useRef(null)
    const gridWrapperRefCallback = useCallback(node => {
      if (gridRoRef.current) { gridRoRef.current.disconnect(); gridRoRef.current = null }
      if (!node) return
      const measure = () => {
        // clientWidth (NOT getBoundingClientRect().width) so the grid width
        // EXCLUDES the vertical scrollbar. Using the border-box width made the
        // grid ~15px too wide once the scrollbar appeared, so its right edge
        // (and the widgets' delete buttons) were clipped by overflow-x-hidden.
        const w = node.clientWidth
        if (w > 0) setGridContainerWidth(w)
      }
      measure()
      const ro = new ResizeObserver(measure)
      ro.observe(node)
      gridRoRef.current = ro
    }, [])
    const [showCustomizeModal, setShowCustomizeModal] = useState(false)
    const [customizeTab, setCustomizeTab] = useState('Parameters')
    // Edit Mode survives a page reload, but NOT closing the tab.
    //
    // sessionStorage rather than localStorage is deliberate: entering Edit Mode
    // requires confirming your password (the "Admin Sign In" prompt), so the
    // confirmation should not outlive the browsing session. A reload keeps you
    // where you were; closing the tab drops back to read-only and the next
    // entry re-asks for the password.
    const EDIT_MODE_KEY = 'wq_edit_mode'
    const [isCustomizingDashboard, setIsCustomizingDashboard] = useState(() => {
        try { return sessionStorage.getItem(EDIT_MODE_KEY) === '1' } catch { return false }
    })
    const [customDashboardWidgets, setCustomDashboardWidgets] = useState([])
    const [draggedWidgetIndex, setDraggedWidgetIndex] = useState(null)
    const [isSavingCustomization, setIsSavingCustomization] = useState(false)
    const [showAddWidgetsModal, setShowAddWidgetsModal] = useState(false)
    const [dynamicWidgetCategories, setDynamicWidgetCategories] = useState([])
    const [loadingWidgetCategories, setLoadingWidgetCategories] = useState(false)
    const [widgetCategoriesError, setWidgetCategoriesError] = useState(null)
    const [searchWidgetsQuery, setSearchWidgetsQuery] = useState('')
    const [showResetDashboardModal, setShowResetDashboardModal] = useState(false)
    const [isEditingSidebar, setIsEditingSidebar] = useState(false)
    // Collapsed by default on phones (the "All Sites" panel is a full-width
    // overlay there, so opening it on load buries the data). Desktop stays open.
    const [isSidebarVisible, setIsSidebarVisible] = useState(
        () => (typeof window !== 'undefined' && window.innerWidth < 768) ? false : true)
    // Presentation Mode. The settings modal is opened by the "Presentation"
    // button in the dashboard header (XD design). Play uses the loaded
    // config (pause + transition + rotation) to cycle through flagged
    // dashboards; Stop restores the sidebar.
    const [showPresentationSettings, setShowPresentationSettings] = useState(false)
    const [presentationConfig, setPresentationConfig] = useState({
        pauseSeconds: 30,
        transition: 'fade',
        randomOrder: false,
    })
    // Sequence of menu-indices to visit when playing. Built by matching the
    // included dashboards (from the backend) against `dashboardMenuItems` by
    // name. Empty array disables Play.
    const [presentationRotation, setPresentationRotation] = useState([])
    // "Auto refresh dashboard when idle" — project-level idle timeout in
    // seconds (minimum 300 / 5 min, default 600). Null = not loaded yet / disabled. Set from
    // the backend on project change and editable in the Configure Dashboards
    // modal; see the idle-refresh effect further down.
    const [autoRefreshSeconds, setAutoRefreshSeconds] = useState(600)
    // Tracks the last template order we applied from presentation config so the
    // useEffect that re-runs when templates change doesn't infinite-loop.
    const presOrderSigRef = useRef(null)
    // Remembers the sidebar's pre-Play state so Stop can restore it.
    const sidebarBeforePlayRef = useRef(true)
    const [showAddDeviceModal, setShowAddDeviceModal] = useState(false)
    const [selectedSiteForDevice, setSelectedSiteForDevice] = useState(null)
    const [newDeviceName, setNewDeviceName] = useState('')
    const [claimMatches, setClaimMatches] = useState([]) // multiple devices share a claim code
    const [configureModalTab, setConfigureModalTab] = useState('Devices')
    const [sensorSelectedDevice, setSensorSelectedDevice] = useState('')
    const [sensorProfileName, setSensorProfileName] = useState('Default')
    const [sensorProfiles, setSensorProfiles] = useState([{ id: 1, name: 'Default' }])
    const [selectedSensorProfile, setSelectedSensorProfile] = useState(null)
    const [showSensorProfileForm, setShowSensorProfileForm] = useState(false)
    const [sensorFormManufacturer, setSensorFormManufacturer] = useState('Sea-Bird')
    const [sensorFormModel, setSensorFormModel] = useState('HydroCAT SDI12')
    const [sensorFormAddress, setSensorFormAddress] = useState('0')
    const [sensorFormMeasCmd, setSensorFormMeasCmd] = useState('M')
    const [sensorFormPort, setSensorFormPort] = useState('P1')
    const [sensorFormPower, setSensorFormPower] = useState('Switch')
    const [sensorFormWarmup, setSensorFormWarmup] = useState('30')
    const [sensorFormTempUnit, setSensorFormTempUnit] = useState('C')
    const [sensorFormShowAll, setSensorFormShowAll] = useState(false)
    const [sensorAvailableParams, setSensorAvailableParams] = useState(['Temperature', 'ODOSat', 'Sample No'])
    const [sensorSelectedParams, setSensorSelectedParams] = useState(['Conductivity (S/m)', 'Pressure (dbar)', 'ODO (mg/L)', 'Salinity (PPT)', 'Sound Speed (m/s)', 'Sp Cond (S/m)'])
    const [draggedSensorParam, setDraggedSensorParam] = useState(null)
    const [draggedSensorParamSource, setDraggedSensorParamSource] = useState(null)
    const [unassignedDevices, setUnassignedDevices] = useState([])
    const [isCreatingDevice, setIsCreatingDevice] = useState(false)
    const [deviceError, setDeviceError] = useState('')
    const [draggedDevice, setDraggedDevice] = useState(null)
    const [dragSource, setDragSource] = useState(null) // 'unassigned' or site id
    const [draggingWidget, setDraggingWidget] = useState(null) // widget being dragged from Add Widgets popup
    const [addWidgetModalHidden, setAddWidgetModalHidden] = useState(false) // visually hide modal during drag without unmounting
    const [pendingWindRoseWidget, setPendingWindRoseWidget] = useState(null) // catalog widget awaiting WindRose config before placement
    const [pendingConfigWidget, setPendingConfigWidget] = useState(null) // catalog widget awaiting its settings modal before placement — see PRE_ADD_CONFIG_PREFIX
    const [modalProjectSites, setModalProjectSites] = useState([]) // Working copy for modal
    const [deviceAssignments, setDeviceAssignments] = useState({}) // Track device assignments: {deviceId: siteId or null}
    const [successMessage, setSuccessMessage] = useState('')
    const [isSavingDevices, setIsSavingDevices] = useState(false)
    const [showCreateSiteModal, setShowCreateSiteModal] = useState(false)
    const [showParamConfigModal, setShowParamConfigModal] = useState(false)
    // Bumped whenever Parameter Configuration is saved/closed — data-fetching
    // widgets (e.g. LineChartWidget) list this in their fetch effect's
    // dependency array so a changed unit Convert / Sensor min-max / precision
    // triggers an immediate re-fetch with the new config, instead of the
    // widget silently continuing to show the stale response from before the
    // change (which previously required a full page reload to clear).
    const [paramConfigVersion, setParamConfigVersion] = useState(0)
    const [newSiteName, setNewSiteName] = useState('')
    const [newSiteDescription, setNewSiteDescription] = useState('')
    const [newSiteLatitude, setNewSiteLatitude] = useState('')
    const [newSiteLongitude, setNewSiteLongitude] = useState('')
    const [useDeviceGPS, setUseDeviceGPS] = useState(false)
    const [isCreatingSite, setIsCreatingSite] = useState(false)
    const [siteError, setSiteError] = useState('')
    const [siteSuccess, setSiteSuccess] = useState('')
    // Non-null when the Create Site modal is reused to EDIT an existing site
    // (holds its id); the form is pre-filled and Save PATCHes instead of POSTs.
    const [editingSiteId, setEditingSiteId] = useState(null)
    // Open the site modal in edit mode, pre-filled from the chosen site.
    const openEditSite = (site) => {
        setEditingSiteId(site.id)
        setNewSiteName(site.name || '')
        setNewSiteDescription(site.description || '')
        setNewSiteLatitude(site.latitude != null ? String(site.latitude) : '')
        setNewSiteLongitude(site.longitude != null ? String(site.longitude) : '')
        setUseDeviceGPS(!!site.autogps)
        setSiteError(''); setSiteSuccess('')
        setShowCreateSiteModal(true)
    }
    const closeSiteModal = () => {
        setShowCreateSiteModal(false); setEditingSiteId(null)
        setNewSiteName(''); setNewSiteDescription(''); setNewSiteLatitude('')
        setNewSiteLongitude(''); setUseDeviceGPS(false); setSiteError(''); setSiteSuccess('')
    }
    const [siteDeleteError, setSiteDeleteError] = useState('')
    // Drag-and-drop for sites in edit mode
    const [draggedSiteId, setDraggedSiteId] = useState(null)
    const [dragOverSiteId, setDragOverSiteId] = useState(null)
    const [isDragOverTrash, setIsDragOverTrash] = useState(false)
    const [showDeleteSiteConfirm, setShowDeleteSiteConfirm] = useState(false)
    const [siteToDelete, setSiteToDelete] = useState(null) // { id, name }
    const [showMoveSiteConfirm, setShowMoveSiteConfirm] = useState(false)
    const [pendingSiteMove, setPendingSiteMove] = useState(null) // { fromId, toId }
    const [isDeletingSite, setIsDeletingSite] = useState(false)
    // Drag-and-drop delete for a single LOGGER (device) in edit mode — lets the
    // user drop just one logger on the trash instead of the whole site.
    const [draggedDeviceId, setDraggedDeviceId] = useState(null)
    const [showDeleteDeviceConfirm, setShowDeleteDeviceConfirm] = useState(false)
    const [deviceToDelete, setDeviceToDelete] = useState(null) // { id, name }
    const [deviceDeleteError, setDeviceDeleteError] = useState('')
    const [isDeletingDevice, setIsDeletingDevice] = useState(false)
    const [showConfigureDashboardsModal, setShowConfigureDashboardsModal] = useState(false)
    // Edit Mode asks the user to confirm their password first (XD "Admin
    // Sign In"). Permission is still decided by canEditDashboard; this is a
    // confirmation step on top of it, not a replacement for it.
    const [adminSignInOpen, setAdminSignInOpen] = useState(false)
    const [showDashboardArchive, setShowDashboardArchive] = useState(false)
    const [dashboardSearchQuery, setDashboardSearchQuery] = useState('')
    const [showAddDashboardModal, setShowAddDashboardModal] = useState(false)
    const [addDashboardSearch, setAddDashboardSearch] = useState('')
    const [showDashboardDetailsModal, setShowDashboardDetailsModal] = useState(false)
    const [dashboardName, setDashboardName] = useState('')
    const [dashboardDetails, setDashboardDetails] = useState('')
    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [selectedTemplateForDetails, setSelectedTemplateForDetails] = useState(null)
    const [isCreatingDashboard, setIsCreatingDashboard] = useState(false)
    // Synchronous re-entrancy guard for dashboard creation. The button's
    // disabled={isCreatingDashboard} is React state, which only applies on the
    // next render — so two clicks (or events) in the same tick both pass it and
    // fire two POSTs, creating TWO dashboards. This ref is set imperatively and
    // blocks the second invocation immediately, before any await.
    const creatingDashboardRef = useRef(false)
    const [dashboardError, setDashboardError] = useState('')
    const [dashboardSuccess, setDashboardSuccess] = useState('')
    const [currentViewingDashboard, setCurrentViewingDashboard] = useState(null)
    const [isPlaying, setIsPlaying] = useState(false)
    const playIntervalRef = useRef(null)
    const [dashboards, setDashboards] = useState([
      { id: 1, name: 'Overview', description: 'Created from Project Map Template' },
      { id: 2, name: 'Parameter Comparison', description: 'Created from Parameter Comparison Template' },
      { id: 3, name: 'Project Map', description: 'Created from Project Map Template' },
      { id: 4, name: 'Current Conditions', description: 'Created from Current Conditions Template' },
      { id: 5, name: 'System Performance', description: 'Created from System Performance Template' }
    ])
    const [archivedDashboards, setArchivedDashboards] = useState([
      { id: 6, name: 'Custom Overview', description: 'Created from Overview Template' },
      { id: 7, name: 'DO Comparison', description: 'Created from Parameter Comparison Template' }
    ])
    // Initialize gallery state — URL params take priority over sessionStorage
    const getInitialGalleryState = () => {
        const urlParams = new URLSearchParams(window.location.search)
        const urlTab = urlParams.get('gallery_tab')
        if (typeof window !== 'undefined') {
            const saved = sessionStorage.getItem('galleryState')
            if (saved) {
                try {
                    const parsed = JSON.parse(saved)
                    if (urlTab) parsed.tab = urlTab
                    return parsed
                } catch (e) { /* ignore */ }
            }
        }
        return { tab: urlTab || 'All Photos', page: 1, search: '', selectedId: null }
    }

    const initialState = getInitialGalleryState()
    const [galleryTab, setGalleryTab] = useState(initialState.tab || 'All Photos')
    const [galleryPage, setGalleryPage] = useState(initialState.page || 1)
    const [gallerySearch, setGallerySearch] = useState(initialState.search || '')
    // Gallery toolbar Filter menu — production-style per-field filters
    // (Title / Description / Tags / Date / Location / Advanced). Photos always
    // display newest-first (backend order). `openGalleryMenu` toggles the whole
    // menu; `openGalleryFilterField` is the single expanded field row inside it.
    const [galleryFilters, setGalleryFilters] = useState({ title: '', description: '', tags: '', location: '', dateFrom: '', dateTo: '' })
    const [openGalleryFilterField, setOpenGalleryFilterField] = useState(null)
    const [openGalleryMenu, setOpenGalleryMenu] = useState(null) // 'filter' | null
    const [selectedPhoto, setSelectedPhoto] = useState(null)
    // Gallery info panel: "Show Map" is collapsed until asked for, and folds
    // back up when a different photo is selected (Symfony reveals its map the
    // same way, per media).
    const [showPhotoMap, setShowPhotoMap] = useState(false)
    const [galleryState, setGalleryState] = useState(initialState)
    const [showCreateAlbumModal, setShowCreateAlbumModal] = useState(false)
    const [projectAlbums, setProjectAlbums] = useState([])
    const [showUploadModal, setShowUploadModal] = useState(false)
    const [editAlbumModal, setEditAlbumModal] = useState(null) // { id, name }
    const [editAlbumName, setEditAlbumName] = useState('')
    const [uploadItems, setUploadItems] = useState([]) // {id, file, previewUrl, album_id, title, tags, location, description, uploading, done, error}
    const [albumPhotos, setAlbumPhotos] = useState({}) // keyed by album_id
    const [albumName, setAlbumName] = useState('')
    const [albumImages, setAlbumImages] = useState([])
    const [isCreatingAlbum, setIsCreatingAlbum] = useState(false)
    const [albumError, setAlbumError] = useState('')
    const [albumSuccess, setAlbumSuccess] = useState('')
    const [failedImageIds, setFailedImageIds] = useState(new Set())
    const [showEditMediaModal, setShowEditMediaModal] = useState(false)
    const [editingPhoto, setEditingPhoto] = useState(null)
    const [editFormData, setEditFormData] = useState({
        title: '',
        description: '',
        tags: '',
        album: '',
        location: ''
    })
    const [editPhotoFile, setEditPhotoFile] = useState(null)
    const [editPhotoPreview, setEditPhotoPreview] = useState(null)
    const [photoEditsMap, setPhotoEditsMap] = useState({})
    const [apiPhotos, setApiPhotos] = useState([])
    const [galleryLoading, setGalleryLoading] = useState(false)
    // Project settings is an inline screen (Settings → Project), not a popup.
    const [projectEditMode, setProjectEditMode] = useState(false)
    const [projectForm, setProjectForm] = useState({ name: '', description: '', latitude: '', longitude: '' })
    const [savingProject, setSavingProject] = useState(false)
    const [deletingProject, setDeletingProject] = useState(false)
    const [projectSites, setProjectSites] = useState([])
    const [loadingSites, setLoadingSites] = useState(false)
    // First device across the project's sites — drives the Live/Data Logger tab.
    // Every device in the project (across all sites), for the Live page's
    // device switcher. label = device name/uid, prefixed with the site name
    // when the project has more than one site so duplicates stay distinct.
    const liveDevices = useMemo(() => {
        const sites = projectSites || []
        const multiSite = sites.length > 1
        const out = []
        for (const site of sites) {
            for (const dev of (site.devices || [])) {
                if (dev?.id == null) continue
                const name = dev.name || dev.uid || `Device ${dev.id}`
                out.push({
                    id: dev.id,
                    label: multiSite && site.name ? `${site.name} — ${name}` : name,
                    // Iridium-capability flag (from /sites/). Drives the Live
                    // "Iridium Messages" tab, shown only for Iridium loggers.
                    iridium: !!dev.iridium,
                })
            }
        }
        return out
    }, [projectSites])
    // Which device the Live tabs show. Defaults to the first device; the Data
    // Logger dropdown switches it. Kept in state (not derived) so the switch
    // sticks, and re-synced when the project's device list changes.
    const [liveDeviceId, setLiveDeviceId] = useState(null)
    useEffect(() => {
        setLiveDeviceId((prev) =>
            (prev != null && liveDevices.some((d) => d.id === prev)) ? prev : (liveDevices[0]?.id ?? null)
        )
    }, [liveDevices])
    // Master switch for the customer-facing Live "Iridium Messages" tab. Hidden
    // for now (per request) — flip to true to re-enable it; the admin device page
    // keeps its own Iridium Messages tab regardless. The component, API and
    // iridium detection all stay in place so this is a one-line re-enable.
    const SHOW_IRIDIUM_TAB = false
    // Whether the selected Live device is an Iridium logger — gates the extra
    // "Iridium Messages" tab (index 3) in the Live section.
    const showIridium = useMemo(
        () => SHOW_IRIDIUM_TAB && !!liveDevices.find((d) => d.id === liveDeviceId)?.iridium,
        [liveDevices, liveDeviceId],
    )
    // If the Iridium tab is active and we switch to a non-Iridium device (so the
    // tab disappears), fall back to Data Logger instead of showing a blank panel.
    // Guard on a selected device so this doesn't fire during the initial load
    // window (before liveDeviceId is set) and drop a reload off the Iridium tab.
    useEffect(() => {
        if (liveDeviceId != null && !showIridium && currentLiveIndex === 3) setCurrentLiveIndex(0)
    }, [showIridium, currentLiveIndex, liveDeviceId])
    const [projectDescription, setProjectDescription] = useState('')
    const [projectImage, setProjectImage] = useState(null)
    // Project Logo upload (Settings > Project). Drives the drag/drop control
    // and the header logo, which reads currentProject.logo.
    const [logoUploading, setLogoUploading] = useState(false)
    const [logoUploadError, setLogoUploadError] = useState('')
    const logoFileInputRef = useRef(null)
    const handleProjectLogoFile = useCallback(async (file) => {
        if (!file || !currentProject?.id) return
        setLogoUploadError('')
        const okType = /^image\//.test(file.type) || /\.(png|jpe?g|gif|webp|svg)$/i.test(file.name || '')
        if (!okType) { setLogoUploadError('Please choose an image (PNG, JPG, GIF, WEBP, or SVG).'); return }
        if (file.size > 5 * 1024 * 1024) { setLogoUploadError('Image is too large (max 5 MB).'); return }
        setLogoUploading(true)
        try {
            const res = await projectsApi.uploadProjectLogo(currentProject.id, file)
            const newUrl = res?.logo
            if (newUrl) {
                // Cache-bust so the header/preview refresh immediately even when
                // the URL path is reused.
                const busted = newUrl + (newUrl.includes('?') ? '&' : '?') + 't=' + Date.now()
                setCurrentProject(prev => prev ? { ...prev, logo: busted } : prev)
                setProjects(prev => prev.map(p => p.id === currentProject.id ? { ...p, logo: busted } : p))
            }
        } catch (err) {
            setLogoUploadError(err?.response?.data?.error || 'Failed to upload logo. Please try again.')
        } finally {
            setLogoUploading(false)
        }
    }, [currentProject?.id])
    // Paired with checkedItems — see hydration note there.
    const [selectedParameters, setSelectedParameters] = useState(
      () => loadSelParams(persistedUserId, projectId)?.selectedParameters || []
    )
    const [dateRangeGraph, setDateRangeGraph] = useState(() => {
        // Use the customer's configured timezone (read synchronously from the
        // Zustand store, which is already populated from localStorage at this
        // point) so the initial D window is "now − 24h → now" in their TZ,
        // not in the browser's local timezone.
        const tz = resolveIanaTz(useAuthStore.getState().user?.timezone)
        const nowMs = Date.now()
        const pad = n => String(n).padStart(2, '0')
        const fmtMs = ms => {
            const p = wallClockPartsInTz(new Date(ms), tz)
            return `${p.year}-${pad(p.month)}-${pad(p.day)} ${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}`
        }
        return {
            from: fmtMs(nowMs - 24 * 60 * 60 * 1000),
            to:   fmtMs(nowMs),
        }
    })
    const [activePeriod, setActivePeriod] = useState('D') // Day (last 24 hours) by default

    // Handle period button clicks (D, W, M, Y).
    // All date arithmetic is done in customerTz (defined at line ~872 in this
    // component) so the rolling window always reflects the customer's wall-clock
    // time, regardless of the browser's local timezone.
    const handlePeriodClick = (period) => {
        setActivePeriod(period)
        const nowMs = Date.now()
        const days = { D: 1, W: 7, M: 30, Y: 365 }[period] ?? 1
        const pad = n => String(n).padStart(2, '0')
        const fmtMs = ms => {
            const p = wallClockPartsInTz(new Date(ms), customerTz)
            return `${p.year}-${pad(p.month)}-${pad(p.day)} ${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}`
        }
        setDateRangeGraph({
            from: fmtMs(nowMs - days * 24 * 60 * 60 * 1000),
            to:   fmtMs(nowMs),
        })
    }

    // ── Dashboard refresh (green button + auto idle timer) ────────────────────
    // Re-fetching the SAME window shows no new data: the chart window's end is
    // pinned to the "now" captured when the dashboard loaded, so readings that
    // arrived since never fall inside it. So refresh does TWO things:
    //   1. Re-anchor the rolling window to the current "now" for the active
    //      relative period (D/W/M/Y) — this is what surfaces upcoming data.
    //      A custom range (activePeriod cleared to '') is left as the user set it.
    //   2. Bump dataRefreshNonce so widgets re-fetch their latest value in place
    //      (no remount) — works in Edit Mode too.
    //   3. When NOT editing, also remount the slide (dashRefreshTick) as a
    //      catch-all so EVERY widget type refreshes. Skipped in Edit Mode because
    //      a remount would discard the user's unsaved layout changes.
    // Assigned on every render so the idle timer always runs the current logic.
    refreshDashboardRef.current = () => {
        if (activePeriod && ['D', 'W', 'M', 'Y'].includes(activePeriod)) {
            handlePeriodClick(activePeriod)
        }
        setDataRefreshNonce((n) => n + 1)
        if (!isCustomizingDashboard) {
            setDashRefreshTick((t) => t + 1)
        }
    }

    // Reset per-project state when the URL `projectId` changes. Without this
    // the React Router navigation (Dashboard ▸ project A ➜ project B) keeps
    // the previous project's `selectedParameters` / `checkedItems` in state,
    // so widgets keep plotting parameters that don't belong to project B.
    //
    // `selectedParameters` / `checkedItems` are scoped by (userId, projectId)
    // in localStorage (SELPARAMS_KEY at the top of the file), so we re-hydrate
    // from the new project's saved slot — or fall back to empty when the user
    // has never opened that project. Date range + active period reset to the
    // default rolling-24h-today window so the new project's data shows up in
    // the visible window even if the prior project was looking at last week.
    useEffect(() => {
        const saved = loadSelParams(persistedUserId, projectId)
        setSelectedParameters(saved?.selectedParameters || [])
        setCheckedItems(saved?.checkedItems || {})

        const tz = resolveIanaTz(useAuthStore.getState().user?.timezone)
        const nowMs = Date.now()
        const pad = n => String(n).padStart(2, '0')
        const fmtMs = ms => {
            const p = wallClockPartsInTz(new Date(ms), tz)
            return `${p.year}-${pad(p.month)}-${pad(p.day)} ${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}`
        }
        setDateRangeGraph({
            from: fmtMs(nowMs - 24 * 60 * 60 * 1000),
            to:   fmtMs(nowMs),
        })
        setActivePeriod('D')
        // Reports are also project-scoped — reset the active report when
        // switching projects so we don't open a stale id that belongs
        // somewhere else. The list reload effect below pulls the new
        // project's reports.
        setReportMode(null)
        setReportId(null)
        setShowReportList(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId])

    // Pull the user's saved reports for this project. Drives both the
    // Report-tab dropdown items in the header and the View-All-Reports grid.
    // Bump `reportsReloadTick` to refetch (called by ReportView's
    // onReportListChange after create / save / delete).
    useEffect(() => {
        if (!projectId) { setSavedReports([]); return }
        let cancelled = false
        listReports(projectId)
            .then((r) => { if (!cancelled) setSavedReports(r?.reports || []) })
            .catch(() => { if (!cancelled) setSavedReports([]) })
        return () => { cancelled = true }
    }, [projectId, reportsReloadTick])

    // Fetch projects on component mount
    useEffect(() => {
        const fetchProjectsData = async () => {
            setLoadingProjects(true)
            try {
                const response = await projectsApi.fetchProjects(1, 100)
                if (response && response.success && Array.isArray(response.data)) {
                    setProjects(response.data)
                    
                    // If projectId is provided in URL, find and set that project
                    if (projectId) {
                        const project = response.data.find(p => p.id.toString() === projectId.toString())
                        if (project) {
                            setCurrentProject(project)
                        } else {
                            // Super admins can open ANY project's frontend, even ones
                            // they aren't a member of — so it won't appear in the list
                            // above. Fetch it directly and treat them as an admin-level
                            // viewer (full view + edit; owner-only actions such as delete
                            // remain with the owner). Non-super-admins get the usual
                            // "No Active Project" screen (currentProject stays null).
                            const authUser = useAuthStore.getState().user
                            const isSuperAdmin = !!(authUser?.is_superuser || authUser?.groups?.some(g => {
                                const s = (g || '').toLowerCase?.() || ''
                                return s.includes('super') || s.includes('role_super_admin')
                            }))
                            if (isSuperAdmin) {
                                try {
                                    const res = await projectsApi.fetchProject(projectId)
                                    setCurrentProject(res?.success && res.data ? { ...res.data, user_role: 'Admin' } : null)
                                } catch {
                                    setCurrentProject(null)
                                }
                            } else {
                                setCurrentProject(null)
                            }
                        }
                    } else if (response.data.length > 0) {
                        // No projectId in URL, but projects exist - set the first one as default
                        setCurrentProject(response.data[0])
                        navigate(`/dashboard/project/${response.data[0].id}`, { replace: true })
                    }
                } else {
                    console.warn('Invalid response structure:', response)
                    setProjects([])
                }
            } catch (error) {
                console.error('Error fetching projects:', error)
                setProjects([])
            } finally {
                setLoadingProjects(false)
            }
        }
        
        fetchProjectsData()
    }, [projectId, navigate])

    // Fetch project-specific data when project changes
    useEffect(() => {
        if (currentProject) {
            setProjectDescription(currentProject.description || '')
            setProjectImage(currentProject.image || null)
            
            // Fetch sites for this project dynamically
            const fetchSites = async () => {
                setLoadingSites(true)
                console.log(`[Dashboard] Fetching sites for project: ${currentProject.name} (ID: ${currentProject.id})`)
                try {
                    const sites = await projectsApi.fetchProjectSites(currentProject.id)
                    console.log(`[Dashboard] Fetched ${sites.length} sites for project ${currentProject.id}:`, sites)
                    setProjectSites(sites || [])
                    
                    // Expand all sites by default to match XD design
                    if (sites && sites.length > 0) {
                        // Expand all sites, devices, and sensors by default
                        const expandedIds = []
                        
                        for (const site of sites) {
                            expandedIds.push(site.id)
                            if (site.devices) {
                                for (const device of site.devices) {
                                    expandedIds.push(`device-${device.id}`)
                                    if (device.sensors) {
                                        for (const sensor of device.sensors) {
                                            expandedIds.push(`sensor-${sensor.id}`)
                                        }
                                    }
                                }
                            }
                        }
                        
                        setExpandedMenus(expandedIds)
                        
                        // Auto-select first parameter from first site for convenience
                        let firstParam = null
                        for (const site of sites) {
                            console.log(`[Dashboard] Checking site: ${site.name} (ID: ${site.id}), devices:`, site.devices?.length || 0)
                            if (site.devices) {
                                for (const device of site.devices) {
                                    console.log(`[Dashboard] Checking device: ${device.name} (ID: ${device.id}), sensors:`, device.sensors?.length || 0)
                                    if (device.sensors) {
                                        for (const sensor of device.sensors) {
                                            console.log(`[Dashboard] Checking sensor: ${sensor.name} (ID: ${sensor.id}), parameters:`, sensor.parameters?.length || 0)
                                            if (sensor.parameters && sensor.parameters.length > 0) {
                                                const fp = sensor.parameters[0]
                                                firstParam = {
                                                    id: fp.id,
                                                    parameter_id: fp.parameter_id,
                                                    param_nid: fp.param_nid ?? null,
                                                    unit_id: fp.unit_id ?? null,
                                                    name: fp.name,
                                                    unit: fp.unit || '',
                                                    siteId: site.id,
                                                    siteName: site.name || '',
                                                    deviceName: device.name || '',
                                                    sensorName: sensor.name || '',
                                                }
                                                console.log(`[Dashboard] Found first parameter: ${firstParam.name} (ID: ${firstParam.id})`)
                                                break
                                            }
                                        }
                                        if (firstParam) break
                                    }
                                }
                                if (firstParam) break
                            }
                        }
                        
                        // Build the set of valid parameter IDs for this
                        // project's sites — used both to filter stale
                        // selectedParameters and to decide whether the
                        // auto-select should fire.
                        const validIds = new Set()
                        // id -> fresh param meta, so persisted selections (saved
                        // before param_nid existed) can be backfilled with the NID
                        // and group correctly without the user re-selecting.
                        const paramMetaById = new Map()
                        for (const site of sites) {
                            for (const device of (site.devices || [])) {
                                for (const sensor of (device.sensors || [])) {
                                    for (const param of (sensor.parameters || [])) {
                                        if (param?.id != null) {
                                            validIds.add(param.id)
                                            paramMetaById.set(param.id, param)
                                        }
                                    }
                                }
                            }
                        }
                        const backfillNid = (p) => {
                            if (p && (p.param_nid == null)) {
                                const fresh = paramMetaById.get(p.id)
                                if (fresh) return { ...p, param_nid: fresh.param_nid ?? null, unit_id: fresh.unit_id ?? p.unit_id ?? null }
                            }
                            return p
                        }

                        // Drop any selectedParameters whose id isn't in this
                        // project — orphans from a previous project would
                        // otherwise keep showing up on the chart even though
                        // the user can't see/uncheck them in the sidebar
                        // (their checkbox doesn't exist in this project).
                        // Resync checkedItems from the filtered list so the
                        // two never disagree.
                        setSelectedParameters(prev => {
                            const filtered = prev.filter(p => validIds.has(p.id)).map(backfillNid)
                            // Auto-select first param only when there's
                            // genuinely nothing left after the filter, AND
                            // the user has no prior selection saved for
                            // this project (filtered.length === 0 implies
                            // either no save or all-orphan save).
                            if (filtered.length === 0 && firstParam) return [firstParam]
                            return filtered
                        })
                        setCheckedItems(prev => {
                            const next = {}
                            for (const id of Object.keys(prev)) {
                                // keep only checkboxes whose param exists in
                                // the current project AND was true.
                                if (validIds.has(Number(id)) && prev[id]) next[id] = true
                            }
                            // If filter wiped everything and we just auto-
                            // selected the first param above, tick its box.
                            if (Object.keys(next).length === 0 && firstParam) next[firstParam.id] = true
                            return next
                        })
                    }
                } catch (error) {
                    console.error('Error fetching project sites:', error)
                    setProjectSites([])
                } finally {
                    setLoadingSites(false)
                }
            }
            
            fetchSites()
        }
    }, [currentProject])

    // ── Auto-refresh: re-fetch sites data on each idle-refresh tick ───────────
    // The idle timer bumps dashRefreshTick, which remounts the active dashboard
    // so its chart/data widgets re-fetch. But the Project Map and the sidebar
    // read from `projectSites`, which the main effect above only loads on project
    // change — so without this the map pin position + popup lat/lon would stay
    // stale through an auto-refresh. Re-fetch the sites payload here too, WITHOUT
    // touching sidebar expansion (expandedMenus) or the user's parameter
    // selection, so the refresh stays non-disruptive. Skips the initial mount
    // (tick 0), where fetchSites() has already run.
    useEffect(() => {
        if (dashRefreshTick === 0 || !currentProject?.id) return
        let cancelled = false
        projectsApi.fetchProjectSites(currentProject.id)
            .then((sites) => { if (!cancelled && Array.isArray(sites)) setProjectSites(sites) })
            .catch(() => { /* keep last-good data on refresh failure */ })
        return () => { cancelled = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashRefreshTick])

    // ── Add Dashboard popup: load the admin-assignable templates for the
    // current user/project. The backend honours TemplateAssignment rows so
    // each customer only sees what their admin allowed.
    //
    // Falls back to the client-side lowest-id-per-known-type filter on
    // `templates` if the dedicated endpoint isn't reachable (older backend,
    // or before today's restart). That keeps the popup populated rather
    // than going empty when the backend is mid-deploy.
    useEffect(() => {
        if (!currentProject?.id) {
            setPredefinedTemplates([])
            return
        }
        let cancelled = false
        setLoadingPredefinedTemplates(true)
        dashboardApi.getAdminAssignableTemplates(currentProject.id)
            .then(res => {
                if (cancelled) return
                const list = Array.isArray(res.data?.templates) ? res.data.templates : []
                setPredefinedTemplates(list)
            })
            .catch(err => {
                if (cancelled) return
                console.warn('[Add Dashboard] assignable endpoint unavailable, falling back to client-side filter', err?.response?.status)
                const PREDEFINED_TYPES = [
                    'overview', 'parameter_comparison', 'project_map',
                    'current_conditions', 'system_performance',
                ]
                const computed = PREDEFINED_TYPES
                    .map(tt => {
                        const ofType = (templates || []).filter(t => t.template_type === tt)
                        if (ofType.length === 0) return null
                        return ofType.reduce((a, b) => (a.id < b.id ? a : b))
                    })
                    .filter(Boolean)
                setPredefinedTemplates(computed)
            })
            .finally(() => { if (!cancelled) setLoadingPredefinedTemplates(false) })
        return () => { cancelled = true }
    }, [currentProject?.id, templates])

    // ── Settings → Users: load when the Settings tab opens on Users sub-page
    useEffect(() => {
        if (activeTab !== 'Settings' || currentSettingsIndex !== 0) return
        if (!currentProject?.id) {
            setSettingsUsers([])
            return
        }
        let cancelled = false
        setSettingsUsersLoading(true)
        setSettingsUsersError('')
        projectsApi.listProjectUsers(currentProject.id)
            .then(res => {
                if (cancelled) return
                const data = Array.isArray(res?.data) ? res.data : []
                setSettingsUsers(data)
            })
            .catch(err => {
                if (cancelled) return
                const detail = err.response?.data?.error || err.message || 'Failed to load users.'
                setSettingsUsersError(detail)
                setSettingsUsers([])
            })
            .finally(() => { if (!cancelled) setSettingsUsersLoading(false) })
        return () => { cancelled = true }
    }, [activeTab, currentSettingsIndex, currentProject?.id])

    // Fetch templates for the *current project* — and re-fetch when the
    // user switches projects so each project sees only its own
    // assignments + custom-created dashboards.
    //
    // Previously this called getTemplates() (the unfiltered list endpoint)
    // with []-deps, so EVERY project of EVERY user appeared in the
    // Dashboard menu of every project. The project-scoped endpoint
    // /api/dashboard/templates/available_for_project/?project=<id>
    // returns:
    //   • admin-assigned templates (global TemplateAssignment that matches
    //     this user, or project-scoped for this project)
    //   • the customer's own custom-created dashboards for this project
    //     (each has its own project_id=this_project TemplateAssignment)
    // Customer dashboards from *other* projects don't match either branch
    // of the OR, so they stay where they were created.
    useEffect(() => {
        if (!currentProject?.id) {
            setTemplates([])
            return
        }
        let cancelled = false
        const fetchTemplates = async () => {
            try {
                setLoadingTemplates(true)
                const response = await dashboardApi.getProjectTemplates(currentProject.id)
                if (cancelled) return
                const templatesData = response.data?.templates || response.data?.results || response.data || []
                const activeTemplates = templatesData.filter(t => t.is_active !== false)
                setTemplates(activeTemplates)
                // Restore the dashboard the user was last on by its STABLE id
                // (not the positional index) so a page reload — or any change in
                // template order — keeps them on the same dashboard (e.g. System
                // Performance) instead of snapping back to the first one.
                setCurrentMenuIndex(prev => {
                    if (!activeTemplates.length) return 0
                    let savedId = null
                    try { savedId = localStorage.getItem('wq_currentDashboardId') } catch { /* private mode */ }
                    if (savedId != null && savedId !== '') {
                        const idx = activeTemplates.findIndex(t => String(t.id) === String(savedId))
                        if (idx >= 0) return idx
                    }
                    return Math.min(prev, activeTemplates.length - 1)
                })
            } catch (error) {
                if (cancelled) return
                console.error('Error fetching project templates:', error)
                setTemplates([])
            } finally {
                if (!cancelled) setLoadingTemplates(false)
            }
        }
        fetchTemplates()
        return () => { cancelled = true }
    }, [currentProject?.id])

    // Persist gallery state to sessionStorage whenever it changes
    useEffect(() => {
        if (typeof window !== 'undefined') {
            sessionStorage.setItem('galleryState', JSON.stringify({
                tab: galleryTab,
                page: galleryPage,
                search: gallerySearch,
                selectedId: selectedPhoto?.id || null
            }))
        }
    }, [galleryTab, galleryPage, gallerySearch, selectedPhoto])

    // Persist active tab so refresh (and full browser restart) returns to
    // same section. localStorage = long-lived; sessionStorage kept for
    // backwards compat with mid-session legacy state.
    useEffect(() => {
        try {
            localStorage.setItem('wq_activeTab',   activeTab)
            sessionStorage.setItem('activeTab',    activeTab)
        } catch { /* private mode */ }
    }, [activeTab])

    // Mirror Edit Mode into sessionStorage so a reload keeps it (see the note
    // on the state declaration for why session- and not localStorage).
    useEffect(() => {
        try {
            if (isCustomizingDashboard) sessionStorage.setItem(EDIT_MODE_KEY, '1')
            else                        sessionStorage.removeItem(EDIT_MODE_KEY)
        } catch { /* private mode */ }
        // Leaving Edit Mode must drop the "All Sites" panel's editing view back to
        // the normal (checkbox) view — the pencil toggle is only reachable in Edit
        // Mode, so otherwise it stayed stuck in the editing layout.
        if (!isCustomizingDashboard) setIsEditingSidebar(false)
        // Let the app header react (it gates "Config Dashboards" on Edit Mode).
        try { window.dispatchEvent(new Event('wq_edit_mode_change')) } catch { /* SSR/no window */ }
    }, [isCustomizingDashboard])

    // Fold the info panel's map away whenever a different photo is opened.
    useEffect(() => {
        setShowPhotoMap(false)
    }, [selectedPhoto?.id])

    // ── Gallery photo ⇄ URL (`?photo=<id>`) ─────────────────────────────────
    // Opening a photo PUSHES a history entry so the browser Back button closes
    // the photo and returns to the grid. Without it, opening a photo changed no
    // URL at all, so Back popped the last real navigation — the project switch
    // in UserDropdown — and dumped the user into a different project. Back is
    // also the only way out of the detail view (there is no close control), so
    // this is the primary exit path, not an edge case.
    //
    // Switching photos from the detail view's thumbnail strip REPLACES instead,
    // so glancing through ten photos doesn't leave ten entries to back out of.
    const openPhoto = useCallback((photo, { replace = false } = {}) => {
        if (!photo) return
        setSelectedPhoto(photo)
        const params = new URLSearchParams(location.search)
        params.set('photo', String(photo.id))
        navigate(`${location.pathname}?${params.toString()}`, { replace })
    }, [location.search, location.pathname, navigate])

    // Drop the `photo` param without touching the rest of the query string.
    const clearPhotoParam = useCallback(() => {
        const params = new URLSearchParams(location.search)
        if (!params.has('photo')) return
        params.delete('photo')
        const search = params.toString()
        navigate(`${location.pathname}${search ? '?' + search : ''}`, { replace: true })
    }, [location.search, location.pathname, navigate])

    const photoParam = new URLSearchParams(location.search).get('photo')

    // URL → state. Back/Forward only change the URL, so this effect is what
    // actually closes the detail view when `photo` disappears, and what restores
    // it from a refresh or a shared link.
    //
    // It MUST live here, with the other hooks above the `return
    // <NoActiveProject />` early returns — a hook placed after them runs on some
    // render paths but not others, which is React error #310.
    //
    // Photos are looked up in the fetched pools rather than the derived
    // `sortedGalleryPhotos` list, which is built below those early returns. The
    // lookup only matters for restoring a photo the user didn't just click — a
    // normal click already carries the object through openPhoto.
    useEffect(() => {
        if (activeTab !== 'Gallery') return
        if (!photoParam) {
            setSelectedPhoto(prev => (prev ? null : prev))
            return
        }
        setSelectedPhoto(prev => {
            if (String(prev?.id) === String(photoParam)) return prev
            const pool = [...apiPhotos, ...Object.values(albumPhotos).flat()]
            const match = pool.find(p => String(p.id) === String(photoParam))
            if (!match) return prev
            return photoEditsMap[match.id] ? { ...match, ...photoEditsMap[match.id] } : match
        })
    }, [photoParam, activeTab, apiPhotos, albumPhotos, photoEditsMap])

    // Clean the gallery's URL params when navigating away from Gallery
    useEffect(() => {
        if (activeTab !== 'Gallery') {
            const params = new URLSearchParams(location.search)
            if (params.has('gallery_tab') || params.has('photo')) {
                params.delete('gallery_tab')
                params.delete('photo')
                navigate(`${location.pathname}${params.toString() ? '?' + params.toString() : ''}`, { replace: true })
            }
        }
    }, [activeTab])

    // Fetch gallery photos when gallery tab is opened or project changes
    useEffect(() => {
        if (activeTab !== 'Gallery' || !currentProject?.id) return
        // Skip if we already fetched for this project and have photos
        if (galleryFetchedProjectRef.current === currentProject.id && apiPhotos.length > 0) return
        if (galleryLoading) return

        const projectId = currentProject.id
        const fetchPhotos = async () => {
            setGalleryLoading(true)
            try {
                const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
                const token = (localStorage.getItem(JWT_STORAGE_KEY) || sessionStorage.getItem(JWT_STORAGE_KEY))
                const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'
                // Photos and videos in parallel — the gallery shows both, newest
                // first. Videos (e.g. EWC camera clips) would otherwise never
                // appear since the grid only ever fetched /photo/.
                const [res, vres] = await Promise.all([
                    fetch(`${API_URL}/api/photo/photo/?project=${projectId}`, { headers: { 'Authorization': `Bearer ${token}` } }),
                    fetch(`${API_URL}/api/photo/video/?project=${projectId}`, { headers: { 'Authorization': `Bearer ${token}` } }),
                ])
                if (!res.ok) throw new Error('Failed to fetch photos')
                const data = await res.json()
                const photos = (data.results || data || []).map(mapApiPhoto)
                let videos = []
                if (vres.ok) {
                    const vdata = await vres.json()
                    videos = (vdata.results || vdata || []).map(mapApiVideo)
                }
                const merged = [...photos, ...videos].sort(
                    (a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0)
                )
                galleryFetchedProjectRef.current = projectId
                setApiPhotos(merged)
            } catch (err) {
                console.error('[Gallery] Error fetching photos:', err)
            } finally {
                setGalleryLoading(false)
            }
        }
        fetchPhotos()
    }, [activeTab, currentProject?.id, apiPhotos.length])

    // Fetch albums for the current project when Gallery tab is active
    useEffect(() => {
        if (activeTab === 'Gallery' && currentProject?.id) {
            // Reset gallery state when switching projects
            setGalleryTab('All Photos')
            setGalleryPage(1)
            setGallerySearch('')
            setSelectedPhoto(null)
            setApiPhotos([])  // Clear photos so they re-fetch for the new project
            
            const fetchAlbums = async () => {
                try {
                    const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
                    const token = (localStorage.getItem(JWT_STORAGE_KEY) || sessionStorage.getItem(JWT_STORAGE_KEY))
                    const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'
                    const res = await fetch(`${API_URL}/api/photo/album/?project=${currentProject.id}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    })
                    if (!res.ok) return
                    const data = await res.json()
                    const albums = data.results || data || []
                    setProjectAlbums(albums)
                    // Auto-fetch photos for the current tab if it matches an album (handles page refresh)
                    const urlParams = new URLSearchParams(window.location.search)
                    const currentGalleryTab = urlParams.get('gallery_tab') || 'All Photos'
                    const matchedAlbum = albums.find(a => a.name === currentGalleryTab)
                    if (matchedAlbum) {
                        fetchAlbumPhotos(matchedAlbum.id)
                    }
                } catch (e) { /* silent */ }
            }
            fetchAlbums()
        }
    }, [activeTab, currentProject?.id])

    // Load unassigned devices when Add Device modal opens
    useEffect(() => {
        if (showAddDeviceModal && projectSites.length > 0 && currentProject) {
            // Create a working copy of project sites for the modal
            setModalProjectSites(JSON.parse(JSON.stringify(projectSites)))
            setDeviceAssignments({})
            
            // Clear any previous messages when modal opens
            setDeviceError('')
            setSuccessMessage('')
            
            // FIXED: Fetch unassigned devices from the backend API (Symfony approach)
            // Unassigned devices are devices where site IS NULL in the database
            const loadUnassignedDevices = async () => {
                try {
                    console.log(`[Unassigned Devices] Fetching from API for project ${currentProject.id}...`)
                    
                    // Fetch unassigned devices from backend API
                    const backendDevices = await projectsApi.fetchUnassignedDevices(currentProject.id)
                    console.log(`[Unassigned Devices] Fetched ${backendDevices.length} devices from backend API`, backendDevices)
                    
                    // Use ONLY backend devices - don't merge with stale localStorage data
                    // This ensures we always show the current state from the database
                    setUnassignedDevices(backendDevices)
                    
                    // Clear localStorage cache after successful API fetch to remove stale data
                    const storageKey = `unassigned_devices_${currentProject.id}`
                    localStorage.removeItem(storageKey)
                    console.log(`[Unassigned Devices] Cleared localStorage cache for project ${currentProject.id}`)
                    
                } catch (error) {
                    console.error(`[Unassigned Devices] Error fetching from API:`, error)
                    
                    // Fallback: Load from localStorage only if API fails
                    console.log('[Unassigned Devices] Falling back to localStorage only')
                    const localDevices = getUnassignedDevicesFromStorage(currentProject.id)
                    setUnassignedDevices(localDevices)
                    
                    // Don't show error for unassigned devices fetch - it can fail silently and load from localStorage
                    // setDeviceError('Note: Could not load unassigned devices from server')
                    // setTimeout(() => setDeviceError(''), 5000)
                }
            }
            
            loadUnassignedDevices()
            
            // Set first site as default if available
            if (!selectedSiteForDevice && projectSites.length > 0) {
                setSelectedSiteForDevice(projectSites[0].id)
            }
        }
    }, [showAddDeviceModal, projectSites, currentProject])

    // Load widget categories when Add Widgets modal opens
    useEffect(() => {
        if (showAddWidgetsModal) {
            loadWidgetCategories()
        }
    }, [showAddWidgetsModal])

    // Function to load widget categories from API
    const loadWidgetCategories = async () => {
        setLoadingWidgetCategories(true)
        setWidgetCategoriesError(null)
        try {
            console.log('[Widget Categories] Fetching from API...')
            const data = await widgetApi.getWidgetsByCategory()
            console.log('[Widget Categories] Loaded:', data)
            
            // Transform API response to match our UI structure
            const categories = data.categories || []
            setDynamicWidgetCategories(categories)
            setSearchWidgetsQuery('')
        } catch (error) {
            console.error('[Widget Categories] Error loading:', error)
            setWidgetCategoriesError('Failed to load widget categories. Please try again.')
            setDynamicWidgetCategories([])
        } finally {
            setLoadingWidgetCategories(false)
        }
    }

    // Clean up the play interval on unmount — MUST be before any early returns
    // so React hooks count stays consistent every render
    useEffect(() => {
        return () => {
            if (playIntervalRef.current) clearInterval(playIntervalRef.current)
        }
    }, [])

    // ── Persist sidebar selection (scope-aware) ──────────────────────────────
    // The naive "save on every state change" approach has a race: when the
    // user lands on `/dashboard` (no projectId yet) after login, then the
    // router resolves to `/dashboard/project/N`, the scope key changes from
    // (userId, undefined) to (userId, N) while state is still [] / {} — and
    // we'd happily save THAT empty state, wiping the user's saved selection.
    //
    // Fix: only save when the scope key matches what we've previously tracked
    // (i.e. the user is making a real change to the current project's
    // selection).  When the scope key flips to a new (userId, projectId),
    // first HYDRATE from localStorage and remember the new scope — without
    // saving on that transition.
    //
    // Hooks block lives here — above the `return <NoActiveProject />` early
    // returns — so React's hook count stays stable across all render paths.
    const persistScopeRef = useRef('')
    useEffect(() => {
      if (!persistedUserId || !projectId) return
      const scope = `${persistedUserId}:${projectId}`
      if (persistScopeRef.current !== scope) {
        // Scope just changed (login, project switch, projectId-arrives-late).
        // Re-hydrate from localStorage; skip saving for this run.
        persistScopeRef.current = scope
        const saved = loadSelParams(persistedUserId, projectId)
        if (saved) {
          setSelectedParameters(saved.selectedParameters)
          setCheckedItems(saved.checkedItems)
        }
        return
      }
      saveSelParams(persistedUserId, projectId, selectedParameters, checkedItems)

      // Mirror the OWNER's selection to the server so the public portal can
      // show the same parameters (the localStorage copy above is per-browser
      // and invisible to anonymous visitors). Owner-only: a viewer's or
      // admin's working selection must not redefine the public view.
      if (currentProject?.user_role === 'Owner') {
        setWidgetConfig(
          PUBLIC_SELECTION_PREFIX, projectId, PUBLIC_SELECTION_WIDGET_ID,
          { parameterIds: (selectedParameters || []).map((p) => p.id) },
        )
      }
      // `user_role` in deps so the owner's *existing* selection syncs to the
      // server as soon as the role resolves — no manual toggle required.
    }, [persistedUserId, projectId, selectedParameters, checkedItems, currentProject?.user_role])

    // ── "As of …" header clock ────────────────────────────────────────────────
    // Hooks declared near the top of the component (BEFORE any early
    // `return <NoActiveProject />`) so the hook order stays stable across
    // every render — required by the Rules of Hooks (React error #310).
    //
    // Renders the current time in the customer's configured timezone
    // (Account.timezone via auth-store).  resolveIanaTz() bridges the
    // legacy display-string profile format → IANA names that Intl accepts.
    // The clock ticks every 30 s so the label stays current without a
    // manual reload.
    const rawTz = useAuthStore(s => s.user?.timezone)
    const customerTz = useMemo(() => resolveIanaTz(rawTz), [rawTz])
    const [nowInstant, setNowInstant] = useState(() => new Date())
    useEffect(() => {
      const id = setInterval(() => setNowInstant(new Date()), 30 * 1000)
      return () => clearInterval(id)
    }, [])

    // 1-second tick that drives the "next auto-refresh in M:SS" countdown in the
    // header. Separate from the 30s "As of" clock so the countdown updates smoothly.
    const [countdownTick, setCountdownTick] = useState(0)
    useEffect(() => {
      const id = setInterval(() => setCountdownTick((t) => t + 1), 1000)
      return () => clearInterval(id)
    }, [])
    const nextRefreshLabel = useMemo(() => {
      void countdownTick   // re-evaluate every second
      if (!nextAutoRefreshAt) return ''        // auto-refresh off or paused
      const remainingMs = nextAutoRefreshAt - Date.now()
      if (remainingMs <= 0) return 'Refreshing…'
      const totalSec = Math.ceil(remainingMs / 1000)
      const m = Math.floor(totalSec / 60)
      const s = totalSec % 60
      return `Auto-refresh in ${m}:${String(s).padStart(2, '0')}`
    }, [nextAutoRefreshAt, countdownTick])
    const nowLabel = useMemo(() => {
      try {
        return new Intl.DateTimeFormat('en-US', {
          timeZone: customerTz,
          month: 'long', day: 'numeric', year: 'numeric',
          hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short',
        }).format(nowInstant)
      } catch {
        // Final safety net — resolveIanaTz should always return something
        // Intl accepts, but if a future TZ string slips through we still
        // render *something* instead of crashing.
        return nowInstant.toLocaleString('en-US', {
          month: 'long', day: 'numeric', year: 'numeric',
          hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short',
        })
      }
    }, [nowInstant, customerTz])

    // Load Presentation config and rebuild the rotation whenever the project or
    // its templates change. Also reorders `templates` to match the saved
    // presentation order so the nav dropdown reflects what the user configured.
    useEffect(() => {
        const pid = currentProject?.id
        if (!pid || templates.length === 0) {
            setPresentationRotation([])
            return
        }

        // Guard: if templates changed because WE reordered them (not a real
        // external change), skip re-fetching to avoid an infinite loop.
        // Include pid so a project change always triggers a fresh fetch.
        const currentSig = `${pid}:${templates.map((t) => t.id).join(',')}`
        if (presOrderSigRef.current === currentSig) return

        let cancelled = false
        dashboardApi
            .getPresentationConfig(pid)
            .then((res) => {
                if (cancelled) return
                const cfg = res.data || {}
                setPresentationConfig({
                    pauseSeconds: Math.max(30, cfg.pause_seconds || 30),
                    transition: cfg.transition || 'fade',
                    randomOrder: !!cfg.random_order,
                })

                // ── Reorder templates to match the saved presentation order ──
                // cfg.dashboards is already sorted: included first (by order),
                // then excluded in their original order.
                const orderedDashboards = cfg.dashboards || []
                const reordered = []
                orderedDashboards.forEach((d) => {
                    const t = templates.find((t) => t.name === d.name || t.id === d.id)
                    if (t) reordered.push(t)
                })
                // Append any templates not mentioned in the config (safety net)
                templates.forEach((t) => {
                    if (!reordered.find((r) => r.id === t.id)) reordered.push(t)
                })

                const reorderedSig = `${pid}:${reordered.map((t) => t.id).join(',')}`
                presOrderSigRef.current = reorderedSig // mark so next run is skipped

                // Build rotation from the NEW order (indices into reordered array)
                const included = orderedDashboards.filter((d) => d.included)
                const rotation = included
                    .map((d) => {
                        let idx = reordered.findIndex((t) => t.name === d.name)
                        if (idx < 0) idx = reordered.findIndex((t) => t.id === d.id)
                        return idx
                    })
                    .filter((idx) => idx >= 0)
                setPresentationRotation(rotation)

                if (reorderedSig !== currentSig) {
                    // Remember which template the user is currently viewing so
                    // we can restore currentMenuIndex after the reorder.
                    const currentTemplateName = templates[currentMenuIndex]?.name
                    setTemplates(reordered)
                    const newIdx = reordered.findIndex((t) => t.name === currentTemplateName)
                    if (newIdx >= 0 && newIdx !== currentMenuIndex) {
                        setCurrentMenuIndex(newIdx)
                    }
                }
            })
            .catch(() => {
                if (!cancelled) setPresentationRotation([])
            })
        return () => {
            cancelled = true
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentProject?.id, templates])

    // ── Auto-refresh-when-idle: load the project's configured idle timeout ────
    // Project-level setting (Project.dashboard_refresh_interval); falls back to
    // the 900s default if the project has none or the request fails.
    useEffect(() => {
        if (!projectId) return
        let cancelled = false
        dashboardApi
            .getAutoRefreshConfig(projectId)
            .then((res) => {
                if (cancelled) return
                const secs = Number(res.data?.auto_refresh_seconds)
                setAutoRefreshSeconds(Number.isFinite(secs) && secs >= 300 ? secs : 600)
            })
            .catch(() => {
                if (!cancelled) setAutoRefreshSeconds(600)
            })
        return () => { cancelled = true }
    }, [projectId])

    // ── Auto-refresh-when-idle: the idle timer itself ─────────────────────────
    // After `autoRefreshSeconds` of no DELIBERATE interaction, refresh the
    // dashboard IN PLACE (NOT a page reload): bump dashRefreshTick, which remounts
    // the current slide so its widgets re-fetch the latest data, and re-fetches
    // the sites payload (map pin + popup). Same effect as the green Refresh button
    // — the page, scroll position and parameter selection all stay put.
    //
    // Only deliberate interaction (click / key / tap / scroll-wheel) resets the
    // countdown — plain mouse MOVEMENT does NOT. This is the key fix: previously a
    // user simply watching the dashboard (and nudging the cursor) reset the timer
    // on every mousemove, so the refresh effectively never fired. Presentation
    // playback has its own cadence, so idle-refresh pauses while it plays.
    useEffect(() => {
        const secs = Number(autoRefreshSeconds)
        if (!Number.isFinite(secs) || secs < 300) { setNextAutoRefreshAt(null); return }
        // Paused only while a presentation is playing (it has its own cadence).
        // Edit Mode is NOT paused: refreshDashboardRef does a data-only refresh
        // (dataRefreshNonce, no remount) while editing, so the dashboard keeps
        // updating without discarding the user's unsaved layout changes.
        if (isPlaying) { setNextAutoRefreshAt(null); return }

        // A PERIODIC checker (not a single resettable timeout) so one stray click
        // early in the window can't silently cancel the whole cycle — the refresh
        // still fires at the next interval boundary once the user is idle. This is
        // why the old single-timeout version could appear to "never fire".
        let lastInteraction = 0            // 0 = treat as idle from load
        const bump = () => { lastInteraction = Date.now() }
        const EVENTS = ['mousedown', 'keydown', 'touchstart', 'wheel']
        const opts = { passive: true }
        EVENTS.forEach((ev) => window.addEventListener(ev, bump, opts))

        let lastRefresh = Date.now()
        const CHECK_MS = 10000             // evaluate conditions every 10s
        const ACTIVE_GRACE_MS = 30000      // don't yank the view mid-interaction
        setNextAutoRefreshAt(lastRefresh + secs * 1000)   // seed the header countdown
        const id = setInterval(() => {
            // Refresh regardless of whether the tab is active/visible (per user
            // requirement). Browsers still throttle background-tab timers, so a
            // hidden tab may refresh a little late — but it WILL refresh.
            const now = Date.now()
            if (now - lastRefresh < secs * 1000) return                  // interval not elapsed yet
            if (lastInteraction && now - lastInteraction < ACTIVE_GRACE_MS) return  // user active → defer
            lastRefresh = now
            setNextAutoRefreshAt(now + secs * 1000)   // reset countdown to the next cycle
            // In-place refresh — advance the window to "now" AND re-fetch the
            // current dashboard's data without reloading the page.
            refreshDashboardRef.current()
        }, CHECK_MS)

        return () => {
            clearInterval(id)
            EVENTS.forEach((ev) => window.removeEventListener(ev, bump, opts))
        }
    }, [autoRefreshSeconds, isPlaying])

    // ── Header navigation applied in place ──────────────────────────────────
    // The header menus (desktop AppHeader and the mobile hamburger drawer)
    // write `wq_activeTab` / `wq_current*Index` and then navigate. Those keys
    // are read ONLY by this component's useState initialisers, i.e. once at
    // mount — so when the user is already on /dashboard/project/<id> the
    // navigate() is a no-op, nothing remounts, and the click does nothing.
    // That is exactly the case for the mobile drawer, which is always opened
    // from the dashboard itself. useHeaderNav fires `wq_nav_change` alongside
    // the navigate so we can apply the selection here without a reload.
    //
    // Deliberately placed in the hooks block, ABOVE the `<NoActiveProject />`
    // early returns, so the hook order stays stable across renders.
    useEffect(() => {
        const onNavChange = (e) => {
            const { tab, indexKey, indexValue, label, report } = e.detail || {}

            // "Config Dashboards" opens the modal instead of switching
            // sub-page — same behaviour as selectMenuItem() on desktop.
            if (label === 'Config Dashboards' || label === 'Configure Dashboards') {
                setShowConfigureDashboardsModal(true)
                return
            }

            // Report actions from the mobile drawer — mirror the desktop Report
            // dropdown exactly so the drawer opens the SAME saved report / list /
            // create flow instead of a static placeholder menu.
            if (report && typeof report === 'object') {
                setActiveTab('Report')
                if (report.action === 'view') {
                    setReportMode('view'); setReportId(report.id)
                } else if (report.action === 'list') {
                    setShowReportList(true)
                } else if (report.action === 'create') {
                    setReportMode('create'); setReportId(null)
                }
                return
            }

            if (indexKey != null && indexValue != null) {
                const setterFor = {
                    wq_currentMenuIndex:          setCurrentMenuIndex,
                    wq_currentDataAnalyticsIndex: setCurrentDataAnalyticsIndex,
                    wq_currentReportIndex:        setCurrentReportIndex,
                    wq_currentSettingsIndex:      setCurrentSettingsIndex,
                    wq_currentLiveIndex:          setCurrentLiveIndex,
                }[indexKey]
                if (setterFor) setterFor(Number(indexValue))
            }
            if (tab) setActiveTab(tab)
        }
        window.addEventListener('wq_nav_change', onNavChange)
        return () => window.removeEventListener('wq_nav_change', onNavChange)
    }, [])

    // Defensive: a Collaborator must never *view* Validate/Formulas (indexes 1/2)
    // even if a stale localStorage index or a nav event lands them there — snap
    // back to Visualize (0). The sub-tab/menu buttons are also disabled for them.
    // (Declared here with the other hooks, i.e. before any early return below, so
    // the hook order stays stable.)
    useEffect(() => {
        if (currentProject?.user_role === 'Collaborator'
            && (currentDataAnalyticsIndex === 1 || currentDataAnalyticsIndex === 2)) {
            setCurrentDataAnalyticsIndex(0)
        }
    }, [currentProject?.user_role, currentDataAnalyticsIndex])


    // Show loading state while fetching projects
    if (loadingProjects) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center">
                    <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
                    <p className="text-gray-600">Loading projects...</p>
                </div>
            </div>
        )
    }

    // Show "No Active Project" screen if user has no projects
    if (projects.length === 0) {
        return <NoActiveProject />
    }

    // If no project is set (shouldn't happen but just in case), show no active project
    if (!currentProject) {
        return <NoActiveProject />
    }


    const galleryTabs = ['All Photos', ...projectAlbums.map(a => a.name)]

    const galleryPhotos = [
        { id: 1, src: 'https://images.unsplash.com/photo-1444464666168-49d633b86797?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1444464666168-49d633b86797?w=1200&h=800&fit=crop', category: 'Bird', title: 'Black Vulture - Coragyps atratus', description: 'Black Vulture - Coragyps atratus', originalFilename: 'BlackVulture_2024-06-27.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Fauna', uploadedBy: 'emma.mcroskey@fondriest.com', date: '06/27/2024 10:15 UTC-04:00', dimensions: '1620 x 1080', size: '486.28 KB', totalSize: '527.12 KB', location: 'Site - Pond Buoy' },
        { id: 2, src: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=1200&h=800&fit=crop', category: 'Deployment', title: 'XB-200 Data Buoy', description: 'XB-200 Data Buoy', originalFilename: 'XB-200_Buoy_2024-05-15.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Deployment', uploadedBy: 'emma.mcroskey@fondriest.com', date: '05/15/2024 14:30 UTC-04:00', dimensions: '1600 x 1200', size: '512.45 KB', totalSize: '551.22 KB', location: 'Site - Pond Buoy' },
        { id: 3, src: 'https://images.unsplash.com/photo-1585066699728-a56ef32a024a?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1585066699728-a56ef32a024a?w=1200&h=800&fit=crop', category: 'Insect', title: 'Blue Dasher - Pachydiplax longipennis', description: 'Blue Dasher - Pachydiplax longipennis', originalFilename: 'BlueDasher_2024-07-10.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Fauna', uploadedBy: 'emma.mcroskey@fondriest.com', date: '07/10/2024 09:45 UTC-04:00', dimensions: '1640 x 1230', size: '498.56 KB', totalSize: '540.15 KB', location: 'Site - Pond Buoy' },
        { id: 4, src: 'https://images.unsplash.com/photo-1480044965905-02098d419e96?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1480044965905-02098d419e96?w=1200&h=800&fit=crop', category: 'Bird', title: 'Great Blue Heron - Ardea herodias', description: 'Great Blue Heron - Ardea herodias', originalFilename: 'BlueHeron_2024-06-12.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Fauna', uploadedBy: 'emma.mcroskey@fondriest.com', date: '06/12/2024 11:20 UTC-04:00', dimensions: '1680 x 1050', size: '515.33 KB', totalSize: '558.44 KB', location: 'Site - Pond Buoy' },
        { id: 5, src: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1200&h=800&fit=crop', category: 'Fish', title: 'Bluegill - Epomis Macrochirus', description: 'Bluegill - Epomis Macrochirus', originalFilename: 'Bluegill_2024-08-20.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Fauna', uploadedBy: 'emma.mcroskey@fondriest.com', date: '08/20/2024 13:00 UTC-04:00', dimensions: '1600 x 1200', size: '520.67 KB', totalSize: '565.21 KB', location: 'Site - Pond Buoy' },
        { id: 6, src: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=1200&h=800&fit=crop', category: 'Deployment', title: 'Weather station comparison...', description: 'Weather station comparison', originalFilename: 'WeatherStation_2024-05-20.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Deployment', uploadedBy: 'emma.mcroskey@fondriest.com', date: '05/20/2024 15:30 UTC-04:00', dimensions: '1620 x 1080', size: '505.44 KB', totalSize: '548.33 KB', location: 'Site - Weather Station' },
        { id: 7, src: 'https://images.unsplash.com/photo-1518709766631-a6a7f45921c3?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1518709766631-a6a7f45921c3?w=1200&h=800&fit=crop', category: 'Deployment', title: 'Diver-Link', description: 'Diver-Link', originalFilename: 'DiverLink_2024-06-05.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Deployment', uploadedBy: 'emma.mcroskey@fondriest.com', date: '06/05/2024 10:00 UTC-04:00', dimensions: '1600 x 1200', size: '518.22 KB', totalSize: '560.88 KB', location: 'Site - Pond Buoy' },
        { id: 8, src: 'https://images.unsplash.com/photo-1474905089749-3eaa06dc99d8?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1474905089749-3eaa06dc99d8?w=1200&h=800&fit=crop', category: 'Fungi and Lichens', title: 'Funeral Bell - Galerina marginata', description: 'Funeral Bell - Galerina marginata', originalFilename: 'FuneralBell_2024-07-25.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Flora', uploadedBy: 'emma.mcroskey@fondriest.com', date: '07/25/2024 08:30 UTC-04:00', dimensions: '1640 x 1090', size: '492.15 KB', totalSize: '535.77 KB', location: 'Site - Land Based' },
        { id: 9, src: 'https://images.unsplash.com/photo-1530587191325-3db32d826c18?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1530587191325-3db32d826c18?w=1200&h=800&fit=crop', category: 'Plant', title: 'Algae growth on pond', description: 'Algae growth on pond', originalFilename: 'AlgaeGrowth_2024-08-10.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Flora', uploadedBy: 'emma.mcroskey@fondriest.com', date: '08/10/2024 12:15 UTC-04:00', dimensions: '1620 x 1200', size: '508.88 KB', totalSize: '552.66 KB', location: 'Site - Pond Buoy' },
        { id: 10, src: 'https://images.unsplash.com/photo-1497436072909-60f360e1d4b1?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1497436072909-60f360e1d4b1?w=1200&h=800&fit=crop', category: 'Deployment', title: 'Garden Hobo Equipment', description: 'Garden Hobo Equipment', originalFilename: 'GardenHobo_2024-06-18.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Deployment', uploadedBy: 'emma.mcroskey@fondriest.com', date: '06/18/2024 14:45 UTC-04:00', dimensions: '1680 x 1120', size: '514.22 KB', totalSize: '557.99 KB', location: 'Site - Land Based' },
        { id: 11, src: 'https://images.unsplash.com/photo-1500829243541-74b677fecc30?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1500829243541-74b677fecc30?w=1200&h=800&fit=crop', category: 'Plant', title: 'Jimsonweed - Datura stramonium', description: 'Jimsonweed - Datura stramonium', originalFilename: 'Jimsonweed_2024-07-30.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Flora', uploadedBy: 'emma.mcroskey@fondriest.com', date: '07/30/2024 09:00 UTC-04:00', dimensions: '1600 x 1200', size: '510.77 KB', totalSize: '555.21 KB', location: 'Site - Land Based' },
        { id: 12, src: 'https://images.unsplash.com/photo-1516728778615-2d590ea1855e?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1516728778615-2d590ea1855e?w=1200&h=800&fit=crop', category: 'Amphibian', title: "Blanchard's Cricket Frog - Acris blanchardi", description: "Blanchard's Cricket Frog - Acris blanchardi", originalFilename: 'CricketFrog_2024-08-05.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Fauna', uploadedBy: 'emma.mcroskey@fondriest.com', date: '08/05/2024 16:20 UTC-04:00', dimensions: '1640 x 1100', size: '521.44 KB', totalSize: '567.88 KB', location: 'Site - Pond Buoy' },
        { id: 13, src: 'https://images.unsplash.com/photo-1552728089-57bdde30beb3?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1552728089-57bdde30beb3?w=1200&h=800&fit=crop', category: 'Bird', title: 'Canada Goose - Goslings', description: 'Canada Goose - Goslings', originalFilename: 'CanadaGoose_2024-05-10.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Fauna', uploadedBy: 'emma.mcroskey@fondriest.com', date: '05/10/2024 07:30 UTC-04:00', dimensions: '1680 x 1120', size: '516.55 KB', totalSize: '560.22 KB', location: 'Site - Pond Buoy' },
        { id: 14, src: 'https://images.unsplash.com/photo-1551085254-e96b210db58a?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1551085254-e96b210db58a?w=1200&h=800&fit=crop', category: 'Bird', title: 'Spotted Sandpiper - Actitis macularius', description: 'Spotted Sandpiper - Actitis macularius', originalFilename: 'SpottedSandpiper_2024-06-22.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Fauna', uploadedBy: 'emma.mcroskey@fondriest.com', date: '06/22/2024 13:45 UTC-04:00', dimensions: '1620 x 1200', size: '509.66 KB', totalSize: '553.44 KB', location: 'Site - Pond Buoy' },
        { id: 15, src: 'https://images.unsplash.com/photo-1509316975850-ff9c5deb0cd9?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1509316975850-ff9c5deb0cd9?w=1200&h=800&fit=crop', category: 'Pond', title: 'New plant growth on pond...', description: 'New plant growth on pond', originalFilename: 'PondPlants_2024-07-08.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Flora', uploadedBy: 'emma.mcroskey@fondriest.com', date: '07/08/2024 10:30 UTC-04:00', dimensions: '1640 x 1100', size: '504.33 KB', totalSize: '548.99 KB', location: 'Site - Pond Buoy' },
        { id: 16, src: 'https://images.unsplash.com/photo-1502082553048-f009c37129b9?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1502082553048-f009c37129b9?w=1200&h=800&fit=crop', category: 'Plant', title: 'Honeysuckle - Needs Removed', description: 'Honeysuckle - Lonicera', originalFilename: 'Honeysuckle_2024-08-12.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Flora', uploadedBy: 'emma.mcroskey@fondriest.com', date: '08/12/2024 11:50 UTC-04:00', dimensions: '1600 x 1200', size: '517.88 KB', totalSize: '562.55 KB', location: 'Site - Land Based' },
        { id: 17, src: 'https://images.unsplash.com/photo-1518173835702-3a7e5f1a3cce?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1518173835702-3a7e5f1a3cce?w=1200&h=800&fit=crop', category: 'Deployment', title: 'Barred Owl Box', description: 'Barred Owl Box', originalFilename: 'OwlBox_2024-05-25.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Deployment', uploadedBy: 'emma.mcroskey@fondriest.com', date: '05/25/2024 09:15 UTC-04:00', dimensions: '1680 x 1120', size: '519.22 KB', totalSize: '564.11 KB', location: 'Site - Land Based' },
        { id: 18, src: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1200&h=800&fit=crop', category: 'Plant', title: 'American Pokeweed - Phytolacca americana', description: 'American Pokeweed - Phytolacca americana', originalFilename: 'Pokeweed_2024-08-18.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Flora', uploadedBy: 'emma.mcroskey@fondriest.com', date: '08/18/2024 14:00 UTC-04:00', dimensions: '1620 x 1080', size: '506.44 KB', totalSize: '550.22 KB', location: 'Site - Land Based' },
        { id: 19, src: 'https://images.unsplash.com/photo-1470058869958-2a77ade41c02?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1470058869958-2a77ade41c02?w=1200&h=800&fit=crop', category: 'Arachnid', title: 'Arabesque Orbweaver - Neoscona arabesca', description: 'Arabesque Orbweaver - Neoscona arabesca', originalFilename: 'Orbweaver_2024-07-15.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Fauna', uploadedBy: 'emma.mcroskey@fondriest.com', date: '07/15/2024 15:30 UTC-04:00', dimensions: '1640 x 1090', size: '512.77 KB', totalSize: '557.66 KB', location: 'Site - Land Based' },
        { id: 20, src: 'https://images.unsplash.com/photo-1497752531616-c3afd9760a11?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1497752531616-c3afd9760a11?w=1200&h=800&fit=crop', category: 'Amphibian', title: 'Northern Leopard Frog - Lithobates pipiens', description: 'Northern Leopard Frog - Lithobates pipiens', originalFilename: 'LeopardFrog_2024-06-28.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Fauna', uploadedBy: 'emma.mcroskey@fondriest.com', date: '06/28/2024 12:45 UTC-04:00', dimensions: '1680 x 1120', size: '515.33 KB', totalSize: '559.88 KB', location: 'Site - Pond Buoy' },
        { id: 21, src: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=1200&h=800&fit=crop', category: 'Deployment', title: 'miniDOT Loggers', description: 'miniDOT Loggers', originalFilename: 'miniDOT_2024-05-12.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Deployment', uploadedBy: 'emma.mcroskey@fondriest.com', date: '05/12/2024 08:00 UTC-04:00', dimensions: '1600 x 1200', size: '520.11 KB', totalSize: '564.33 KB', location: 'Site - Pond Buoy' },
        { id: 22, src: 'https://images.unsplash.com/photo-1518709766631-a6a7f45921c3?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1518709766631-a6a7f45921c3?w=1200&h=800&fit=crop', category: 'Arachnid', title: 'Dark Fishing Spider in nest b...', description: 'Dark Fishing Spider in nest', originalFilename: 'FishingSpider_2024-07-22.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Fauna', uploadedBy: 'emma.mcroskey@fondriest.com', date: '07/22/2024 16:00 UTC-04:00', dimensions: '1620 x 1080', size: '508.55 KB', totalSize: '552.77 KB', location: 'Site - Pond Buoy' },
        { id: 23, src: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1200&h=800&fit=crop', category: 'Deployment', title: 'EWC', description: 'Environmental Water Collection', originalFilename: 'EWC_2024-06-08.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Deployment', uploadedBy: 'emma.mcroskey@fondriest.com', date: '06/08/2024 13:30 UTC-04:00', dimensions: '1640 x 1100', size: '513.44 KB', totalSize: '558.99 KB', location: 'Site - Pond Buoy' },
        { id: 24, src: 'https://images.unsplash.com/photo-1453748866136-b1dd97284f49?w=300&h=200&fit=crop', largeSrc: 'https://images.unsplash.com/photo-1453748866136-b1dd97284f49?w=1200&h=800&fit=crop', category: 'Plant', title: 'Watercress - Nasturtium', description: 'Watercress - Nasturtium officinale', originalFilename: 'Watercress_2024-08-03.jpg', cameraSN: 'N/A', tags: 'N/A', album: 'Flora', uploadedBy: 'emma.mcroskey@fondriest.com', date: '08/03/2024 10:20 UTC-04:00', dimensions: '1600 x 1200', size: '511.22 KB', totalSize: '556.44 KB', location: 'Site - Land Based' },
    ]

    const photosPerPage = 12
    const matchedAlbum = galleryTab !== 'All Photos' ? projectAlbums.find(a => a.name === galleryTab) : null
    const activeGalleryPhotos = matchedAlbum && albumPhotos[matchedAlbum.id]?.length > 0
        ? albumPhotos[matchedAlbum.id]
        // A video-only project has no http image src; still treat it as real
        // data (not the demo fallback) so its clips show.
        : (apiPhotos.length > 0 && apiPhotos.some(p => (p.src && p.src.startsWith('http')) || p.mediaType === 'video')) ? apiPhotos : galleryPhotos
    const albumFilteredPhotos = matchedAlbum
        // Keep videos (which legitimately have an empty image src) alongside
        // photos that have a real src.
        ? (albumPhotos[matchedAlbum.id] || []).filter(p => p.mediaType === 'video' || (p.src && p.src.length > 0))
        : galleryTab !== 'All Photos'
            ? activeGalleryPhotos.filter(p => (p.album || '') === galleryTab)
            : activeGalleryPhotos
    const filteredGalleryPhotos = gallerySearch.trim()
        ? albumFilteredPhotos.filter(p =>
            (p.title || '').toLowerCase().includes(gallerySearch.toLowerCase()) ||
            (p.category || '').toLowerCase().includes(gallerySearch.toLowerCase()) ||
            (p.description || '').toLowerCase().includes(gallerySearch.toLowerCase())
          )
        : albumFilteredPhotos
    // Filter menu → per-field "contains" filters (AND across the fields that
    // are set) plus an uploaded-date range. Empty fields are ignored.
    // Unparseable dates fall back to id (which tracks upload order) so real API
    // photos still compare chronologically.
    const photoTime = (p) => { const t = Date.parse(p.timestamp || p.date); return Number.isNaN(t) ? (Number(p.id) || 0) : t }
    const galleryTextMatch = (field, needle) =>
        !needle.trim() || (field || '').toString().toLowerCase().includes(needle.trim().toLowerCase())
    const fieldFilteredPhotos = filteredGalleryPhotos.filter(p => {
        if (!galleryTextMatch(p.title, galleryFilters.title)) return false
        if (!galleryTextMatch(p.description, galleryFilters.description)) return false
        if (!galleryTextMatch(p.tags, galleryFilters.tags)) return false
        if (!galleryTextMatch(p.location, galleryFilters.location)) return false
        if (galleryFilters.dateFrom || galleryFilters.dateTo) {
            const t = photoTime(p)
            const lo = galleryFilters.dateFrom ? Date.parse(`${galleryFilters.dateFrom}T00:00:00`) : null
            const hi = galleryFilters.dateTo ? Date.parse(`${galleryFilters.dateTo}T23:59:59`) : null
            if (lo != null && !Number.isNaN(lo) && t < lo) return false
            if (hi != null && !Number.isNaN(hi) && t > hi) return false
        }
        return true
    })
    const activeGalleryFilterCount =
        ['title', 'description', 'tags', 'location'].filter(k => galleryFilters[k].trim()).length +
        ((galleryFilters.dateFrom || galleryFilters.dateTo) ? 1 : 0)
    // Show newest first (matches the backend's -date_uploaded ordering).
    const sortedGalleryPhotos = [...fieldFilteredPhotos].sort((a, b) => photoTime(b) - photoTime(a))
    const totalGalleryPages = Math.ceil(sortedGalleryPhotos.length / photosPerPage)
    const currentPhotos = sortedGalleryPhotos
        .slice((galleryPage - 1) * photosPerPage, galleryPage * photosPerPage)
        .map(p => photoEditsMap[p.id] ? { ...p, ...photoEditsMap[p.id] } : p)

    // "Config Dashboards" is ALWAYS listed now (like the Settings menu). The
    // modal opens read-only and its edit controls stay disabled until Edit Mode
    // is on (ConfigDashboardsModal is wrapped in EditModeGate), so viewers can
    // open it but can't change anything without entering Edit Mode.
    const dashboardMenuItems = [
        ...templates.map(t => t.name),
        'Config Dashboards',
    ]

    const dashboardCyclableItems = templates.length // Only cycle through template names

    const dataAnalyticsMenuItems = [
        'Visualize',
        'Validate',
        'Formulas'
    ]
    // Collaborators get Visualize only — Validate & Formulas are owner/admin
    // tools, so their menu items + sub-tabs are disabled for a Collaborator
    // (indexes 1 and 2 in dataAnalyticsMenuItems / the sub-tab row).
    const isCollaborator = currentProject?.user_role === 'Collaborator'
    const restrictedDataAnalyticsIndexes = isCollaborator ? new Set([1, 2]) : new Set()

    // reportMenuItems is built up top (above the early returns) — see the
    // hooks block near the saved-reports state at the start of the
    // component. Inlining it here would put it AFTER the `<NoActiveProject />`
    // early returns and React would crash with #310 on the empty-projects
    // render path.

    const settingsMenuItems = [
        'Users',
        'Project',
        'API',
        'Streaming Method',
        'Public Portal',
        'Embedded Data Applet',
        'RSS Feed',
        'Edit Mode',
    ]

    const liveMenuItems = [
        'Data Logger',
        'Communications',
        'Sensor Readings'
    ]

    const toggleMenu = (menu) => {
        setExpandedMenus(prev =>
            prev.includes(menu) ?
            prev.filter(m => m !== menu) :
            [...prev, menu]
        )
    }

    const toggleCheckbox = (id) => {
        console.log(`[Dashboard] toggleCheckbox called for ID:`, id)
        setCheckedItems(prev => ({
            ...prev,
            [id]: !prev[id]
        }))
        
        // Track selected parameters for graph
        // Find the parameter from projectSites — capture site/sensor/device/unit
        // so the Parameter Comparison view can title each chart "Site | Param (unit)".
        let parameterFound = null
        for (const site of projectSites) {
            if (site.devices) {
                for (const device of site.devices) {
                    if (device.sensors) {
                        for (const sensor of device.sensors) {
                            if (sensor.parameters) {
                                const param = sensor.parameters.find(p => p.id === id)
                                if (param) {
                                    parameterFound = {
                                        id: param.id,
                                        parameter_id: param.parameter_id,
                                        param_nid: param.param_nid ?? null,
                                        unit_id: param.unit_id ?? null,
                                        name: param.name,
                                        unit: param.unit || '',
                                        siteId: site.id,
                                        siteName: site.name || '',
                                        deviceName: device.name || '',
                                        sensorName: sensor.name || '',
                                    }
                                    console.log(`[Dashboard] Found parameter:`, parameterFound)
                                    break
                                }
                            }
                        }
                    }
                }
            }
        }
        
        // Add or remove from selected parameters
        if (parameterFound) {
            setSelectedParameters(prev => {
                const isSelected = prev.some(p => p.id === parameterFound.id)
                const newParams = isSelected 
                    ? prev.filter(p => p.id !== parameterFound.id)
                    : [...prev, parameterFound]
                console.log(`[Dashboard] Updated selectedParameters:`, newParams)
                return newParams
            })
        }
    }

    // Sensor-level checkbox: check/uncheck all parameters under a sensor at once.
    const toggleSensorCheckbox = (site, device, sensor) => {
        const newChecked = !checkedItems[sensor.id]
        const params = sensor.parameters || []
        const paramIds = new Set(params.map(p => p.id))

        setCheckedItems(prev => {
            const next = { ...prev, [sensor.id]: newChecked }
            for (const pid of paramIds) next[pid] = newChecked
            return next
        })

        setSelectedParameters(prev => {
            if (newChecked) {
                const existingIds = new Set(prev.map(p => p.id))
                const toAdd = params
                    .filter(p => !existingIds.has(p.id))
                    .map(p => ({
                        id: p.id,
                        parameter_id: p.parameter_id,
                        // Param NID — chart Y-axis grouping key; must survive the
                        // sensor-level select-all or those series lose grouping.
                        param_nid: p.param_nid ?? null,
                        unit_id: p.unit_id ?? null,
                        name: p.name,
                        unit: p.unit || '',
                        siteId: site.id,
                        siteName: site.name || '',
                        deviceName: device.name || '',
                        sensorName: sensor.name || '',
                    }))
                return [...prev, ...toAdd]
            } else {
                return prev.filter(p => !paramIds.has(p.id))
            }
        })
    }

    // Filter the "All Sites" tree by the search box. The query is split into
    // terms on spaces/commas and ALL terms must match (AND) — each term can match
    // at ANY level of a parameter's path (Site name/code, Logger name/UID, Sensor
    // name, Parameter name). This lets users combine criteria, e.g. "ajit temp" =
    // site "Ajit…" AND parameter "Temp…", or "g3-4g wind" = that logger AND wind
    // parameters. A single term keeps the old behaviour (inclusive: matching a
    // Site/Logger/Sensor by name shows everything under it, since each descendant
    // parameter's path then contains that term). (Box: "Search sites, loggers, parameters".)
    const getFilteredSites = () => {
        const raw = searchQuery.trim()
        if (!raw) {
            return projectSites
        }

        const terms = raw.toLowerCase().split(/[\s,]+/).filter(Boolean)
        const matchesAll = (text) => {
            const t = (text || '').toLowerCase()
            return terms.every((term) => t.includes(term))
        }
        const filtered = []

        projectSites.forEach(site => {
            const siteText = `${site.name || ''} ${site.site_code || ''}`
            const siteMatches = matchesAll(siteText)
            const devices = []

            ;(site.devices || []).forEach(device => {
                // Context accumulates down the hierarchy, so a term matched on an
                // ancestor (site/logger/sensor) counts for every parameter below it.
                const deviceText = `${siteText} ${device.name || ''} ${device.uid || ''}`
                const deviceMatches = matchesAll(deviceText)

                const sensors = []
                ;(device.sensors || []).forEach(sensor => {
                    const sensorText = `${deviceText} ${sensor.name || ''}`
                    const sensorMatches = matchesAll(sensorText)
                    const parameters = sensorMatches
                        ? (sensor.parameters || [])
                        : (sensor.parameters || []).filter(
                              p => matchesAll(`${sensorText} ${p.name || ''}`))
                    if (sensorMatches || parameters.length > 0) {
                        sensors.push({ ...sensor, parameters })
                    }
                })

                // Keep the logger if some params matched, or the site+logger context
                // alone already satisfies every term (e.g. a logger with no params).
                if (sensors.length > 0) {
                    devices.push({ ...device, sensors })
                } else if (deviceMatches) {
                    devices.push(device)
                }
            })

            if (devices.length > 0 || siteMatches) {
                filtered.push({ ...site, devices })
            }
        })

        return filtered
    }

    // Site drag-and-drop handlers (edit mode)
    const handleSiteDragStart = (e, siteId) => {
        setDraggedSiteId(siteId)
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', siteId)
    }

    const handleSiteDragOver = (e, targetSiteId) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        if (targetSiteId !== draggedSiteId) {
            setDragOverSiteId(targetSiteId)
        }
    }

    const handleSiteDrop = (e, targetSiteId) => {
        e.preventDefault()
        setDragOverSiteId(null)
        if (!draggedSiteId || draggedSiteId === targetSiteId) {
            setDraggedSiteId(null)
            return
        }
        const fromSite = projectSites.find(s => s.id === draggedSiteId)
        const toSite = projectSites.find(s => s.id === targetSiteId)
        setPendingSiteMove({ fromId: draggedSiteId, toId: targetSiteId, fromName: fromSite?.name, toName: toSite?.name })
        setShowMoveSiteConfirm(true)
        setDraggedSiteId(null)
    }

    const handleSiteDragEnd = () => {
        setDraggedSiteId(null)
        setDragOverSiteId(null)
        setIsDragOverTrash(false)
    }

    // Dragging a single LOGGER (device). stopPropagation so the parent site's
    // drag doesn't also fire, and clear any site drag so the trash deletes only
    // the logger.
    const handleDeviceDragStart = (e, deviceId) => {
        e.stopPropagation()
        setDraggedDeviceId(deviceId)
        setDraggedSiteId(null)
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', `device-${deviceId}`)
    }
    const handleDeviceDragEnd = (e) => {
        e.stopPropagation()
        setDraggedDeviceId(null)
        setIsDragOverTrash(false)
    }

    const handleTrashDragOver = (e) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        setIsDragOverTrash(true)
    }

    const handleTrashDragLeave = () => {
        setIsDragOverTrash(false)
    }

    const handleTrashDrop = (e) => {
        e.preventDefault()
        setIsDragOverTrash(false)
        // A logger was dragged → delete just that logger (not its site).
        if (draggedDeviceId) {
            let dev = null
            for (const s of projectSites) {
                dev = (s.devices || []).find(d => d.id === draggedDeviceId)
                if (dev) break
            }
            setDeviceToDelete({ id: draggedDeviceId, name: dev?.name || 'this logger' })
            setDeviceDeleteError('')
            setShowDeleteDeviceConfirm(true)
            setDraggedDeviceId(null)
            return
        }
        if (!draggedSiteId) return
        const site = projectSites.find(s => s.id === draggedSiteId)
        setSiteToDelete({ id: draggedSiteId, name: site?.name || 'this site' })
        setShowDeleteSiteConfirm(true)
        setDraggedSiteId(null)
    }

    const confirmSiteMove = () => {
        if (!pendingSiteMove) return
        const { fromId, toId } = pendingSiteMove
        const sites = [...projectSites]
        const fromIdx = sites.findIndex(s => s.id === fromId)
        const toIdx = sites.findIndex(s => s.id === toId)
        if (fromIdx === -1 || toIdx === -1) { setShowMoveSiteConfirm(false); return }
        const [moved] = sites.splice(fromIdx, 1)
        sites.splice(toIdx, 0, moved)
        setProjectSites(sites)
        setShowMoveSiteConfirm(false)
        setPendingSiteMove(null)
    }

    const confirmSiteDelete = async () => {
        if (!siteToDelete) return
        setIsDeletingSite(true)
        setSiteDeleteError('')
        try {
            const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
            const token = (localStorage.getItem(import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token') || sessionStorage.getItem(import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'))
            const response = await fetch(`${API_URL}/api/project/site/${siteToDelete.id}/`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            })
            const data = await response.json().catch(() => ({}))
            if (!response.ok) {
                throw new Error(data?.detail || data?.message || 'Failed to delete site')
            }
            // Success — remove from list, close modal, and confirm with a toast.
            const deletedName = siteToDelete.name
            setProjectSites(prev => prev.filter(s => s.id !== siteToDelete.id))
            setShowDeleteSiteConfirm(false)
            setSiteToDelete(null)
            toast.success(data?.detail || `Site "${deletedName}" deleted successfully.`)
        } catch (err) {
            console.error('Delete site error:', err)
            setSiteDeleteError(err.message || 'Failed to delete site. Please try again.')
        } finally {
            setIsDeletingSite(false)
        }
    }

    const confirmDeviceDelete = async () => {
        if (!deviceToDelete) return
        setIsDeletingDevice(true)
        setDeviceDeleteError('')
        try {
            const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
            const token = (localStorage.getItem(import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token') || sessionStorage.getItem(import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'))
            const response = await fetch(`${API_URL}/api/project/${currentProject?.id}/device/${deviceToDelete.id}/delete/`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            })
            const data = await response.json().catch(() => ({}))
            if (!response.ok || data?.success === false) {
                throw new Error(data?.error || data?.detail || 'Failed to remove logger')
            }
            // Success — drop the device from its site view, close modal, confirm.
            const removedName = deviceToDelete.name
            setProjectSites(prev => prev.map(s => ({
                ...s, devices: (s.devices || []).filter(d => d.id !== deviceToDelete.id),
            })))
            setShowDeleteDeviceConfirm(false)
            setDeviceToDelete(null)
            toast.success(data?.message || `Logger "${removedName}" removed from this project.`)
        } catch (err) {
            console.error('Remove logger error:', err)
            setDeviceDeleteError(err.message || 'Failed to remove logger. Please try again.')
        } finally {
            setIsDeletingDevice(false)
        }
    }

    // Stop play mode helper (used by manual navigation too). Also restores
    // the sidebar — Play collapses it; Stop / Prev / Next put it back.
    const stopPlay = () => {
        if (playIntervalRef.current) {
            clearInterval(playIntervalRef.current)
            playIntervalRef.current = null
        }
        if (isPlaying) {
            setIsSidebarVisible(sidebarBeforePlayRef.current)
        }
        setIsPlaying(false)
    }

    const handlePrevious = () => {
        stopPlay() // manual navigation cancels auto-play
        if (currentMenuIndex === dashboardCyclableItems) {
            setCurrentMenuIndex(dashboardCyclableItems - 1)
        } else {
            setCurrentMenuIndex(prev =>
                prev === 0 ? dashboardCyclableItems - 1 : prev - 1
            )
        }
    }

    const handleNext = () => {
        stopPlay() // manual navigation cancels auto-play
        setCurrentMenuIndex(prev =>
            prev >= dashboardCyclableItems - 1 ? 0 : prev + 1
        )
    }

    // Build a shuffled copy if random order is on. Sets persist for the
    // duration of the run — we don't re-shuffle every cycle, which would be
    // disorienting. Plain function (no deps) — safe to call anywhere.
    const buildPlaybackOrder = (rotation, random) => {
        if (!random || rotation.length <= 1) return rotation.slice()
        const out = rotation.slice()
        for (let i = out.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1))
            ;[out[i], out[j]] = [out[j], out[i]]
        }
        return out
    }

    // Play: cycle through dashboards flagged for presentation, at the cadence
    // configured in Presentation Settings. Clicking Play again (or Stop)
    // stops the cycle. Manual navigation (Previous/Next) also stops it via
    // the existing stopPlay() helper.
    const handlePlay = () => {
        if (isPlaying) {
            clearInterval(playIntervalRef.current)
            playIntervalRef.current = null
            setIsPlaying(false)
            // Restore sidebar to whatever it was before Play.
            setIsSidebarVisible(sidebarBeforePlayRef.current)
            return
        }

        if (presentationRotation.length === 0) {
            window.alert(
                'No dashboards are selected for presentation.\n\nClick "Presentation" to choose which dashboards to include.'
            )
            return
        }

        // Snapshot sidebar state so Stop restores it.
        sidebarBeforePlayRef.current = isSidebarVisible
        setIsSidebarVisible(false)

        const order = buildPlaybackOrder(presentationRotation, presentationConfig.randomOrder)
        let pos = 0
        setCurrentMenuIndex(order[pos])
        setIsPlaying(true)

        // If only one dashboard is flagged, there's nothing to cycle — just
        // show it and leave isPlaying=true so the Stop button stays available.
        if (order.length > 1) {
            playIntervalRef.current = setInterval(() => {
                pos = (pos + 1) % order.length
                setCurrentMenuIndex(order[pos])
            }, presentationConfig.pauseSeconds * 1000)
        }
    }

    // Leaving Edit Mode: save any dashboard layout changes, then drop the
    // flag. Shared by the toolbar's "End Edit Mode" button and the Settings
    // menu item, which is the only way out when Edit Mode was entered from a
    // Settings or Live page (that toolbar isn't on screen there).
    // gridRef is null off the Dashboard tab — optional chaining makes that a
    // no-op rather than an error.
    const endEditMode = async () => {
        setIsSavingCustomization(true)
        try { await gridRef.current?.save() } catch { /* keep going: never trap the user in Edit Mode */ }
        setIsSavingCustomization(false)
        setIsCustomizingDashboard(false)
    }

    const selectMenuItem = (index) => {
        const menuItem = dashboardMenuItems[index]
        
        // Handle special menu items
        if (menuItem === 'Config Dashboards' || menuItem === 'Configure Dashboards') {
            setShowConfigureDashboardsModal(true)
            return
        }
        
        // Find the template with this name
        const template = templates.find(t => t.name === menuItem)
        if (template) {
            // Store selected template for creating a dashboard
            setSelectedTemplate(template)
            console.log('[Dashboard] Selected template:', template)
        }
        
        setCurrentMenuIndex(index)
        setActiveTab('Dashboard')
    }

    // Create dashboard from template with default name
    const createDashboardFromTemplate = async (template, isBlank = false) => {
        if (!currentProject) {
            setDashboardError('Please select a project first')
            return
        }

        setIsCreatingDashboard(true)
        setDashboardError('')

        try {
            // Generate dashboard name
            let dashboardName
            if (isBlank) {
                // For blank template, use "New Dashboard" with timestamp
                const timestamp = new Date().toLocaleTimeString()
                dashboardName = `New Dashboard (${timestamp})`
            } else {
                // For API templates, use template name
                dashboardName = template.name || 'New Dashboard'
            }

            console.log('[Create Dashboard] Auto-creating dashboard:', {
                name: dashboardName,
                template: template.id || 'blank',
                isBlank: isBlank
            })

            // Call backend API to create dashboard
            const response = await projectsApi.createDashboard(currentProject.id, {
                name: dashboardName,
                template: template.id || 'blank',
                description: template.description || `Created from ${template.name || 'Blank'} Template`
            })

            console.log('[Create Dashboard] Dashboard created successfully:', response)

            // Add the new dashboard to the list
            const newDashboard = response.dashboard || response
            setDashboards([...dashboards, newDashboard])

            // Show success message
            setDashboardSuccess(`Dashboard "${dashboardName}" created successfully!`)

            // Close modal and reset state
            setTimeout(() => {
                setShowAddDashboardModal(false)
                setShowDashboardDetailsModal(false)
                setSelectedTemplateForDetails(null)
                setDashboardName('')
                setDashboardDetails('')
                setDashboardSuccess('')
            }, 1500)
        } catch (error) {
            console.error('[Create Dashboard] Error:', error)
            setDashboardError(error.response?.data?.message || error.message || 'Error creating dashboard')
        } finally {
            setIsCreatingDashboard(false)
        }
    }

    // Dashboard archive/reactivate/delete handlers with backend persistence
    const handleArchiveDashboard = async (dashboardId) => {
        const dashboard = dashboards.find(d => d.id === dashboardId)
        if (dashboard) {
            try {
                // Call backend API to archive dashboard
                await projectsApi.archiveDashboard(dashboardId)
                // Update frontend state only after successful backend call
                setDashboards(dashboards.filter(d => d.id !== dashboardId))
                setArchivedDashboards([...archivedDashboards, dashboard])
                setDashboardSuccess(`Dashboard "${dashboard.name}" archived successfully!`)
                setTimeout(() => setDashboardSuccess(''), 1500)
            } catch (error) {
                console.error('Error archiving dashboard:', error)
                setDashboardError(`Failed to archive dashboard: ${error.response?.data?.detail || error.message}`)
                setTimeout(() => setDashboardError(''), 1500)
            }
        }
    }

    const handleReactivateDashboard = async (dashboardId) => {
        const dashboard = archivedDashboards.find(d => d.id === dashboardId)
        if (dashboard) {
            try {
                // Call backend API to reactivate dashboard
                await projectsApi.reactivateDashboard(dashboardId)
                // Update frontend state only after successful backend call
                setArchivedDashboards(archivedDashboards.filter(d => d.id !== dashboardId))
                setDashboards([...dashboards, dashboard])
                setDashboardSuccess(`Dashboard "${dashboard.name}" reactivated successfully!`)
                setTimeout(() => setDashboardSuccess(''), 1500)
            } catch (error) {
                console.error('Error reactivating dashboard:', error)
                setDashboardError(`Failed to reactivate dashboard: ${error.response?.data?.detail || error.message}`)
                setTimeout(() => setDashboardError(''), 1500)
            }
        }
    }

    const handlePermanentlyDeleteDashboard = async (dashboardId) => {
        const dashboard = archivedDashboards.find(d => d.id === dashboardId)
        if (dashboard) {
            try {
                // Call backend API to delete dashboard permanently
                await projectsApi.deleteDashboard(dashboardId)
                // Update frontend state only after successful backend call
                setArchivedDashboards(archivedDashboards.filter(d => d.id !== dashboardId))
                setDashboardSuccess(`Dashboard "${dashboard.name}" permanently deleted!`)
                setTimeout(() => setDashboardSuccess(''), 1500)
            } catch (error) {
                console.error('Error deleting dashboard:', error)
                setDashboardError(`Failed to delete dashboard: ${error.response?.data?.detail || error.message}`)
                setTimeout(() => setDashboardError(''), 1500)
            }
        }
    }

    // Fetch photos for a specific album and store in albumPhotos state
    const fetchAlbumPhotos = async (albumId) => {
        try {
            const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
            const token = (localStorage.getItem(JWT_STORAGE_KEY) || sessionStorage.getItem(JWT_STORAGE_KEY))
            const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'
            // Both photos and videos linked to this album (e.g. an EWC camera
            // album holds hourly clips and/or stills).
            const [res, vres] = await Promise.all([
                fetch(`${API_URL}/api/photo/photo/?album=${albumId}`, { headers: { 'Authorization': `Bearer ${token}` } }),
                fetch(`${API_URL}/api/photo/video/?album=${albumId}`, { headers: { 'Authorization': `Bearer ${token}` } }),
            ])
            if (!res.ok) return
            const data = await res.json()
            const photos = (data.results || data || []).map(mapApiPhoto)
            let videos = []
            if (vres.ok) {
                const vdata = await vres.json()
                videos = (vdata.results || vdata || []).map(mapApiVideo)
            }
            const merged = [...photos, ...videos].sort(
                (a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0)
            )
            setAlbumPhotos(prev => ({ ...prev, [albumId]: merged }))
        } catch (e) {
            console.error('[Gallery] Error fetching album photos:', e)
        }
    }

    // Handle gallery tab click — sync URL param and fetch album photos on demand
    const handleGalleryTabClick = (tab) => {
        setGalleryTab(tab)
        setGalleryPage(1)
        // Clear field filters — a filter carried over could blank out the new tab.
        setGalleryFilters({ title: '', description: '', tags: '', location: '', dateFrom: '', dateTo: '' })
        setOpenGalleryFilterField(null)
        // Sync to URL
        const params = new URLSearchParams(location.search)
        // Switching tab always lands on that tab's grid, so drop any open photo
        // — leaving `photo` behind would have the URL→state effect immediately
        // reopen the previous photo on top of the new tab.
        params.delete('photo')
        if (tab === 'All Photos') {
            params.delete('gallery_tab')
        } else {
            params.set('gallery_tab', tab)
        }
        const newSearch = params.toString()
        navigate(`${location.pathname}${newSearch ? '?' + newSearch : ''}`, { replace: true })
        // Fetch album photos if this is an API album tab
        const album = projectAlbums.find(a => a.name === tab)
        if (album && !albumPhotos[album.id]?.length) {
            fetchAlbumPhotos(album.id)
        }
    }

    // Handle album creation
    const handleCreateAlbum = async () => {
        if (!albumName.trim()) {
            setAlbumError('Album name is required')
            return
        }
        const _albumSecErr = scanInput(albumName)
        if (_albumSecErr) { setAlbumError(_albumSecErr); return }

        if (albumImages.length === 0) {
            setAlbumError('Please upload at least one image')
            return
        }

        setIsCreatingAlbum(true)
        setAlbumError('')
        setAlbumSuccess('')

        try {
            const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
            const token = (localStorage.getItem(JWT_STORAGE_KEY) || sessionStorage.getItem(JWT_STORAGE_KEY))
            const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'

            // Create FormData for multipart upload
            const formData = new FormData()
            formData.append('name', albumName)
            formData.append('project', currentProject.id)
            formData.append('public', 1)

            // Add images with keys file_0, file_1, …
            albumImages.forEach((file, index) => {
                formData.append(`file_${index}`, file)
            })

            // POST to the combined upload endpoint
            const albumRes = await fetch(`${API_URL}/api/photo/album/upload/`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            })

            if (!albumRes.ok) {
                const errData = await albumRes.json().catch(() => ({}))
                throw new Error(errData.detail || 'Failed to create album')
            }

            const result = await albumRes.json()
            console.log('[Create Album] Created:', result)

            const newAlbum = {
                id: result.album.id,
                name: result.album.name,
                project: result.album.project_id,
                public: result.album.public,
            }

            // Add new album to list and store its photos
            setProjectAlbums(prev => [...prev, newAlbum])
            if (result.photos?.length > 0) {
                // Same mapping as every other gallery fetch. This block used to
                // hand-roll photo objects with blank size / total size /
                // dimensions / uploaded-by, so a freshly created album showed an
                // empty info panel until a refetch happened to occur.
                const mappedPhotos = result.photos.map(mapApiPhoto)
                setAlbumPhotos(prev => ({ ...prev, [newAlbum.id]: mappedPhotos }))
            }

            setAlbumSuccess('Album created successfully!')
            setTimeout(() => {
                setShowCreateAlbumModal(false)
                setAlbumName('')
                setAlbumImages([])
                setAlbumError('')
                setAlbumSuccess('')
                setGalleryTab(newAlbum.name)
                setGalleryPage(1)
            }, 1000)
        } catch (error) {
            console.error('[Create Album] Error:', error)
            setAlbumError(error.message || 'Failed to create album')
        } finally {
            setIsCreatingAlbum(false)
        }
    }

    // Handle album image upload
    const handleAlbumImageUpload = (e) => {
        const files = Array.from(e.target.files || [])
        setAlbumImages(prev => [...prev, ...files])
        setAlbumError('')
    }

    // Remove image from album
    const removeAlbumImage = (index) => {
        setAlbumImages(prev => prev.filter((_, i) => i !== index))
    }

    // Use dynamic projectSites instead of hardcoded sites
    // (nowLabel + customerTz are declared near the top of the component, above
    //  the early `return <NoActiveProject />` paths — see the hooks block there.)

    // ── Upload to Gallery handlers ────────────────────────────────────────
    const handleUploadFilesSelect = (files) => {
        const newItems = Array.from(files).map(file => ({
            id: Math.random().toString(36).slice(2),
            file,
            previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
            album_id: '',
            // Album, Location and Title are all REQUIRED before an item can be
            // uploaded (see uploadItemMissing). Title starts blank so the user
            // consciously enters one rather than shipping the raw file name.
            title: '',
            tags: '',
            location: '',
            description: '',
            uploading: false,
            done: false,
            error: ''
        }))
        setUploadItems(prev => [...prev, ...newItems])
    }

    const uploadSingleItem = async (item) => {
        const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
        const token = (localStorage.getItem(JWT_STORAGE_KEY) || sessionStorage.getItem(JWT_STORAGE_KEY))
        const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'

        let resolvedAlbumId = item.album_id
        // If album value is a name string (not a numeric DB id), find or create the album
        if (item.album_id && isNaN(parseInt(item.album_id, 10))) {
            const albumName = item.album_id
            const existing = projectAlbums.find(a => a.name === albumName)
            if (existing) {
                resolvedAlbumId = existing.id
            } else {
                const albumRes = await fetch(`${API_URL}/api/photo/album/`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ name: albumName, project: currentProject.id, public: 1 })
                })
                if (albumRes.ok) {
                    const albumData = await albumRes.json()
                    resolvedAlbumId = albumData.id
                    setProjectAlbums(prev => [...prev, albumData])
                } else {
                    resolvedAlbumId = ''
                }
            }
        }

        const formData = new FormData()
        formData.append('project', currentProject.id)
        formData.append('file_0', item.file)
        formData.append('title_0', item.title)
        formData.append('description_0', item.description)
        formData.append('tags_0', item.tags)
        if (resolvedAlbumId) formData.append('album_id_0', resolvedAlbumId)
        // item.location holds the selected site's id (see the upload form).
        if (item.location) formData.append('site_id_0', item.location)
        const res = await fetch(`${API_URL}/api/photo/photo/upload/`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
        })
        if (!res.ok) throw new Error('Upload failed')
        return res.json()
    }

    // Album, Location and Title are all required before a photo can be uploaded.
    // Returns the list of missing field labels ([] when the item is ready).
    const uploadItemMissing = (item) => {
        const missing = []
        if (!item.album_id) missing.push('Album')
        if (!item.location) missing.push('Location')
        if (!(item.title || '').trim()) missing.push('Title')
        return missing
    }

    const handleUploadItem = async (itemId) => {
        const item = uploadItems.find(i => i.id === itemId)
        if (!item) return
        const missing = uploadItemMissing(item)
        if (missing.length) {
            setUploadItems(prev => prev.map(i => i.id === itemId
                ? { ...i, error: `Please select ${missing.join(', ')} before uploading.` } : i))
            return
        }
        setUploadItems(prev => prev.map(i => i.id === itemId ? { ...i, uploading: true, error: '' } : i))
        try {
            await uploadSingleItem(item)
            setUploadItems(prev => prev.map(i => i.id === itemId ? { ...i, uploading: false, done: true } : i))
        } catch (e) {
            setUploadItems(prev => prev.map(i => i.id === itemId ? { ...i, uploading: false, error: e.message } : i))
        }
    }

    const handleStartUpload = async () => {
        const pending = uploadItems.filter(i => !i.done)
        // Block the whole batch if any pending item is missing a required field —
        // flag each so the user sees exactly what to fill in.
        if (pending.some(i => uploadItemMissing(i).length)) {
            setUploadItems(prev => prev.map(i => {
                const missing = uploadItemMissing(i)
                return (!i.done && missing.length)
                    ? { ...i, error: `Please select ${missing.join(', ')} before uploading.` } : i
            }))
            return
        }
        for (const item of pending) {
            await handleUploadItem(item.id)
        }
        // Refresh album photos if on an album tab
        setTimeout(() => {
            setShowUploadModal(false)
            setUploadItems([])
            // Refresh gallery
            setApiPhotos([])
            if (galleryTab !== 'All Photos') {
                const album = projectAlbums.find(a => a.name === galleryTab)
                if (album) {
                    setAlbumPhotos(prev => ({ ...prev, [album.id]: [] }))
                    fetchAlbumPhotos(album.id)
                }
            }
        }, 800)
    }
    // ── End Upload to Gallery handlers ───────────────────────────────────

    // ── Album edit / delete ───────────────────────────────────────────────
    const handleEditAlbumSave = async () => {
        if (!editAlbumModal || !editAlbumName.trim()) return
        const secErr = scanInput(editAlbumName)
        if (secErr) { alert(secErr); return }
        const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
        const token = (localStorage.getItem(JWT_STORAGE_KEY) || sessionStorage.getItem(JWT_STORAGE_KEY))
        const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'
        try {
            const res = await fetch(`${API_URL}/api/photo/album/${editAlbumModal.id}/`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: editAlbumName.trim() })
            })
            if (!res.ok) throw new Error('Failed to rename album')
            setProjectAlbums(prev => prev.map(a => a.id === editAlbumModal.id ? { ...a, name: editAlbumName.trim() } : a))
            if (galleryTab === editAlbumModal.name) {
                setGalleryTab(editAlbumName.trim())
            }
            setEditAlbumModal(null)
        } catch (e) {
            alert(e.message)
        }
    }

    const handleDeleteAlbum = async (albumId, albumName) => {
        if (!window.confirm(`Delete album "${albumName}"? The album will be removed but photos will remain in All Photos.`)) return
        const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
        const token = (localStorage.getItem(JWT_STORAGE_KEY) || sessionStorage.getItem(JWT_STORAGE_KEY))
        const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'
        try {
            const res = await fetch(`${API_URL}/api/photo/album/${albumId}/`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            })
            if (!res.ok) throw new Error('Failed to delete album')
            setProjectAlbums(prev => prev.filter(a => a.id !== albumId))
            if (galleryTab === albumName) {
                setGalleryTab('All Photos')
                navigate(location.pathname, { replace: true })
            }
        } catch (e) {
            alert(e.message)
        }
    }
    // ── End Album edit / delete ───────────────────────────────────────────

    // ── Photo save / delete ───────────────────────────────────────────────
    const handleSavePhotoInfo = async () => {
        if (!editingPhoto) return
        const secErr = scanInput(editFormData.title) || scanInput(editFormData.description) || scanInput(editFormData.tags)
        if (secErr) { alert(secErr); return }
        const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
        const token = (localStorage.getItem(JWT_STORAGE_KEY) || sessionStorage.getItem(JWT_STORAGE_KEY))
        const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'

        let newSrc = editingPhoto.src

        if (editingPhoto._fromApi) {
            try {
                // If a new image file was chosen, upload it as a replacement
                if (editPhotoFile) {
                    const fd = new FormData()
                    fd.append('project', currentProject.id)
                    fd.append('file_0', editPhotoFile)
                    fd.append('title_0', editFormData.title)
                    fd.append('description_0', editFormData.description)
                    const uploadRes = await fetch(`${API_URL}/api/photo/photo/upload/`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` },
                        body: fd
                    })
                    if (uploadRes.ok) {
                        const uploadData = await uploadRes.json()
                        const newPhoto = uploadData.photos?.[0]
                        if (newPhoto?.photo_url) newSrc = newPhoto.photo_url
                        // Delete old photo from backend
                        await fetch(`${API_URL}/api/photo/photo/${editingPhoto.id}/`, {
                            method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
                        })
                    }
                } else {
                    // Update metadata (title + notes + site). `site` is the FK
                    // the info panel's Location row and map read from.
                    await fetch(`${API_URL}/api/photo/photo/${editingPhoto.id}/`, {
                        method: 'PATCH',
                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            name: editFormData.title,
                            notes: editFormData.description,
                            site: editFormData.siteId ? Number(editFormData.siteId) : null,
                        })
                    })
                }

                // Persist tags if they changed. Without this the edited tags
                // lived in local state only, so the Tags filter — which matches
                // against freshly fetched photos — never found them, and they
                // disappeared on reload.
                const nextTags = (editFormData.tags || '').trim()
                const prevTags = (editingPhoto.tags || '').trim()
                if (nextTags !== prevTags) {
                    await fetch(`${API_URL}/api/photo/photo/${editingPhoto.id}/tags/`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ tags: nextTags })
                    })
                }

                // Update album assignment if album changed
                const selectedAlbumObj = projectAlbums.find(a => a.name === editFormData.album)
                const prevAlbumObj = projectAlbums.find(a => a.name === editingPhoto.album)
                if (editFormData.album !== (editingPhoto.album || '')) {
                    await fetch(`${API_URL}/api/photo/photo/${editingPhoto.id}/assign-album/`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ album_id: selectedAlbumObj ? selectedAlbumObj.id : null })
                    })
                    // Invalidate cached album photos so they refresh on next tab click
                    if (prevAlbumObj) {
                        setAlbumPhotos(prev => { const n = { ...prev }; delete n[prevAlbumObj.id]; return n })
                    }
                    if (selectedAlbumObj) {
                        setAlbumPhotos(prev => { const n = { ...prev }; delete n[selectedAlbumObj.id]; return n })
                    }
                }
            } catch (e) {
                console.error('Failed to save photo info:', e)
            }
        }

        // Reflect a changed site locally so the Location row and its map update
        // without a refetch.
        const chosenSite = projectSites.find(s => String(s.id) === String(editFormData.siteId))
        const updated = {
            ...editingPhoto,
            ...editFormData,
            src: newSrc,
            largeSrc: newSrc,
            location: chosenSite ? `Site - ${chosenSite.name}` : '',
            latitude: chosenSite ? chosenSite.latitude : null,
            longitude: chosenSite ? chosenSite.longitude : null,
            siteId: chosenSite ? chosenSite.id : null,
        }
        setPhotoEditsMap(prev => ({ ...prev, [editingPhoto.id]: updated }))
        setSelectedPhoto(updated)
        setEditPhotoFile(null)
        setEditPhotoPreview(null)
        setShowEditMediaModal(false)
    }

    const handleDeletePhoto = async (photo) => {
        if (!window.confirm(`Delete "${photo.title}"? This cannot be undone.`)) return
        if (photo._fromApi) {
            const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
            const token = (localStorage.getItem(JWT_STORAGE_KEY) || sessionStorage.getItem(JWT_STORAGE_KEY))
            const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'
            try {
                await fetch(`${API_URL}/api/photo/photo/${photo.id}/`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            } catch (e) {
                console.error('Failed to delete photo:', e)
            }
        }
        setApiPhotos(prev => prev.filter(p => p.id !== photo.id))
        setAlbumPhotos(prev => {
            const next = { ...prev }
            Object.keys(next).forEach(k => { next[k] = next[k].filter(p => p.id !== photo.id) })
            return next
        })
        setPhotoEditsMap(prev => { const n = { ...prev }; delete n[photo.id]; return n })
        setSelectedPhoto(null)
        // The photo no longer exists — don't leave its id in the URL.
        clearPhotoParam()
    }
    // ── End Photo save / delete ───────────────────────────────────────────

    // Create Site form validation (computed fresh every render)
    const _siteNameVal = newSiteName.trim()
    const _siteNamePattern = /^[A-Za-z][A-Za-z0-9 \-_]*$/
    const _siteNameInvalid = _siteNameVal !== '' && !_siteNamePattern.test(_siteNameVal)
    const _siteNameFieldError = _siteNameInvalid ? 'Must begin with a letter; may contain letters, digits, spaces, dashes, and underscores.' : ''
    const _siteLatVal = newSiteLatitude.trim()
    const _siteLonVal = newSiteLongitude.trim()
    const _siteLatError = _siteLatVal !== '' && isNaN(parseFloat(_siteLatVal)) ? 'Enter a valid decimal number, e.g. 39.782875' : ''
    const _siteLonError = _siteLonVal !== '' && isNaN(parseFloat(_siteLonVal)) ? 'Enter a valid decimal number, e.g. -83.993666' : ''
    const _isSiteFormValid = _siteNameVal !== '' && !_siteNameInvalid && !_siteLatError && !_siteLonError

    // Only the project Owner and Admins may edit dashboard components. The
    // backend enforces this on save; here we hide the edit affordances so
    // Collaborators get a clean read-only dashboard.
    const canEditDashboard = ['Owner', 'Admin'].includes(currentProject?.user_role)

    return (
    <DashboardContext.Provider value={{
      selectedParameters,
      // Project-scoped data exposed to widgets so they can render without
      // every render site having to plumb it through props. Used by the
      // GPS widget (Project Map dashboard) which needs both the site list
      // and the active project id to render markers + persist per-project
      // map config.
      sites: projectSites,
      projectId: currentProject?.id,
      dateRange: dateRangeGraph,
      activePeriod,
      handlePeriodClick,
      setDateRange: setDateRangeGraph,
      // Bumped on every refresh (manual + auto). Widgets add it to their fetch
      // deps to re-pull their latest value in place, no remount required.
      dataRefreshNonce,
      isCustomizing: isCustomizingDashboard,
      // Only Owner/Admin may edit dashboard components. Collaborators are
      // read-only — widgets hide their edit affordances when this is false,
      // and the backend rejects their saves regardless.
      canEdit: canEditDashboard,
      // Incremented on every Parameter Configuration save — see the state
      // declaration above for why widgets need to watch this.
      paramConfigVersion,
      // Widgets call this to deep-link into the Gallery tab. Optional
      // `albumName` lands the user inside that album's sub-tab (galleryTab)
      // rather than the default "All Photos" view — used by the Gallery
      // widget's "View All" so the user lands directly on the album the
      // widget is currently displaying.
      goToGallery: (albumName) => {
        setActiveTab('Gallery')
        setGalleryTab(albumName || 'All Photos')
        setGalleryPage(1)
        setSelectedPhoto(null)
        navigate(location.pathname, { replace: true })
      },
    }}>
        <div
          className={`responsive-h flex flex-col bg-gray-100 w-full ${
            // Dashboard scrolls as a normal document (browser scrollbar) so the
            // content has no inner scrollbar; every other tab keeps the
            // fixed-viewport shell with its own internal scroll regions.
            activeTab === 'Dashboard' ? 'min-h-screen' : 'h-screen overflow-hidden'
          }`}
          style={{ '--dash-header-h': `${dashHeaderH}px` }}
        >
      {/* White header — the frame, spacing and styling live in the shared
          AppHeaderShell (components/AppHeaderShell.jsx) so this page and every
          other logged-in page render an identical strip and cannot drift apart
          again. Behaviour is unchanged: the top-level tab button toggles its own
          dropdown, and the dropdown items drive this page's in-place state
          rather than navigating away.
          On Dashboard the page is document-scrolled, so the header is made
          sticky (and measured) to stay pinned while the content scrolls. */}
      <div ref={dashHeaderRef} className={activeTab === 'Dashboard' ? 'sticky top-0 z-[60]' : ''}>
      <AppHeaderShell
        activeTab={activeTab}
        openTab={hoveredTab}
        nowLabel={nowLabel}
        // Countdown to the next auto-refresh, shown under the "As of" time on ALL
        // tabs (the auto-refresh timer runs globally, not just on Dashboard) so the
        // user always sees when the next refresh will fire.
        nextRefreshLabel={nextRefreshLabel}
        // Green Refresh: on Dashboard, refresh the current slide's data in place
        // — advance the rolling window to "now" AND remount the slide so widgets
        // re-fetch (showing data that arrived since load) — keeping the user on
        // the SAME slide instead of a full reload. Other tabs keep plain reload.
        onRefresh={() => {
          if (activeTab === 'Dashboard') refreshDashboardRef.current()
          else window.location.reload()
        }}
        // Top-left logo = the current project's Project Logo (Settings > Project),
        // falling back to the generic WQData LIVE logo when the project has none.
        logoSrc={currentProject?.logo || '/images/logo.png'}
        onTabEnter={(tab) => openHeaderMenu(tab)}
        onTabLeave={(tab) => closeHeaderMenu(tab)}
        onTabClick={(tab) => {
          // The top-level tab button is purely a dropdown toggle
          // for menus that have a submenu (Dashboard / Data
          // Analytics / Report / Settings / Live) — clicking it
          // must NOT switch the active tab; only a submenu item
          // (or the no-submenu Gallery tab) does that.
          const hasSubmenu = ['Dashboard', 'Data Analytics', 'Report', 'Settings', 'Live'].includes(tab)
          if (hasSubmenu) {
            if (headerCloseTimer.current) { clearTimeout(headerCloseTimer.current); headerCloseTimer.current = null }
            setHoveredTab(prev => prev === tab ? null : tab)
            return
          }
          setActiveTab(tab)
          if (tab === 'Gallery') {
            // Always start fresh on Gallery — reset to All Photos
            setGalleryTab('All Photos')
            setGalleryPage(1)
            setSelectedPhoto(null)
            navigate(location.pathname, { replace: true })
          }
        }}
        tabButtonProps={(tab) => ({
          'data-tab-name': tab,
          onFocus: () => { if (tab === 'Live') setLiveMenuFocused(true) },
          onKeyDown: (e) => {
            if (tab === 'Live' && (e.key === 'ArrowDown' || e.key === ' ' || e.key === 'Enter' || e.key === 'Tab')) {
              if (e.key === 'Tab') {
                e.preventDefault()
                setLiveMenuFocused(true)
                setTimeout(() => { liveMenuItemsRefs.current[0]?.focus() }, 0)
              } else {
                e.preventDefault()
                setLiveMenuFocused(true)
                setTimeout(() => { liveMenuItemsRefs.current[0]?.focus() }, 0)
              }
            }
          },
        })}
        renderDropdown={(tab) => (
          <>
            {/* Dashboard Dropdown */}
            {tab === 'Dashboard' && hoveredTab === tab && (
              <div className="absolute top-full left-[-20px] mt-0.5 bg-white border border-gray-100 rounded-lg shadow-xl z-50 min-w-[250px] py-1.5 overflow-hidden">
                {dashboardMenuItems.map((item, index) => {
                  const isConfigItem = item === 'Config Dashboards' || item === 'Configure Dashboards'
                  const isFirstConfig = isConfigItem && index > 0
                  return (
                    <div key={index}>
                      {isFirstConfig && <div className="border-t border-gray-200 my-1.5" />}
                      <button
                        onClick={() => selectMenuItem(index)}
                        className={`common-size15 w-full text-left px-6 py-2 text-sm transition ${
                          index === currentMenuIndex && !isConfigItem
                            ? 'text-[#4D4D4D] '
                            : 'text-[#4D4D4D] hover:bg-gray-50'
                        }`}
                      >
                        {item}
                      </button>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Data Analytics Dropdown */}
            {tab === 'Data Analytics' && hoveredTab === tab && (
              <div className="absolute top-full left-[-25px] mt-0.5 bg-white border border-gray-100 rounded-lg shadow-xl z-50 min-w-[250px] py-1.5">
                {dataAnalyticsMenuItems.map((item, index) => {
                  const restricted = restrictedDataAnalyticsIndexes.has(index)
                  return (
                  <button
                    key={index}
                    disabled={restricted}
                    title={restricted ? 'Not available for collaborators' : undefined}
                    onClick={restricted ? undefined : () => { setCurrentDataAnalyticsIndex(index); setActiveTab('Data Analytics') }}
                    className={`common-size15 w-full text-left px-6 py-2 text-sm transition ${
                      restricted
                        ? 'text-gray-300 cursor-not-allowed'
                        : index === currentDataAnalyticsIndex
                          ? 'text-[#4D4D4D] '
                          : 'text-[#4D4D4D] hover:bg-gray-50'
                    }`}
                  >
                    {item}
                  </button>
                  )
                })}
              </div>
            )}

            {/* Report Dropdown — dynamic: saved reports for the current
                project (per-user, server-side scoped via reportApi) on top,
                a divider, then 'View All Reports' and '+ Create Report'.
                Clicking a saved report jumps the tab to that report's
                view; action items run the matching mode change. */}
            {tab === 'Report' && hoveredTab === tab && (
              <div className="absolute top-full left-[-25px] mt-0.5 bg-white border border-gray-100 rounded-lg shadow-xl z-50 min-w-[250px] py-1.5">
                {reportMenuItems.map((item, index) => {
                  if (item.kind === 'divider') {
                    return <div key={`div-${index}`} className="border-t border-gray-200 my-1.5" />
                  }
                  const isReport = item.kind === 'report'
                  const isActive = isReport && reportMode === 'view' && reportId === item.id
                  return (
                    <button
                      key={isReport ? `r-${item.id}` : `a-${item.action}`}
                      onClick={() => {
                        setActiveTab('Report')
                        if (isReport) {
                          setReportMode('view'); setReportId(item.id)
                        } else if (item.action === 'list') {
                          // 'View All Reports' — open the modal as an
                          // overlay; leave the underlying reportMode +
                          // reportId alone so the user returns to
                          // whatever they were viewing on close.
                          setShowReportList(true)
                        } else if (item.action === 'create') {
                          setReportMode('create'); setReportId(null)
                        }
                      }}
                      className={`common-size15 w-full text-left px-6 py-2 text-sm transition ${
                        isActive ? 'text-[#4D4D4D] ' : 'text-[#4D4D4D] hover:bg-gray-50'
                      }`}
                    >
                      {item.label}
                    </button>
                  )
                })}
                {savedReports.length === 0 && (
                  <div className="px-6 py-2 text-xs text-gray-400 italic">
                    No saved reports for this project yet.
                  </div>
                )}
              </div>
            )}

            {/* Settings Dropdown */}
            {tab === 'Settings' && hoveredTab === tab && (
              <div className="absolute top-full left-[-25px] mt-0.5 bg-white border border-gray-100 rounded-lg shadow-xl z-50 min-w-[250px] py-1.5">
                {settingsMenuItems.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      // Public Portal + Embedded Data Applet open
                      // as overlay popups without navigating away
                      // from the current settings sub-page.
                      if (item === 'Public Portal') {
                        setPublicPortalOpen(true)
                        setHoveredTab(null)
                        return
                      }
                      if (item === 'Embedded Data Applet') {
                        setEmbeddedAppletOpen(true)
                        setHoveredTab(null)
                        return
                      }
                      // Edit Mode: enter dashboard customize/edit mode (layout +
                      // widgets) on the Dashboard tab rather than a settings page.
                      // "End Edit Mode" (customize toolbar) saves the changes.
                      if (item === 'Edit Mode') {
                        setHoveredTab(null)
                        // Deliberately does NOT switch tabs: entering Edit Mode
                        // must leave the user exactly where they were. It used
                        // to setActiveTab('Dashboard'), which bounced everyone
                        // back to Overview no matter which page they were on.
                        if (isCustomizingDashboard) {
                          endEditMode()
                          return
                        }
                        // Permission first, prompt second: someone without
                        // edit rights is never asked for a password they
                        // could not act on anyway.
                        if (canEditDashboard) setAdminSignInOpen(true)
                        return
                      }
                      setCurrentSettingsIndex(index)
                      setActiveTab('Settings')
                      if (item === 'Project') setProjectEditMode(false)
                    }}
                    className="common-size15 w-full text-left px-6 py-2 text-sm text-[#4D4D4D] hover:bg-gray-50 transition"
                  >
                    {/* While editing, this same row is the way out — the
                        toolbar's End Edit Mode button only exists on the
                        Dashboard tab. */}
                    {item === 'Edit Mode' && isCustomizingDashboard ? 'End Edit Mode' : item}
                  </button>
                ))}
              </div>
            )}

            {/* Live Dropdown */}
            {tab === 'Live' && (hoveredTab === tab || liveMenuFocused) && (
              <div className="absolute top-full left-[-20px] mt-0.5 bg-white border border-gray-100 rounded-lg shadow-xl z-50 min-w-[250px] py-1.5">
                {liveMenuItems.map((item, index) => (
                  <button
                    key={index}
                    ref={(el) => { if (el) liveMenuItemsRefs.current[index] = el }}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Tab') {
                        e.preventDefault()
                        if (e.shiftKey) {
                          index > 0 ? liveMenuItemsRefs.current[index - 1]?.focus() : document.querySelector('[data-tab-name="Live"]')?.focus()
                        } else {
                          index < liveMenuItems.length - 1 ? liveMenuItemsRefs.current[index + 1]?.focus() : liveMenuItemsRefs.current[0]?.focus()
                        }
                      } else if (e.key === 'Escape') {
                        e.preventDefault()
                        setLiveMenuFocused(false)
                        document.querySelector('[data-tab-name="Live"]')?.focus()
                      } else if (e.key === 'ArrowDown') {
                        e.preventDefault()
                        index < liveMenuItems.length - 1 ? liveMenuItemsRefs.current[index + 1]?.focus() : liveMenuItemsRefs.current[0]?.focus()
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault()
                        index > 0 ? liveMenuItemsRefs.current[index - 1]?.focus() : document.querySelector('[data-tab-name="Live"]')?.focus()
                      }
                    }}
                    onFocus={() => setLiveMenuFocused(true)}
                    onClick={() => { setCurrentLiveIndex(index); setActiveTab('Live'); setLiveMenuFocused(false) }}
                    className={`common-size15 w-full text-left px-6 py-2 text-sm transition ${
                      index === currentLiveIndex
                        ? 'text-[#4D4D4D] '
                        : 'text-[#4D4D4D] hover:bg-gray-50'
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      />
      </div>

      {/* Desktop: the row never scrolls as a whole — each column scrolls itself
          (left sidebar, centre content, right Comments dock), so the page shows
          no outer scrollbar. Mobile stays single-column scroll. */}
      <div className={`dash-main flex flex-col md:flex-row flex-1 min-h-0 mt-8 px-4 pl-4 md:pl-6 ${
        // Dashboard: document-scrolled, so the row must NOT clip or own a scroll —
        // content flows into the browser scroll and the sidebar is sticky
        // (md:items-start lets it be shorter than the content and pin). Other tabs
        // keep the fixed-viewport shell (row clips; columns scroll internally).
        activeTab === 'Dashboard' ? 'overflow-visible md:items-start' : 'overflow-y-auto md:overflow-hidden'
      }`}>
        {/* Sidebar hide rules.
            - Gallery: always hide.
            - Report view mode: hide (Edit/Create keep it so the user can
              change parameter selection).
            - Settings → API (index 2) and Streaming Method (index 3): hide
              so the full-width panels match the XD reference.
            Compute once so the toggle button and the sidebar container
            stay in sync. */}
        {(() => null)()}
        {/* Toggle Sidebar Button — hidden whenever the sidebar itself
            is forced off for the current tab/sub-page. */}
        {!isSidebarVisible
          && activeTab !== 'Gallery'
          && !(activeTab === 'Report' && reportMode === 'view')
          && !(activeTab === 'Settings')
          && (
          // Collapsed rail. The XD shows the closed state as a narrow WHITE
          // rounded panel running the full height of the content row, with the
          // toggle glyph at its top — the same surface as the open sidebar,
          // just narrowed. It previously rendered as a bare borderless icon
          // sitting directly on the grey page, which read as a stray button
          // rather than a collapsed panel.
          //
          // 64px wide / 16px gutter are taken from the XD frame (rail is 4.35%
          // of the viewport, gutter 1.2% — ~63px and ~17px at 1440px). Height
          // comes from the parent flex row's default `align-items: stretch`,
          // so no explicit height is needed; the old `self-start` is dropped
          // because it was what kept the control shrink-wrapped to the icon.
          <div className="hidden md:flex flex-shrink-0 w-16 mr-4 bg-white rounded-lg justify-center pt-4">
            <button
              onClick={() => setIsSidebarVisible(true)}
              className="self-start p-1 hover:bg-gray-100 rounded transition"
              title="Open Sidebar"
            >
              {/* Same panel glyph as the in-sidebar Close button, but mirrored
                  horizontally so the arrow points RIGHT (open/expand) instead of
                  LEFT (close). The close asset is the only panel icon we ship,
                  so scaleX(-1) gives us the "left_panel_open" direction without a
                  second image.
                  w-5 h-5 matches the in-sidebar toggle so the icon doesn't
                  change size between open/closed. The only shipped asset is the
                  #AFAFAF (175) glyph; brightness(0.44) darkens the pure-grey PNG
                  to #4D4D4D (77 ≈ 175×0.44) for the closed state per the design. */}
              <img
                src="/images/left_panel_close_24dp_AFAFAF_FILL1_wght400_GRAD0_opsz24.png"
                alt="Open Sidebar"
                className="w-5 h-5"
                style={{ transform: 'scaleX(-1)', filter: 'brightness(0.44)' }}
              />
            </button>
          </div>
        )}

        {/* Left Sidebar — see comment above for the full hide rule set. */}
        <div className={`ds-pg-main bg-white rounded-lg border-gray-200 overflow-y-auto transition-all duration-300 ease-in-out ${
          activeTab === 'Gallery'
            || (activeTab === 'Report' && reportMode === 'view')
            || activeTab === 'Settings'
              // Forced off for this tab: gone at every width.
              ? 'hidden md:block md:max-w-0 md:flex-0 md:opacity-0 md:pointer-events-none' :
          // Mobile: always a full-width card above the content, because the
          // 17% column squeezed "All Sites" into an unreadable strip on a
          // 390px screen. Its BODY collapses instead (see wrapper below).
          // From md up the original column rules apply untouched.
          isSidebarVisible
            ? 'w-full mb-4 opacity-100 md:w-auto md:mb-0 md:max-w-[17%] md:flex-[17%]'
            : 'w-full mb-4 opacity-100 md:w-auto md:mb-0 md:max-w-0 md:flex-0 md:opacity-0'
        } ${
          // Dashboard is document-scrolled, so the sidebar pins (sticky, below the
          // sticky header) and keeps its OWN scroll via max-height + the existing
          // overflow-y-auto — that's the "All Sites" scrollbar, while the page uses
          // the browser scrollbar. md+ only; on mobile it's a full-width card.
          activeTab === 'Dashboard'
            ? 'md:sticky md:self-start md:top-[var(--dash-header-h)] md:[max-height:calc(100vh_-_var(--dash-header-h))]'
            : ''
        }`}>
          {/* Sidebar Header */}
          <div className="p-3 md:p-5 border-gray-200 flex items-center justify-between">
            <h2 className="common-head25 text-xl font-bold text-[#4D4D4D]">All Sites</h2>
            <div className="flex items-center gap-1">
              {/* Edit-Mode only: the pencil (and the "All Sites" editing view it
                  opens) is shown ONLY when dashboard Edit Mode is on. Ending Edit
                  Mode also drops back to the normal view (see the edit-mode effect). */}
              {isCustomizingDashboard && (
                <button
                  onClick={() => {
                    setIsEditingSidebar(!isEditingSidebar)
                  }}
                  className="p-1 hover:bg-gray-100 rounded transition"
                  title="Edit Sites"
                >
                  <img src="/images/ICON_EditAllSitesBar.png" alt="Edit" className="w-5 h-5" />
                </button>
              )}
              <button
                // Toggle rather than close-only: on mobile the header stays
                // visible when collapsed, so this same control reopens it.
                // On desktop the header is only reachable while open, so
                // toggling is identical to the previous close-only behaviour.
                onClick={() => setIsSidebarVisible((v) => !v)}
                className="drop-mb p-1 hover:bg-gray-100 rounded transition"
                title={isSidebarVisible ? 'Close Sidebar' : 'Open Sidebar'}
              >
                {/* Arrow points LEFT while open (close) and RIGHT while collapsed
                    (open) — mirror the close glyph when collapsed. */}
                <img
                  src="/images/left_panel_close_24dp_AFAFAF_FILL1_wght400_GRAD0_opsz24.png"
                  alt={isSidebarVisible ? 'Close' : 'Open'}
                  className="w-5 h-5"
                  style={isSidebarVisible ? undefined : { transform: 'scaleX(-1)' }}
                />
              </button>
            </div>
          </div>
          {/* Sidebar body. On mobile the panel is always present as a
              full-width card and THIS is what collapses, so the "All Sites"
              header stays tappable (XD mobile frame). On desktop the whole
              container collapses instead, so this stays visible there. */}
          <div className={isSidebarVisible ? '' : 'hidden md:block'}>

          {/* Search Parameters */}
          <div className="px-5 py-2 border-gray-200">
            <div className="relative">
              <input
                type="text"
                placeholder="Search sites, loggers, parameters"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
              <Search className="absolute right-3 top-2.5 w-3.5 h-3.5 text-gray-400" />
            </div>
          </div>

          {/* Sidebar Menu */}
          <div className="px-5 py-2 space-y-0.5">
            {loadingSites ? (
              <div className="flex items-center justify-center py-4">
                <Loader className="w-5 h-5 text-blue-500 animate-spin" />
                <span className="ml-2 text-sm text-gray-600">Loading sites...</span>
              </div>
            ) : projectSites.length === 0 && !isEditingSidebar ? (
              // Fresh project with no sites yet: in *view* mode we show the
              // hint; in *edit* mode we still fall through so the
              // "+ Add Site" / Parameter Configuration / Link External Site
              // buttons render — otherwise there'd be no way to add the
              // first site.
              <div className="py-4 px-2 text-center text-sm text-gray-500">
                No sites available for this project
              </div>
            ) : (
              <>
                {/* Edit Mode View */}
                {isEditingSidebar ? (
                  <div className="space-y-1">
                    {projectSites.length === 0 && (
                      <div className="px-2 py-3 text-center text-xs text-gray-500">
                        No sites yet — click <span className=" text-[#358ECD]">+ Add Site</span> to create the first one.
                      </div>
                    )}
                    {getFilteredSites().map((site) => (
                      <div
                        key={site.id}
                        className={`space-y-1 rounded transition ${
                          dragOverSiteId === site.id ? 'border-2 border-blue-400 bg-blue-50' : ''
                        } ${draggedSiteId === site.id ? 'opacity-40' : ''}`}
                        draggable
                        onDragStart={(e) => handleSiteDragStart(e, site.id)}
                        onDragOver={(e) => handleSiteDragOver(e, site.id)}
                        onDrop={(e) => handleSiteDrop(e, site.id)}
                        onDragEnd={handleSiteDragEnd}
                      >
                        {/* Site Name - Edit Mode */}
                        <div className="w-full flex items-center px-2 py-1.5 hover:bg-gray-50 rounded transition">
                          <span className="text-gray-400 text-xs flex-shrink-0 mr-1.5 cursor-grab active:cursor-grabbing">☰</span>
                          <button
                            onClick={() => toggleMenu(site.id)}
                            className="flex items-center gap-1 flex-1 min-w-0 text-[#4D4D4D] common-size15"
                          >
                            <span className="flex-1 text-left  text-[#4D4D4D] text-xs truncate common-size15 font-bold">{site.name}</span>
                            {site.devices && site.devices.length > 0 && (
                              <ChevronDown
                                className={`w-3 h-3 text-gray-500 transition-transform flex-shrink-0 ${
                                  expandedMenus.includes(site.id) ? 'rotate-180' : ''
                                }`}
                              />
                            )}
                          </button>
                          {/* Edit Site — name / description / location (lat-long
                              + Use Device GPS), same fields as Create Site. */}
                          <button
                            onClick={(e) => { e.stopPropagation(); openEditSite(site) }}
                            className="ml-1 flex-shrink-0 p-1 rounded hover:bg-gray-200 transition"
                            title={`Edit ${site.name} (location & details)`}
                          >
                            <svg className="w-3 h-3 text-[#4D4D4D]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                          </button>
                          {/* Configure icon — always visible in edit mode */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedSiteForDevice(site.id)
                              setConfigureModalTab('Devices')
                              setNewDeviceName('')
                              setDeviceError('')
                              setSuccessMessage('')
                              setModalProjectSites(JSON.parse(JSON.stringify(projectSites)))
                              setSensorSelectedDevice('')
                              setSensorProfileName('Default')
                              setSensorProfiles([{ id: 1, name: 'Default' }])
                              setSelectedSensorProfile(null)
                              setShowSensorProfileForm(false)
                              setShowAddDeviceModal(true)
                            }}
                            className="ml-1 flex-shrink-0 p-1 rounded hover:bg-gray-200 transition"
                            title={`Configure ${site.name}`}
                          >
                            <Edit2 className="w-3 h-3 text-[#4D4D4D]" />
                          </button>
                        </div>

                        {/* Devices for this site - Edit Mode */}
                        {site.devices && expandedMenus.includes(site.id) && (
                          <div className="ml-2 space-y-1">
                            {site.devices.map((device) => (
                              <div
                                key={device.id}
                                draggable
                                onDragStart={(e) => handleDeviceDragStart(e, device.id)}
                                onDragEnd={handleDeviceDragEnd}
                                className={draggedDeviceId === device.id ? 'opacity-40' : ''}
                              >
                                <button
                                  onClick={() => toggleMenu(`device-${device.id}`)}
                                  className="w-full flex items-center gap-2 px-2 py-1 text-xs text-[#4D4D4D] hover:bg-gray-100 transition cursor-grab active:cursor-grabbing"
                                >
                                  <span className="text-[#4D4D4D] text-xs">☰</span>
                                  <span className="flex-1 text-left truncate font-bold text-[#4D4D4D] common-size15">{device.name}</span>
                                  {device.sensors && device.sensors.length > 0 && (
                                    <ChevronDown 
                                      className={`w-2.5 h-2.5 text-[#4D4D4D] common-size15 transition-transform flex-shrink-0 ${
                                        expandedMenus.includes(`device-${device.id}`) ? 'rotate-180' : ''
                                      }`}
                                    />
                                  )}
                                </button>

                                {/* Parameters for this device - Edit Mode */}
                                {device.sensors && expandedMenus.includes(`device-${device.id}`) && (
                                  <div className="ml-10 rounded py-0.5 mt-0.5 space-y-0">
                                    {device.sensors.map((sensor) => (
                                      <div key={sensor.id}>
                                        {sensor.parameters && sensor.parameters.length > 0 && (
                                          <>
                                            <div className="px-2 py-1 text-xs text-[#4D4D4D] common-size15 mb-2">{sensor.name}</div>
                                            {sensor.parameters.map((param) => (
                                              <div
                                                key={param.id}
                                                className="px-2 py-0.5 text-xs text-[#4D4D4D] common-size15 mb-2"
                                              >
                                                {param.name}
                                              </div>
                                            ))}
                                          </>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Trash Drop Zone */}
                    <div
                      onDragOver={handleTrashDragOver}
                      onDragLeave={handleTrashDragLeave}
                      onDrop={handleTrashDrop}
                      className={`my-3 py-2 flex items-center justify-center gap-2 rounded-lg border-2 font-bold border-dashed text-xs transition-all common-size15 ${
                        isDragOverTrash
                          ? 'border-red-400 bg-red-50 text-red-600'
                          : 'border-gray-300 text-gray-400'
                      }`}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                      </svg>
                      {isDragOverTrash ? 'Drop to delete' : 'Drag here to delete'}
                    </div>

                    {/* Add Site, Parameter Configuration, Link External Site */}
                    <div className="space-y-1 mt-1">
                    <button
                        onClick={() => setShowParamConfigModal(true)}
                        className="w-full flex items-center gap-1.5 px-2 py-1 text-xs text-[#358ECD] hover:text-blue-700 transition common-size15 font-semibold"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="3"/>
                          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                        </svg>
                        Parameter Configuration
                      </button>
                      <button
                        onClick={() => {
                          setEditingSiteId(null)   // create mode, not edit
                          setShowCreateSiteModal(true)
                          setNewSiteName('')
                          setNewSiteDescription('')
                          setNewSiteLatitude('')
                          setNewSiteLongitude('')
                          setUseDeviceGPS(false)
                        }}
                        className="w-full flex items-center gap-1.5 px-2 py-1 text-xs text-[#358ECD] hover:text-blue-700 transition common-size15 font-semibold">
                        <span className="font-bold text-sm leading-none common-size15">+</span>
                        Add Site
                      </button>
                      
                      <button className="w-full flex items-center gap-1.5 px-2 py-1 text-xs text-[#358ECD] hover:text-blue-700 transition common-size15 font-semibold">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                        </svg>
                        Link External Site
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Normal View — matches XD All Sites sidebar:
                       Site      bold black,   no checkbox            left edge 0
                       Device    semibold,     no checkbox            left edge 0, smaller
                       Sensor    regular,      checkbox + chevron     left edge 0 (checkbox at edge)
                       Parameter regular,      checkbox               indented ~20px from sensor
                  */
                  getFilteredSites().map((site) => (
                    <div key={site.id} className="space-y-1.5">
                      {/* Site — bold heading at the very left edge */}
                      <h3 className="common-size18 pt-2 font-bold text-[#4D4D4D] leading-tight">
                        {site.name}
                      </h3>

                      {/* Devices for this site — always rendered. No
                          collapse trigger on site / device per XD. */}
                      {(site.devices || []).map((device) => (
                        <div key={device.id} className="space-y-1">
                          {/* Device — bold sub-heading per XD (slightly
                              smaller than Site, but same weight + dark
                              colour). XD shows "<uid> Data Logger" — we
                              append the suffix when the device name
                              doesn't already carry it. */}
                          <h4 className="common-size15 pt-1 font-bold text-[#4D4D4D] leading-tight flex items-center gap-1.5">
                            <span className="truncate">
                              {/Data Logger$/i.test(device.name || '')
                                ? device.name
                                : `${device.name || `Device ${device.id}`} Data Logger`}
                            </span>
                            <LastContactClock
                              lastContact={device.last_contact_time}
                              status={device.contact_status}
                              tz={customerTz}
                            />
                          </h4>

                          {/* Sensors — checkbox sits at the same left edge
                              as the device heading (no ml-* on the wrapper) */}
                          {(device.sensors || []).map((sensor) => (
                            <div key={sensor.id}>
                              <button
                                onClick={() => toggleMenu(`sensor-${sensor.id}`)}
                                className="w-full flex items-center gap-2 py-1 common-size15 font-bold text-[#4D4D4D] hover:bg-gray-50 rounded transition"
                              >
                                <input
                                  type="checkbox"
                                  checked={checkedItems[sensor.id] || false}
                                  onChange={() => toggleSensorCheckbox(site, device, sensor)}
                                  className="w-4 h-4 rounded border-gray-300 cursor-pointer flex-shrink-0"
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <span className="common-size15 flex-1 font-semibold text-left truncate text-[#4D4D4D]">{sensor.name}</span>
                                {sensor.parameters && sensor.parameters.length > 0 && (
                                  <ChevronDown
                                    className={`w-3.5 h-3.5 text-gray-500 transition-transform flex-shrink-0 mr-1 ${
                                      expandedMenus.includes(`sensor-${sensor.id}`) ? 'rotate-180' : ''
                                    }`}
                                  />
                                )}
                              </button>

                              {/* Parameters — indented ONE step from the
                                  sensor's checkbox column (≈ ml-6 = 24px). */}
                              {sensor.parameters && expandedMenus.includes(`sensor-${sensor.id}`) && (
                                <div className="ml-6 space-y-0">
                                  {sensor.parameters.map((param) => (
                                    <button
                                      key={param.id}
                                      onClick={() => toggleCheckbox(param.id)}
                                      className="w-full flex items-center gap-2 py-1 common-size15 text-[#4D4D4D] hover:bg-gray-50 rounded transition"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checkedItems[param.id] || false}
                                        onChange={() => toggleCheckbox(param.id)}
                                        className="w-4 h-4 rounded border-gray-300 cursor-pointer flex-shrink-0"
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <span className="common-size15 truncate text-left flex-1 font-normal">{param.name}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </>
            )}
            {searchQuery.trim() && getFilteredSites().length === 0 && (
              <div className="py-4 px-2 text-center text-sm text-gray-500">
                No results found for "{searchQuery}"
              </div>
            )}
          </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={`flex-1 flex flex-col pl-4 resp-set ${
          // Dashboard flows into the document scroll (no clip); other tabs keep the
          // fixed-viewport clip so their inner regions own the scroll.
          activeTab === 'Dashboard' ? 'overflow-visible' : 'overflow-hidden min-h-0'
        }`}>
          {/* Project name/description header intentionally removed per the
              6/16 review — the project is already visible in the top-right
              user/project dropdown, and giving every page this header was
              taking vertical real-estate from the actual content. */}
          
          {/* Filter Bar - Only for Dashboard */}
          {activeTab === 'Dashboard' && (
            <div className="sticky top-0 z-20 border-gray-200">
              {/* Customize mode toolbar */}
              {isCustomizingDashboard ? (
                <div className="customiz-bar flex items-center gap-2 px-4 py-2.5 flex-wrap">
                  {/* Active dashboard view tab (kept visible in customize mode to match XD) */}
                  <div className="res-btn flex items-center gap-1.5 border-b-2 border-[#358ECD] pb-1.5 mr-1">
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <path d="M0 8.88892V0H8.88889V8.88892H0ZM0 20V11.1111H8.88889V20H0ZM11.1111 8.88892V0H20V8.88892H11.1111ZM11.1111 20V11.1111H20V20H11.1111Z" fill="#358ECD"/>
                    </svg>
                    <span className="text-sm font-semibold text-[#358ECD]">{dashboardMenuItems[currentMenuIndex]}</span>
                  </div>
                  <button
                    onClick={() => setShowPresentationSettings(true)}
                    title="Configure which dashboards play in Presentation mode"
                    className="res-btn flex items-center gap-1.5 border border-gray-300 text-[#4D4D4D] px-3 py-1.5 rounded-md text-sm  hover:bg-gray-50 transition"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                    Presentation
                  </button>
                  <span className="res-btn for-m-drag flex items-center gap-1.5 text-xs text-gray-400 ml-1">
                    <GripVertical className="w-3.5 h-3.5" />
                    Drag Widgets to Reorder
                  </span>
                  <div className="flex-1 m-none" />
                  <button
                    onClick={() => setShowAddWidgetsModal(true)}
                    className="res-btn flex items-center gap-1.5 border border-[#358ECD] text-[#358ECD] px-3 py-1.5 rounded-md text-sm  hover:bg-[#EBF5FB] transition"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add Widgets
                  </button>
                  <button
                    onClick={() => setShowResetDashboardModal(true)}
                    className="res-btn flex items-center gap-1.5 border border-gray-300 text-[#4D4D4D] px-3 py-1.5 rounded-md text-sm  hover:bg-gray-50 transition"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Reset Dashboard
                  </button>
                  <button
                    onClick={endEditMode}
                    disabled={isSavingCustomization}
                    className="res-btn flex items-center gap-1.5 bg-[#358ECD] text-white px-4 py-1.5 rounded-md text-sm font-semibold hover:bg-[#2d7ab5] disabled:opacity-60 transition"
                  >
                    {isSavingCustomization ? 'Saving…' : 'End Edit Mode'}
                  </button>
                  <button
                    onClick={() => setIsCustomizingDashboard(false)}
                    className="res-btn border border-gray-300 text-[#4D4D4D] px-3 py-1.5 rounded-md text-sm  hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                /* Normal view mode */
                <div className="flex items-center justify-between gap-2 flex-wrap md:flex-nowrap mb-3">
                  {/* Left: Overview tab + navigation */}
                  <div className="response-bar flex items-center gap-2 md:gap-3">
                    {/* Active dashboard name as tab */}
                    <div className="frst-tb min-w-0 md:min-w-[200px] flex items-center bg-white rounded-md gap-1.5 py-2 px-3 md:px-5 md:mr-1">
                      <span className="common-size15 text-sm font-bold text-[#4D4D4D]">{dashboardMenuItems[currentMenuIndex]}</span>
                    </div>
                    {/* Previous / Next / Play */}
                    <div className="flex items-center rounded-md divide-x divide-gray-200 bg-white shadow-sm overflow-hidden">
                      <button onClick={handlePrevious} className="common-size15 px-3 md:px-4 py-2 text-sm text-[#888888] hover:bg-gray-50 transition">Previous</button>
                      <button onClick={handleNext} className="common-size15 px-3 md:px-4 py-2 text-sm text-[#4D4D4D] hover:bg-gray-50 transition">Next</button>
                    </div>
                    <div className="flex items-center rounded-md divide-x divide-gray-200 bg-white shadow-sm overflow-hidden">
                      <button
                        onClick={handlePlay}
                        title={
                          isPlaying
                            ? 'Stop presentation'
                            : presentationRotation.length === 0
                              ? 'No dashboards selected — open Presentation to configure'
                              : `Play presentation (${presentationRotation.length} dashboard${presentationRotation.length === 1 ? '' : 's'}, ${presentationConfig.pauseSeconds}s each)`
                        }
                        className={`common-size15 play-stop flex items-center gap-1.5 px-4 py-2 text-sm transition ${
                          isPlaying
                            ? 'bg-[#358ECD] text-white hover:bg-[#2d7ab5]'
                            : 'text-[#4D4D4D] hover:bg-gray-50'
                        }`}
                      >
                        {isPlaying ? (
                          /* Pause icon */
                          <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                            <rect x="3" y="3" width="5" height="14" rx="1" fill="currentColor"/>
                            <rect x="12" y="3" width="5" height="14" rx="1" fill="currentColor"/>
                          </svg>
                        ) : (
                          /* Play icon */
                          <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                            <path d="M7.5 14.5L14.5 10L7.5 5.5V14.5ZM10 20C8.617 20 7.317 19.738 6.1 19.213C4.883 18.688 3.825 17.975 2.925 17.075C2.025 16.175 1.313 15.117 0.788 13.9C0.263 12.683 0 11.383 0 10C0 8.617 0.263 7.317 0.788 6.1C1.313 4.883 2.025 3.825 2.925 2.925C3.825 2.025 4.883 1.313 6.1 0.788C7.317 0.263 8.617 0 10 0C11.383 0 12.683 0.263 13.9 0.788C15.117 1.313 16.175 2.025 17.075 2.925C17.975 3.825 18.688 4.883 19.213 6.1C19.738 7.317 20 8.617 20 10C20 11.383 19.738 12.683 19.213 13.9C18.688 15.117 17.975 16.175 17.075 17.075C16.175 17.975 15.117 18.688 13.9 19.213C12.683 19.738 11.383 20 10 20ZM10 18C12.233 18 14.125 17.225 15.675 15.675C17.225 14.125 18 12.233 18 10C18 7.767 17.225 5.875 15.675 4.325C14.125 2.775 12.233 2 10 2C7.767 2 5.875 2.775 4.325 4.325C2.775 5.875 2 7.767 2 10C2 12.233 2.775 14.125 4.325 15.675C5.875 17.225 7.767 18 10 18Z" fill="#888888"/>
                          </svg>
                        )}
                        <span>{isPlaying ? 'Stop' : 'Play'}</span>
                      </button>
                    </div>
                  </div>

                  {/* Right: Customize — Owner/Admin only (collaborators are
                      read-only) AND only inside Edit Mode. Entering edit is now
                      done through Settings -> Edit Mode, which asks for the
                      password first, so this shortcut must not bypass it. */}
                  {canEditDashboard && isCustomizingDashboard && (
                  <button
                    onClick={() => setIsCustomizingDashboard(true)}
                    className="common-size15 flex items-center gap-2 bg-white text-[#4D4D4D] py-2 px-3 md:px-4 rounded-md text-sm hover:bg-white transition shadow-sm"
                    title="Customize"
                  >
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                      <path d="M0 8.88892V0H8.88889V8.88892H0ZM0 20V11.1111H8.88889V20H0ZM11.1111 8.88892V0H20V8.88892H11.1111ZM11.1111 20V11.1111H20V20H11.1111Z" fill="#888888"/>
                    </svg>
                    {/* Icon-only below md — the XD mobile frame shows just the
                        grid glyph, and the label alone is ~80px of a 390px row. */}
                    <span className="hidden md:inline">Customize</span>
                  </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Main Content Area */}
          <div ref={gridWrapperRefCallback} className={`custmz-crash flex-1 min-w-0 mb-4 ${activeTab === 'Data Analytics' ? 'min-h-0 overflow-y-auto overflow-x-hidden md:overflow-hidden' : activeTab === 'Dashboard' ? 'overflow-visible' : 'min-h-0 overflow-y-auto overflow-x-hidden'}`}>
            {/* Data Analytics View */}
            {activeTab === 'Data Analytics' && (
              <DataAnalyticsView
                projectId={projectId || currentProject?.id}
                currentDataAnalyticsIndex={currentDataAnalyticsIndex}
                setCurrentDataAnalyticsIndex={setCurrentDataAnalyticsIndex}
                dateRangeGraph={dateRangeGraph}
                setDateRangeGraph={setDateRangeGraph}
                activePeriod={activePeriod}
                handlePeriodClick={handlePeriodClick}
                selectedParameters={selectedParameters}
                restrictedSubTabIndexes={restrictedDataAnalyticsIndexes}
              />
            )}
            {/* Report View — driven by ReportView component.
                Modes: null (empty Report tab) | 'view' (read-only saved
                report) | 'edit' (inline editor with right-hand Settings
                sidebar) | 'create' (empty draft + editor). 'View All
                Reports' lives in its own popup (ReportListModal) so the
                user can browse the list without losing whatever report is
                currently open underneath. */}
            {activeTab === 'Report' && (
              <ReportView
                mode={reportMode}
                reportId={reportId}
                selectedParameters={selectedParameters}
                savedReports={savedReports}
                onSelectedParametersChange={(nextParams) => {
                  // When the user opens an existing report, mirror its
                  // saved parameter list into the dashboard sidebar so
                  // the checkboxes and the Settings → Data section stay
                  // in sync with the report being edited. Also flip the
                  // per-id map that drives each checkbox's checked
                  // state, otherwise the sidebar shows the params but
                  // with empty tickboxes.
                  setSelectedParameters(nextParams || [])
                  setCheckedItems(
                    Object.fromEntries((nextParams || []).map((p) => [p.id, true]))
                  )
                }}
                onCancelCreate={() => {
                  // No saved report to fall back to → return to whatever
                  // tab the user was on before they opened Create Report.
                  setReportMode(null)
                  setReportId(null)
                  const prev = previousActiveTabRef.current
                  setActiveTab(prev && prev !== 'Report' ? prev : 'Dashboard')
                }}
                onModeChange={(nextMode, nextId = null) => {
                  setReportMode(nextMode)
                  setReportId(nextId)
                }}
                onReportListChange={() => setReportsReloadTick((n) => n + 1)}
              />
            )}
            <ReportListModal
              open={showReportList}
              projectId={projectId}
              onClose={() => setShowReportList(false)}
              onOpenReport={(id) => {
                setReportMode('view'); setReportId(id)
                setActiveTab('Report')
              }}
              onReportListChange={() => setReportsReloadTick((n) => n + 1)}
            />

            {/* Project settings now render inline in the Settings tab (no popup). */}

            {/* Settings View */}
            {activeTab === 'Settings' && (
              // All Settings sub-tabs render full-width on a light-gray
              // page, with each panel as a centered white card — matches
              // the XD design across Users / Project / API / Streaming.
              <div className="w-full h-full bg-[#F1F2F4] overflow-y-auto px-0 pt-0 pb-0 md:px-6 md:pt-8 md:pb-12 flex justify-center">
                {currentSettingsIndex === 0 && (
                  <div className="ovflo-x bg-white rounded-lg shadow-sm border border-gray-200 w-full max-w-4xl h-fit">
                    <h3 className="common-size18 text-base font-bold text-[#4D4D4D] border-b py-5 px-6">Users</h3>

                    {settingsUsersError && (
                      <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                        {settingsUsersError}
                      </div>
                    )}

                    {/* Users table */}
                    <table className="w-full text-sm text-gray-700 border-b">
                      <thead>
                        <tr className="text-gray-500 text-xs font-semibold">
                          <th className="common-size15 text-left py-3 px-6 font-semibold text-[#4D4D4D]">Name</th>
                          <th className="common-size15 text-left py-3 px-6 font-semibold text-[#4D4D4D]">Email Address</th>
                          <th className="common-size15 text-left py-3 px-6 font-semibold text-[#4D4D4D]">Access</th>
                          <th className="w-20" />
                        </tr>
                      </thead>
                      <tbody>
                        {settingsUsersLoading ? (
                          <tr><td colSpan="4" className="py-6 text-center text-gray-400 text-sm">
                            <Loader className="w-4 h-4 animate-spin inline-block mr-2" />
                            Loading users…
                          </td></tr>
                        ) : settingsUsers.length === 0 ? (
                          <tr><td colSpan="4" className="py-6 text-center text-gray-400 text-sm">
                            {currentProject?.id ? 'No users yet — add one below.' : 'Open a project to manage users.'}
                          </td></tr>
                        ) : settingsUsers.map(u => (
                          <tr key={u.id} className="border-t border-gray-100">
                            <td className="common-size15 py-3 px-6 text-[#4D4D4D]">{u.name}</td>
                            <td className="common-size15 py-3 px-6 text-[#4D4D4D]">{u.email}</td>
                            <td className="common-size15 py-3 px-6 text-[#4D4D4D]">{u.access}</td>
                            <td className="common-size15 py-3 px-6 text-right">
                              {!isProjectOwner ? (
                                null /* Only the Owner sees Remove controls */
                              ) : u.access === 'Owner' ? (
                                <span className="text-gray-300 text-sm cursor-not-allowed" title="Cannot remove the project owner">Remove</span>
                              ) : (
                                <button
                                  type="button"
                                  disabled={settingsUsersBusy}
                                  onClick={async () => {
                                    if (!currentProject?.id) return
                                    if (!window.confirm(`Remove ${u.name || u.email} from this project?`)) return
                                    setSettingsUsersBusy(true)
                                    setSettingsUsersError('')
                                    try {
                                      await projectsApi.removeProjectUser(currentProject.id, u.id)
                                      setSettingsUsers(prev => prev.filter(x => x.id !== u.id))
                                    } catch (err) {
                                      setSettingsUsersError(err.response?.data?.error || err.message || 'Failed to remove user.')
                                    } finally {
                                      setSettingsUsersBusy(false)
                                    }
                                  }}
                                  className="common-size15 text-[#4D4D4D] text-sm underline-offset-2 hover:underline transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Remove
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Add new user row — Owner only, and Edit-Mode only
                        (client request: adding users is an editing action). */}
                    {isProjectOwner && isCustomizingDashboard && (
                    <div className="mt-3 py-4 border-gray-100 px-6">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-sm text-gray-700 w-20 flex-shrink-0">New User</span>
                        <input
                          type="email"
                          value={newUserEmail}
                          onChange={(e) => setNewUserEmail(e.target.value)}
                          placeholder="Email"
                          disabled={settingsUsersBusy || !currentProject?.id}
                          className="flex-1 min-w-[180px] px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#358ECD] disabled:bg-gray-50"
                        />
                        <select
                          value={newUserAccess}
                          onChange={(e) => setNewUserAccess(e.target.value)}
                          disabled={settingsUsersBusy || !currentProject?.id}
                          className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#358ECD] bg-white disabled:bg-gray-50"
                        >
                          <option value="Admin">Admin</option>
                          <option value="Collaborator">Collaborator</option>
                        </select>
                        <button
                          type="button"
                          onClick={async () => {
                            const email = newUserEmail.trim()
                            if (!email || !currentProject?.id) return
                            setSettingsUsersBusy(true)
                            setSettingsUsersError('')
                            try {
                              const res = await projectsApi.addProjectUser(currentProject.id, { email, access: newUserAccess })
                              if (res?.data) {
                                setSettingsUsers(prev => [...prev, res.data])
                              }
                              setNewUserEmail('')
                              setNewUserAccess('Admin')
                            } catch (err) {
                              setSettingsUsersError(err.response?.data?.error || err.message || 'Failed to add user.')
                            } finally {
                              setSettingsUsersBusy(false)
                            }
                          }}
                          disabled={!newUserEmail.trim() || settingsUsersBusy || !currentProject?.id}
                          className="px-5 py-1.5 border-2 border-[#358ECD] text-[#358ECD] hover:bg-[#358ECD] hover:text-white rounded text-sm font-semibold tracking-wider transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#358ECD]"
                        >
                          {settingsUsersBusy ? '…' : 'ADD'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 italic mt-2 ml-[92px]">
                        *User must have an active WQData LIVE account
                      </p>
                    </div>
                    )}
                  </div>
                )}
                {currentSettingsIndex === 1 && (
                  <div className="w-full max-w-xl h-fit">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                      {/* Header */}
                      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                        <h2 className="common-head25 text-lg font-bold text-[#4D4D4D]">Project</h2>
                        {/* Edit-Mode only (client request). */}
                        {!projectEditMode && isCustomizingDashboard && (
                          <button
                            onClick={() => {
                              setProjectForm({
                                name: currentProject?.name || '',
                                description: currentProject?.description || '',
                                latitude: currentProject?.latitude || '',
                                longitude: currentProject?.longitude || '',
                              })
                              setProjectEditMode(true)
                            }}
                            title="Edit project"
                            className="text-gray-400 hover:text-gray-600 transition"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      {!projectEditMode ? (
                        /* Read-only screen (dynamic) */
                        <div className="px-6 py-8 space-y-6">
                          <div>
                            <h4 className="common-size16 text-sm font-bold text-[#4D4D4D]">Project Name</h4>
                            <p className="common-size15 text-sm text-[#4D4D4D]">{currentProject?.name || 'N/A'}</p>
                          </div>
                          <div>
                            <h4 className="common-size18 text-sm font-bold text-[#4D4D4D]">Project Description</h4>
                            {currentProject?.description
                              ? <p className="common-size16 text-sm text-[#4D4D4D]">{currentProject.description}</p>
                              : <p className="common-size15 text-sm text-[#4D4D4D] italic">Edit to add project description.</p>}
                          </div>
                          <div>
                            <h4 className="common-size16 text-sm font-bold text-[#4D4D4D]">Default Dashboard</h4>
                            <p className="common-size15 text-sm text-[#4D4D4D]">{dashboardMenuItems[0] || 'Overview'}</p>
                          </div>
                          <div>
                            <h4 className="common-size16 text-sm font-bold text-[#4D4D4D]">Default Map Type</h4>
                            <p className="common-size15 text-sm text-[#4D4D4D]">Imagery</p>
                          </div>
                          <div>
                            <h4 className="common-size16 text-sm font-bold text-[#4D4D4D]">Project Logo</h4>
                            {currentProject?.logo
                              ? <img src={currentProject.logo} alt="Project logo" className="h-[70px] w-auto object-contain border border-gray-100 rounded bg-white p-1" />
                              : <p className="common-size15 text-sm text-[#4D4D4D] italic">No logo set. Showing the default WQData LIVE logo.</p>}
                          </div>
                          <div>
                            <h4 className="common-size16 text-sm font-bold text-[#4D4D4D]">Disclaimer</h4>
                            <p className="common-size15 text-sm text-[#4D4D4D] italic">Edit to add disclaimer.</p>
                          </div>
                          {(currentProject?.latitude || currentProject?.longitude) && (
                            <div>
                              <h4 className="common-size16 text-sm font-bold text-[#4D4D4D]">Coordinates</h4>
                              <p className="common-size15 text-sm text-[#4D4D4D]">{currentProject.latitude}, {currentProject.longitude}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        /* Inline edit screen (no popup) */
                        <div className="px-6 py-8 space-y-6">
                          <div>
                            <label className="common-size15 text-sm font-bold text-[#4D4D4D] mb-1">Project Name</label>
                            <input type="text" value={projectForm.name} onChange={(e) => setProjectForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#358ECD]" />
                          </div>
                          <div>
                            <label className="common-size15 text-sm font-bold text-[#4D4D4D] mb-1">Project Description</label>
                            <textarea value={projectForm.description} onChange={(e) => setProjectForm(f => ({ ...f, description: e.target.value }))} rows="3" placeholder="Project Description" className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#358ECD] resize-none" />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="common-size15 text-sm font-bold text-[#4D4D4D] mb-1">Default Dashboard</label>
                              <select className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#358ECD]"><option>Overview</option><option>Parameter Comparison</option><option>Project Map</option><option>Current Conditions</option><option>System Performance</option></select>
                            </div>
                            <div>
                              <label className="common-size15 text-sm font-bold text-[#4D4D4D] mb-1">Default Map Type</label>
                              <select className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#358ECD]"><option>Imagery</option><option>Street</option><option>Terrain</option><option>Hybrid</option></select>
                            </div>
                          </div>
                          <div>
                            <label className="common-size15 text-sm font-bold text-[#4D4D4D] mb-1">Project Logo (232 x 70 pixels)</label>
                            {currentProject?.logo && (
                              <div className="mb-2">
                                <img src={currentProject.logo} alt="Current project logo" className="h-[60px] w-auto object-contain border border-gray-100 rounded bg-white p-1" />
                              </div>
                            )}
                            <input
                              ref={logoFileInputRef}
                              type="file"
                              accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                              className="hidden"
                              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleProjectLogoFile(f); e.target.value = '' }}
                            />
                            <div
                              onClick={() => { if (!logoUploading) logoFileInputRef.current?.click() }}
                              onDragOver={(e) => { e.preventDefault() }}
                              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) handleProjectLogoFile(f) }}
                              className={`border border-dashed border-gray-300 rounded p-4 text-center text-sm text-gray-600 transition ${logoUploading ? 'opacity-60 cursor-wait' : 'cursor-pointer hover:border-[#358ECD] hover:bg-gray-50'}`}
                            >
                              {logoUploading
                                ? 'Uploading…'
                                : <>Drag and Drop Files or <span className="text-[#358ECD] font-semibold hover:underline">Browse</span></>}
                            </div>
                            {logoUploadError && (
                              <p className="mt-1 text-xs text-red-600">{logoUploadError}</p>
                            )}
                          </div>
                          <div>
                            <label className="common-size15 text-sm font-bold text-[#4D4D4D] mb-1">Coordinates</label>
                            <div className="grid grid-cols-2 gap-4">
                              <input type="text" value={projectForm.latitude} onChange={(e) => setProjectForm(f => ({ ...f, latitude: e.target.value }))} placeholder="Latitude" className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#358ECD]" />
                              <input type="text" value={projectForm.longitude} onChange={(e) => setProjectForm(f => ({ ...f, longitude: e.target.value }))} placeholder="Longitude" className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#358ECD]" />
                            </div>
                          </div>
                          <button
                            disabled={savingProject || deletingProject || !currentProject?.id}
                            onClick={async () => {
                              if (!currentProject?.id) return
                              setSavingProject(true)
                              try {
                                await projectsApi.updateProject(currentProject.id, { name: projectForm.name, description: projectForm.description, latitude: projectForm.latitude, longitude: projectForm.longitude })
                                setCurrentProject(prev => ({ ...prev, ...projectForm }))
                                setProjectEditMode(false)
                              } catch (err) {
                                alert('Failed to save project: ' + (err.response?.data?.error || err.message))
                              } finally { setSavingProject(false) }
                            }}
                            className="w-full py-2.5 text-white rounded text-sm font-semibold hover:opacity-90 transition disabled:opacity-50"
                            style={{ backgroundColor: '#358ECD' }}
                          >
                            {savingProject ? 'SAVING…' : 'SAVE'}
                          </button>
                          {/* Delete Project — OWNER only (project admins/collaborators
                              may edit but not delete). Soft-deletes on the backend
                              and leaves to the next project (or home if none left). */}
                          {currentProject?.user_role === 'Owner' && (
                            <button
                              disabled={savingProject || deletingProject || !currentProject?.id}
                              onClick={async () => {
                                if (!currentProject?.id) return
                                if (!window.confirm(`Are you sure you wish to delete this project (${currentProject.name})?`)) return
                                setDeletingProject(true)
                                try {
                                  await projectsApi.deleteProject(currentProject.id)
                                  const remaining = projects.filter(p => p.id !== currentProject.id)
                                  setProjects(remaining)
                                  setProjectEditMode(false)
                                  if (remaining.length > 0) {
                                    setCurrentProject(remaining[0])
                                    navigate(`/dashboard/project/${remaining[0].id}`, { replace: true })
                                  } else {
                                    setCurrentProject(null)
                                    navigate('/', { replace: true })
                                  }
                                } catch (err) {
                                  alert('Failed to delete project: ' + (err.response?.data?.error || err.message))
                                } finally { setDeletingProject(false) }
                              }}
                              className="w-full py-2.5 rounded text-sm font-semibold border border-red-500 text-red-600 hover:bg-red-50 transition disabled:opacity-50"
                            >
                              {deletingProject ? 'DELETING…' : 'DELETE PROJECT'}
                            </button>
                          )}
                          <button onClick={() => setProjectEditMode(false)} className="w-full text-center text-sm text-[#4D4D4D] hover:underline">Cancel</button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {/* API and Streaming Method panels — the outer Settings
                    wrapper already provides the gray background + center
                    alignment, so each panel just renders its card. */}
                {/* Settings panels are read-only until Edit Mode (client
                    request). Keys and current values stay READABLE — only the
                    controls that change something are disabled. */}
                {currentSettingsIndex === 2 && (
                  <EditModeGate editMode={isCustomizingDashboard} label="API settings">
                    <ApiKeysPanel projectId={currentProject?.id} />
                  </EditModeGate>
                )}
                {currentSettingsIndex === 3 && (
                  <EditModeGate editMode={isCustomizingDashboard} label="Streaming Method settings">
                    <StreamingMethodPanel projectId={currentProject?.id} onCancel={() => setActiveTab('Dashboard')} />
                  </EditModeGate>
                )}

                {/* RSS Feed — placeholder scaffold at settings index 6.
                    Public Portal + Embedded Data Applet are modal-only
                    overlays and are rendered outside the sub-page
                    switch (see below) so they don't disturb the
                    active page underneath. */}
                {currentSettingsIndex === 6 && (
                  <EditModeGate editMode={isCustomizingDashboard} label="RSS Feed settings">
                    <SettingsPlaceholder
                      title="RSS Feed"
                      subtitle="Serve this project's latest readings as an RSS feed for downstream consumers."
                    />
                  </EditModeGate>
                )}
              </div>
            )}

            {/* Modal-only Settings entries — rendered outside the tab
                switch so they overlay whatever page is active
                underneath. Opened from the Settings dropdown; closed
                via the modal's own X / Cancel / Save. */}
            {/* Public Portal + Embedded Data Applet are fixed-overlay MODALS, so
                the read-only gating lives INSIDE each panel (banner + disabled
                fieldset) via a readOnly prop — wrapping the overlay in
                EditModeGate would render the banner inline on the page and break
                the modal layout. */}
            {publicPortalOpen && (
              <PublicPortalPanel
                projectId={currentProject?.id}
                onClose={() => setPublicPortalOpen(false)}
                readOnly={!isCustomizingDashboard}
              />
            )}
            {embeddedAppletOpen && (
              <EmbeddedDataAppletPanel
                projectId={currentProject?.id}
                projectName={currentProject?.name || ''}
                sites={projectSites}
                onClose={() => setEmbeddedAppletOpen(false)}
                readOnly={!isCustomizingDashboard}
              />
            )}

            {/* Live View — read-only outside Edit Mode (client request:
                "Making any changes in Live screens"). Readings and status
                stay visible; the controls that command the logger (rename,
                Reset Device, Sync Clock, interval edits) are disabled. */}
            {activeTab === 'Live' && (
              <div className="w-full h-full flex flex-col bg-white">
                {/* Icon-tab + device switcher live OUTSIDE the read-only gate:
                    they're navigation, not editable settings. The gate uses
                    <fieldset disabled>, which disables every button inside it —
                    that was making these three tabs unclickable in read-only
                    mode. showHeader={false} suppresses each sub-tab's own copy. */}
                <div className="bg-white border-gray-200 rounded-lg shadow-sm px-3 py-2">
                  <LiveIconTabs
                    currentLiveIndex={currentLiveIndex}
                    setCurrentLiveIndex={setCurrentLiveIndex}
                    devices={liveDevices}
                    deviceId={liveDeviceId}
                    onSelectDevice={setLiveDeviceId}
                    streamSlotRef={setLiveStreamSlot}
                    showIridium={showIridium}
                  />
                </div>
                {/* Gate only Data Logger / Communications (they command the
                    logger). Render it ONLY for those tabs — an empty EditModeGate
                    still shows its read-only banner, so leaving it mounted on the
                    Sensor Readings / Iridium Messages tabs put the banner there too. */}
                {currentLiveIndex !== 2 && currentLiveIndex !== 3 && (
                  <EditModeGate editMode={isCustomizingDashboard} label="Live controls">
                    {currentLiveIndex === 0 && (
                      <DataLoggerTab
                        projectId={currentProject?.id}
                        deviceId={liveDeviceId}
                        devices={liveDevices}
                        onSelectDevice={setLiveDeviceId}
                        currentLiveIndex={currentLiveIndex}
                        setCurrentLiveIndex={setCurrentLiveIndex}
                        showHeader={false}
                      />
                    )}
                    {currentLiveIndex === 1 && (
                      <CommunicationsTab
                        projectId={currentProject?.id}
                        deviceId={liveDeviceId}
                        devices={liveDevices}
                        onSelectDevice={setLiveDeviceId}
                        currentLiveIndex={currentLiveIndex}
                        setCurrentLiveIndex={setCurrentLiveIndex}
                        showHeader={false}
                      />
                    )}
                  </EditModeGate>
                )}
                {/* Sensor Readings is a pure read-only LIVE view (no controls to
                    command the logger), so it renders OUTSIDE the EditModeGate —
                    no "Live controls are read-only" banner, and its streaming
                    poll isn't disabled by the gate's <fieldset disabled>. */}
                {currentLiveIndex === 2 && (
                  <SensorReadingsTab
                    projectId={currentProject?.id}
                    deviceId={liveDeviceId}
                    devices={liveDevices}
                    onSelectDevice={setLiveDeviceId}
                    currentLiveIndex={currentLiveIndex}
                    setCurrentLiveIndex={setCurrentLiveIndex}
                    showHeader={false}
                    streamSlot={liveStreamSlot}
                    // Firmware-gate "Update to Latest": don't queue the update here.
                    // Send the user to Live > Data Logger and open the Edit Mode
                    // login so they start the update from there.
                    onRequestEditUpdate={() => {
                      setCurrentLiveIndex(0)
                      if (!isCustomizingDashboard && canEditDashboard) setAdminSignInOpen(true)
                    }}
                  />
                )}
                {/* Iridium Messages — read-only list, shown only for Iridium
                    loggers. Rendered OUTSIDE the EditModeGate (no logger commands). */}
                {currentLiveIndex === 3 && showIridium && (
                  <IridiumMessagesTab
                    projectId={currentProject?.id}
                    deviceId={liveDeviceId}
                    devices={liveDevices}
                    onSelectDevice={setLiveDeviceId}
                    currentLiveIndex={currentLiveIndex}
                    setCurrentLiveIndex={setCurrentLiveIndex}
                    showHeader={false}
                    showIridium={showIridium}
                  />
                )}
              </div>
            )}

            {/* Gallery View */}
            {activeTab === 'Gallery' && (
              <div className="w-full h-full flex flex-col">
                {/* Gallery Header Bar */}
                <div className="flex items-center justify-between pb-4 border-gray-200 envir-field">
                  <h2 className="common-size15 font-bold text-sm text-[#4D4D4D] bg-white px-6 py-2 rounded-lg">Gallery: Environmental Field Station</h2>
                  <div className="break-sec flex items-center gap-3">
                    {/* Album edit/delete — shown only when a DB album tab is active */}
                    {(() => {
                      const activeDbAlbum = projectAlbums.find(a => a.name === galleryTab)
                      if (!activeDbAlbum) return null
                      return (
                        <>
                          <button
                            onClick={() => { setEditAlbumModal({ id: activeDbAlbum.id, name: activeDbAlbum.name }); setEditAlbumName(activeDbAlbum.name) }}
                            className="common-size15 flex items-center gap-1.5 text-sm text-[#358ECD] bg-white text-[#4D4D4D] common-size15 px-3 py-2 rounded-md transition"
                            title="Rename this album"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 012.828 2.828L11.828 15.828a2 2 0 01-1.414.586H8v-2.414a2 2 0 01.586-1.414z" /></svg>
                            Edit Album
                          </button>
                          <button
                            onClick={() => handleDeleteAlbum(activeDbAlbum.id, activeDbAlbum.name)}
                            className="common-size15 flex items-center gap-1.5 text-sm text-red-500 bg-white hover:text-red-700 text-[#4D4D4D] common-size15 px-3 py-2 rounded-md hover:bg-red-50 transition"
                            title="Delete this album"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            Delete Album
                          </button>
                          <div className="w-px h-5 bg-gray-300" />
                        </>
                      )
                    })()}
                    <button
                      onClick={() => {
                        setShowCreateAlbumModal(true)
                        setAlbumName('')
                        setAlbumImages([])
                        setAlbumError('')
                        setAlbumSuccess('')
                      }}
                      className="flex items-center gap-1.5 text-sm text-gray-600 bg-white hover:text-gray-900 px-3 py-2 border-gray-300 rounded-md text-[#4D4D4D] common-size15 hover:bg-gray-50 transition">
                      <span>+</span> Album
                    </button>
                    <button
                      onClick={() => { setUploadItems([]); setShowUploadModal(true) }}
                      className="flex items-center gap-1.5 text-sm text-gray-600 bg-white hover:text-gray-900 px-3 py-2 border-gray-300 rounded-md text-[#4D4D4D] common-size15 hover:bg-gray-50 transition">
                      <span>↑</span> Upload
                    </button>
                    <div className="relative">
                      <button
                        onClick={() => setOpenGalleryMenu(m => m === 'filter' ? null : 'filter')}
                        className={`flex items-center gap-1.5 text-sm px-3 bg-white py-2 bg-white rounded-md text-[#4D4D4D] common-size15 transition ${activeGalleryFilterCount > 0 ? 'border-[#ffffff] text-[#358ECD] bg-[#E8F1F8]' : 'border-gray-300 text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}>
                        <span>☰</span> Filter{activeGalleryFilterCount > 0 ? ` (${activeGalleryFilterCount})` : ''}
                      </button>
                      {openGalleryMenu === 'filter' && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setOpenGalleryMenu(null)} />
                          <div className="absolute right-0 mt-1 w-64 max-h-[70vh] overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                            {[
                              { key: 'title',       label: 'Title' },
                              { key: 'description', label: 'Description' },
                              { key: 'tags',        label: 'Tags' },
                              { key: 'date',        label: 'Date' },
                              { key: 'location',    label: 'Location' },
                              { key: 'advanced',    label: 'Advanced' },
                            ].map(field => {
                              const isOpen = openGalleryFilterField === field.key
                              const hasVal = field.key === 'date'     ? (galleryFilters.dateFrom || galleryFilters.dateTo)
                                           : field.key === 'advanced' ? activeGalleryFilterCount > 0
                                           : (galleryFilters[field.key] || '').trim()
                              const dateRow = (
                                <div className="flex flex-col gap-2">
                                  <label className="text-[11px] text-gray-500">From
                                    <input type="date" value={galleryFilters.dateFrom}
                                      onChange={e => { setGalleryFilters(p => ({ ...p, dateFrom: e.target.value })); setGalleryPage(1) }}
                                      className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD]" /></label>
                                  <label className="text-[11px] text-gray-500">To
                                    <input type="date" value={galleryFilters.dateTo}
                                      onChange={e => { setGalleryFilters(p => ({ ...p, dateTo: e.target.value })); setGalleryPage(1) }}
                                      className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD]" /></label>
                                </div>
                              )
                              return (
                                <div key={field.key} className="border-b border-gray-50 last:border-b-0">
                                  <button
                                    onClick={() => setOpenGalleryFilterField(f => f === field.key ? null : field.key)}
                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition">
                                    <span className="common-size15 text-[#4D4D4D] text-xs">▾</span>
                                    <span className="common-size15 text-[#4D4D4D]">{field.label}</span>
                                    {hasVal ? <span className="w-1.5 h-1.5 rounded-full bg-[#358ECD]" /> : null}
                                  </button>
                                  {isOpen && (
                                    <div className="px-4 pb-3 pt-1 space-y-2">
                                      {field.key === 'date' ? dateRow
                                        : field.key === 'advanced' ? (
                                          <div className="space-y-2">
                                            {['title', 'description', 'tags', 'location'].map(k => (
                                              <input key={k} type="text" placeholder={k.charAt(0).toUpperCase() + k.slice(1)}
                                                value={galleryFilters[k]}
                                                onChange={e => { setGalleryFilters(p => ({ ...p, [k]: e.target.value })); setGalleryPage(1) }}
                                                className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD]" />
                                            ))}
                                            {dateRow}
                                          </div>
                                        ) : (
                                          <input type="text" autoFocus placeholder={`Filter by ${field.label.toLowerCase()}…`}
                                            value={galleryFilters[field.key]}
                                            onChange={e => { setGalleryFilters(p => ({ ...p, [field.key]: e.target.value })); setGalleryPage(1) }}
                                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD]" />
                                        )}
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                            {activeGalleryFilterCount > 0 && (
                              <div className="px-4 py-2 border-t border-gray-100">
                                <button
                                  onClick={() => { setGalleryFilters({ title: '', description: '', tags: '', location: '', dateFrom: '', dateTo: '' }); setGalleryPage(1) }}
                                  className="text-xs text-[#358ECD] hover:underline">Clear all filters</button>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Category Tabs + Search */}
                <div className="envir-field flex items-center justify-between py-3 border-gray-200 bg-white px-6 py-2 rounded-lg mb-5">
                  <div className="flex gap-8 items-center">
                    {galleryTabs.map((tab) => (
                      <button
                        key={tab}
                        onClick={() => { handleGalleryTabClick(tab); setSelectedPhoto(null); }}
                        className={`text-sm pb-1 border-b-4 font-semibold text-[#4D4D4D] common-size18 transition ${
                          galleryTab === tab ? 'border-[#56B565] text-[#4D4D4D]' : 'border-transparent text-[#4D4D4D] hover:text-gray-700'
                        }`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search Photos"
                      value={gallerySearch}
                      onChange={(e) => setGallerySearch(e.target.value)}
                      className="pl-8 pr-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500 w-80"
                    />
                    <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-gray-400" />
                  </div>
                </div>

                {/* Detail View when photo is selected */}
                {selectedPhoto ? (
                  <div className="flex flex-1 overflow-hidden gap-4">
                    {/* Left Panel: Thumbnails + Info */}
                    <div className="w-f-mb w-120 flex-shrink-0 custom-scrollbar rounded-md overflow-y-auto flex flex-col mb-4">
                      {/* Thumbnail Section */}
                      <div className="envir-field p-6 border-b border-gray-100 rounded-md bg-white mb-4">
                        <div className="all-med flex items-baseline justify-between mb-5">
                          <h3 className="common-head25 text-sm font-semibold text-[#4D4D4D]">
                            {galleryTab === 'All Photos' ? 'All Media' : `All ${galleryTab} Media`}
                          </h3>
                          <span className="common-size18 text-xs text-[#4D4D4D]">(Page {galleryPage}/{totalGalleryPages || 1})</span>
                        </div>
                        {/* 4-column thumbnail grid */}
                        <div className="grid grid-cols-4 gap-1 mb-3">
                          {currentPhotos.map((photo) => {
                            const imageUrl = photo.src || ''
                            const imageHasError = failedImageIds.has(photo.id)
                            return (
                              <div
                                key={photo.id}
                                // Already inside the detail view — swap the photo
                                // in place rather than stacking history entries.
                                onClick={() => openPhoto(
                                    photoEditsMap[photo.id] ? { ...photo, ...photoEditsMap[photo.id] } : photo,
                                    { replace: true },
                                )}
                                className={`cursor-pointer rounded-md overflow-hidden border-2 transition-all ${
                                  selectedPhoto.id === photo.id
                                    ? 'border-blue-500'
                                    : 'border-transparent hover:border-gray-300'
                                }`}
                              >
                                {imageHasError || !imageUrl ? (
                                  <div className="w-full h-14 bg-gray-100 flex items-center justify-center">
                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                  </div>
                                ) : (
                                  <img
                                    src={imageUrl}
                                    alt={photo.title}
                                    className="w-32 h-32 object-cover"
                                    onError={() => setFailedImageIds(prev => new Set(prev).add(photo.id))}
                                    loading="lazy"
                                  />
                                )}
                              </div>
                            )
                          })}
                        </div>
                        {/* Pagination arrows */}
                        <div className="flex items-center justify-center gap-3">
                          <button
                            onClick={() => setGalleryPage(p => Math.max(1, p - 1))}
                            disabled={galleryPage <= 1}
                            className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded text-gray-500 hover:bg-gray-50 disabled:opacity-40 transition"
                          >▲</button>
                          <button
                            onClick={() => setGalleryPage(p => Math.min(totalGalleryPages, p + 1))}
                            disabled={galleryPage >= totalGalleryPages}
                            className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded text-gray-500 hover:bg-gray-50 disabled:opacity-40 transition"
                          >▼</button>
                        </div>
                      </div>

                      {/* Information Panel */}
                      <div className="flex-1 rounded-md bg-white">
                        {/* Info header with edit button */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                          <h2 className="common-size18 text-sm font-bold text-[#4D4D4D]">Information</h2>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => {
                                setEditingPhoto(selectedPhoto)
                                setEditPhotoFile(null)
                                setEditPhotoPreview(null)
                                const currentAlbum = selectedPhoto.album || (galleryTab !== 'All Photos' ? galleryTab : '')
                                setEditFormData({
                                  title: selectedPhoto.title,
                                  description: selectedPhoto.description,
                                  tags: selectedPhoto.tags,
                                  album: currentAlbum,
                                  location: selectedPhoto.location,
                                  // The select binds to the site id; `location`
                                  // above stays the display label ("Site - x").
                                  siteId: selectedPhoto.siteId ? String(selectedPhoto.siteId) : ''
                                })
                                setShowEditMediaModal(true)
                              }}
                              className="p-1 text-gray-400 hover:text-[#358ECD] transition"
                              title="Edit information"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeletePhoto(selectedPhoto)}
                              className="p-1 text-gray-400 hover:text-red-500 transition"
                              title="Delete photo"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div className="px-4 py-3 space-y-4">
                          {[
                            { label: 'Title', value: selectedPhoto.title },
                            { label: 'Description', value: selectedPhoto.description },
                            { label: 'Original Filename', value: selectedPhoto.originalFilename },
                            { label: 'Camera S/N', value: selectedPhoto.cameraSN },
                            { label: 'Tags', value: selectedPhoto.tags },
                            { label: 'Album', value: selectedPhoto.album },
                            { label: 'Uploaded By', value: selectedPhoto.uploadedBy },
                            { label: 'Date', value: selectedPhoto.date },
                            { label: 'Dimensions', value: selectedPhoto.dimensions },
                            { label: 'Size', value: selectedPhoto.size },
                            { label: 'Total Size', value: selectedPhoto.totalSize },
                            { label: 'Location', value: selectedPhoto.location },
                          ].map(({ label, value }) => (
                            <div key={label}>
                              <p className="common-size15 text-xs font-bold text-[#4D4D4D] uppercase tracking-wide">{label}</p>
                              <p className="common-size15 text-sm text-[#4D4D4D] mt-0.5">{value || '—'}</p>
                            </div>
                          ))}
                          <div>
                            <p className="common-size15 text-sm text-[#4D4D4D] font-bold uppercase tracking-wide">Map</p>
                            <button
                              onClick={() => setShowPhotoMap(v => !v)}
                              className="common-size15 text-sm text-[#4D4D4D] hover:underline mt-0.5"
                            >
                              {showPhotoMap ? 'Hide Map' : 'Show Map'}
                            </button>
                            {showPhotoMap && (
                              <PhotoLocationMap
                                latitude={selectedPhoto.latitude}
                                longitude={selectedPhoto.longitude}
                                title={selectedPhoto.location || selectedPhoto.title}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Panel: Large Photo or Video */}
                    <div className="flex-1 bg-white flex rounded-md items-center justify-center overflow-hidden">
                      {selectedPhoto.mediaType === 'video' ? (
                        <video
                          src={selectedPhoto.videoUrl || selectedPhoto.largeSrc}
                          className="max-w-full max-h-full object-contain"
                          controls
                          autoPlay
                          playsInline
                        />
                      ) : failedImageIds.has(selectedPhoto.id) || (!selectedPhoto.largeSrc && !selectedPhoto.src) ? (
                        <div className="flex flex-col items-center justify-center text-gray-400 gap-3">
                          <svg className="w-20 h-20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="text-sm">Unable to load image</p>
                        </div>
                      ) : (
                        <img
                          src={selectedPhoto.largeSrc || selectedPhoto.src}
                          alt={selectedPhoto.title}
                          className="max-w-full max-h-full object-contain"
                          onError={() => setFailedImageIds(prev => new Set(prev).add(selectedPhoto.id))}
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  /* Grid View when no photo selected */
                  <div className="flex-1 overflow-y-auto py-4">
                    {galleryLoading && apiPhotos.length === 0 ? (
                      <div className="flex items-center justify-center py-16">
                        <Loader className="w-6 h-6 animate-spin text-blue-500 mr-2" />
                        <span className="text-sm text-gray-600">Loading photos...</span>
                      </div>
                    ) : currentPhotos.length === 0 && !galleryLoading ? (
                      <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                        <svg className="w-16 h-16 mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-sm">No photos found for this project</p>
                      </div>
                    ) : (
                    <div className="envir-field grid grid-cols-6 gap-6">
                      {currentPhotos.map((photo) => {
                        const imageUrl = photo.src || photo.thumbnail || photo.image || photo.file_url || photo.url || ''
                        const imageHasError = failedImageIds.has(photo.id)
                        
                        return (
                          <div
                            key={photo.id}
                            className="cursor-pointer group bg-white rounded-lg overflow-hidden"
                            // Pushes ?photo=<id> so Back returns to this grid.
                            onClick={() => openPhoto(photo)}
                          >
                            <div className="aspect-[4/3] overflow-hidden bg-gray-100 flex items-center justify-center relative">
                              {photo.mediaType === 'video' ? (
                                <>
                                  {/* First frame as the poster; a play badge marks it as video. */}
                                  <video
                                    src={photo.videoUrl || photo.largeSrc}
                                    className="w-full h-full object-cover"
                                    muted
                                    playsInline
                                    preload="metadata"
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <div className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center">
                                      <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                                    </div>
                                  </div>
                                </>
                              ) : imageHasError || !imageUrl ? (
                                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                </div>
                              ) : (
                                <img
                                  src={imageUrl}
                                  alt={photo.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                  onError={() => setFailedImageIds(prev => new Set(prev).add(photo.id))}
                                  loading="lazy"
                                />
                              )}
                            </div>
                            <div className="mt-4 text-center pb-4 px-2">
                              {/* Matches the legacy Symfony gallery tile
                                  (gallery-image-title-top/-bottom): title on
                                  top, description below only when present —
                                  NOT the upload category ("photo"/"Photo"),
                                  which isn't a title at all (see
                                  DashboardPage.jsx:916). */}
                              <p className="common-size15 text-xs font-bold text-[#4D4D4D] capitalize mb-3">{photo.title}</p>
                              {photo.description && (
                                <p className="common-size15 font-semibold text-xs text-[#4D4D4D] italic capitalize">{photo.description}</p>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                    )}

                    {/* Pagination */}
                    <div className="flex items-center justify-center gap-3 mt-8 pb-6">
                      <button 
                        onClick={() => setGalleryPage(prev => Math.max(1, prev - 1))}
                        disabled={galleryPage === 1}
                        className="common-size20 px-4 py-2.5 text-sm text-[#AFAFAF] bg-white rounded-md disabled:opacity-40 disabled:cursor-not-allowed transition"
                      >
                        Back
                      </button>
                      {Array.from({ length: totalGalleryPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setGalleryPage(page)}
                          className={`common-size20 w-10 h-10 text-sm rounded-md transition ${
                            galleryPage === page
                              ? 'bg-white text-[#358ECD] '
                              : 'bg-white text-[#4D4D4D]'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      <button 
                        onClick={() => setGalleryPage(prev => Math.min(totalGalleryPages, prev + 1))}
                        disabled={galleryPage === totalGalleryPages}
                        className="common-size20 px-4 py-2.5 text-sm text-[#4D4D4D] bg-white rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}

                {/* Create Album Modal */}
                {showCreateAlbumModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4" onClick={() => setShowCreateAlbumModal(false)}>
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                      {/* Modal Header */}
                      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                        <h2 className="common-head25 text-lg font-bold text-[#4D4D4D]">Create New Album</h2>
                        <button 
                          onClick={() => setShowCreateAlbumModal(false)}
                          className="text-gray-400 hover:text-gray-600 transition"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>

                      {/* Modal Content */}
                      <div className="px-6 py-4 space-y-4">
                        {/* Album Name Input */}
                        <div>
                          <label className="common-size15 block font-semibold text-sm text-[#4D4D4D] mb-1">Album Name</label>
                          <SecureInput
                            type="text"
                            value={albumName}
                            onChange={(e) => {
                              setAlbumName(e.target.value)
                              setAlbumError('')
                            }}
                            placeholder="Enter album name"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>

                        {/* Image Upload Section */}
                        <div>
                          <label className="common-size15 block font-semibold text-sm text-[#4D4D4D] mb-2">Upload Images</label>
                          
                          {/* Upload Area */}
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 hover:bg-blue-50 transition cursor-pointer">
                            <input
                              type="file"
                              multiple
                              accept="image/*"
                              onChange={handleAlbumImageUpload}
                              className="hidden"
                              id="album-image-upload"
                            />
                            <label htmlFor="album-image-upload" className="cursor-pointer">
                              <svg className="w-8 h-8 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
                              </svg>
                              <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                              <p className="text-xs text-gray-500 mt-1">PNG, JPG, GIF up to 10MB</p>
                            </label>
                          </div>

                          {/* Uploaded Images Preview */}
                          {albumImages.length > 0 && (
                            <div className="mt-4">
                              <p className="text-sm  text-gray-700 mb-2">Uploaded Images ({albumImages.length})</p>
                              <div className="grid grid-cols-4 gap-2">
                                {albumImages.map((file, index) => (
                                  <div key={index} className="relative group">
                                    <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                                      <img
                                        src={URL.createObjectURL(file)}
                                        alt={`Preview ${index}`}
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                    <button
                                      onClick={() => removeAlbumImage(index)}
                                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                                      title="Remove image"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                    <p className="text-xs text-gray-600 mt-1 truncate">{file.name}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Error Message */}
                        {albumError && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <p className="text-sm text-red-700">{albumError}</p>
                          </div>
                        )}

                        {/* Success Message */}
                        {albumSuccess && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <p className="text-sm text-green-700">{albumSuccess}</p>
                          </div>
                        )}
                      </div>

                      {/* Modal Footer */}
                      <div className="px-6 py-2 bg-white">
                        <button
                          onClick={handleCreateAlbum}
                          disabled={isCreatingAlbum || !albumName.trim() || albumImages.length === 0}
                          className="savebtn common-size18 w-full px-4 py-1.5 bg-[#358ECD] font-bold text-white text-sm rounded-md transition"
                        >
                          {isCreatingAlbum ? (
                            <>
                              <Loader className="w-4 h-4 animate-spin" />
                              Creating...
                            </>
                          ) : (
                            'Create Album'
                          )}
                        </button>
                        <button
                          onClick={() => setShowCreateAlbumModal(false)}
                          disabled={isCreatingAlbum}
                          className="cancelbtn common-size16 mt-2 w-full text-center text-sm text-[#358ECD] font-semibold hover:underline disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Dashboard View */}
            {activeTab === 'Dashboard' && (
              /* display:contents keeps the layout identical to the old fragment
                 while giving the slide content a key: bumping dashRefreshTick
                 (green Refresh) remounts it so its widgets re-fetch, and because
                 currentMenuIndex is unchanged the user stays on the same slide. */
              <div style={{ display: 'contents' }} key={`dash-${currentMenuIndex}-${dashRefreshTick}`}>
                {/* Overview + Weather Station content — both are widget-grid
                    dashboards rendered generically from the template's widgets. */}
                {(templates[currentMenuIndex]?.template_type === 'overview' || templates[currentMenuIndex]?.name === 'Overview' || templates[currentMenuIndex]?.template_type === 'weather_station' || templates[currentMenuIndex]?.name === 'Weather Station') && (
                  <>
                    {currentProject?.id ? (
                      <TemplateDashboardGrid
                        key={templates[currentMenuIndex]?.id}
                        ref={gridRef}
                        projectId={currentProject.id}
                        templateId={templates[currentMenuIndex]?.id}
                        isEditing={isCustomizingDashboard}
                        droppingWidget={draggingWidget}
                        onDropComplete={() => { setDraggingWidget(null); setAddWidgetModalHidden(false); setShowAddWidgetsModal(false) }}
                        containerWidth={gridContainerWidth}
                      />
                    ) : (
                      <div className="flex items-center justify-center py-20 text-gray-400 text-sm">
                        Select a project to view the dashboard
                      </div>
                    )}
                  </>
                )}

                {/* ── HIDDEN LEGACY OVERVIEW (kept for reference) ── */}
                {false && (
                  <>
            {/* Graph Section with Time Controls */}
            <div className="bg-white rounded shadow-sm mb-4">
              <div className="flex border-b border-gray-100 items-center justify-between p-4">
                <h3 className="font-bold text-[#4D4D4D] text-lg">
                  {projectSites.length > 0 ? projectSites[0].name : 'Project Site'}
                </h3>
              </div>
              
              {/* Time Period Controls */}
              <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-600 ">Time Range:</span>
                  <div className="flex items-center gap-1 bg-white rounded px-2 py-1 border border-gray-300">
                    <button 
                      onClick={() => handlePeriodClick('D')}
                      className={`px-2 py-1 text-xs  rounded transition ${
                        activePeriod === 'D' 
                          ? 'bg-gray-500 text-white' 
                          : 'text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      D
                    </button>
                    <button 
                      onClick={() => handlePeriodClick('W')}
                      className={`px-2 py-1 text-xs  rounded transition ${
                        activePeriod === 'W' 
                          ? 'bg-gray-500 text-white' 
                          : 'text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      W
                    </button>
                    <button 
                      onClick={() => handlePeriodClick('M')}
                      className={`px-2 py-1 text-xs  rounded transition ${
                        activePeriod === 'M' 
                          ? 'bg-gray-500 text-white' 
                          : 'text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      M
                    </button>
                    <button 
                      onClick={() => handlePeriodClick('Y')}
                      className={`px-2 py-1 text-xs  rounded transition ${
                        activePeriod === 'Y' 
                          ? 'bg-gray-500 text-white' 
                          : 'text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Y
                    </button>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  {selectedParameters.length > 0 
                    ? `${selectedParameters.length} parameter${selectedParameters.length > 1 ? 's' : ''} selected`
                    : 'Select parameters to view data'}
                </div>
              </div>

              {/* Dynamic Graph Component */}
              <div className="p-4">
                <DynamicGraph 
                  selectedParameters={selectedParameters}
                  dateRange={dateRangeGraph}
                  timezone="UTC"
                  timePeriod={activePeriod}
                />
              </div>
            </div>

            {/* Second Row with Wind Rose and other content */}
            <div className="flex gap-4 mb-4 items-start">
              {/* Wind Rose */}
            <div className="w-[68%] flex flex-wrap gap-4">
              <div className="w-[49%]">
                <div className="bg-white rounded-lg shadow-sm">
                  <div className="flex border-b border-gray-100 items-center justify-between p-4">
                    <h3 className="font-bold text-[#4D4D4D] text-lg">Wind Rose</h3>
                    <button className="p-1 text-[#4D4D4D] hover:bg-gray-100 rounded transition">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.22222 17.7778H3.80556L14.6667 6.91663L13.0833 5.33337L2.22222 16.1945V17.7778ZM0 20V15.2778L14.6667 0.638916C14.8889 0.435181 15.1342 0.277832 15.4028 0.166687C15.6713 0.055542 15.9537 0 16.25 0C16.5463 0 16.8333 0.055542 17.1111 0.166687C17.3889 0.277832 17.6296 0.444458 17.8333 0.666626L19.3611 2.22223C19.5833 2.4259 19.7454 2.66669 19.8472 2.94446C19.9491 3.22223 20 3.5 20 3.77777C20 4.0741 19.9491 4.35651 19.8472 4.625C19.7454 4.89355 19.5833 5.13892 19.3611 5.36108L4.72221 20H0ZM13.8611 6.13892L13.0833 5.33337L14.6667 6.91663L13.8611 6.13892Z" fill="#888888"/>
                    </svg>
                    </button>
                  </div>
                  <div className=" flex items-center justify-center p-[25px] pb-[38px]">
                    <img alt="Project" src="/images/windrose.png"></img>
                  </div>
                </div>
                {/* Environmental News */}
                <div className="bg-white rounded-lg shadow-sm mt-4">
                  <div className="flex border-b border-gray-100 items-center justify-between p-4">
                    <h3 className="font-bold text-[#4D4D4D] text-lg">Environmental News</h3>
                    <button className="text-[#4D4D4D] flex justify-between items-center gap-4  text-base bg-gray-200 py-2 px-6 rounded-lg"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.22222 20C1.61111 20 1.08797 19.7824 0.652779 19.3472C0.21759 18.912 0 18.3889 0 17.7778V2.22223C0 1.61108 0.21759 1.08795 0.652779 0.652771C1.08797 0.21759 1.61111 0 2.22222 0H10V2.22223H2.22222V17.7778H17.7778V10H20V17.7778C20 18.3889 19.7824 18.912 19.3472 19.3472C18.912 19.7824 18.3889 20 17.7778 20H2.22222ZM7.44445 14.1111L5.88889 12.5555L16.2222 2.22223H12.2222V0H20V7.77777H17.7778V3.77777L7.44445 14.1111Z" fill="#888888"/>
                    </svg> View All</button>
                  </div>
                  <div className="space-y-2 p-[25px]">
                    <div className="mb-4">
                      <p className="text-base  text-gray-900 line-clamp-2">From Source to Sea: Monitoring and Protecting...</p>
                      <button className="text-gray-600  text-base mt-1 hover:underline">Read Article</button>
                    </div>
                    <div className="mb-4 border-t border-gray-200 my-4 py-2">
                      <p className="text-base  text-gray-900 line-clamp-2">From Source to Sea: Monitoring and Protecting...</p>
                      <button className="text-gray-600  text-base mt-1 hover:underline">Read Article</button>
                    </div>
                    <div className="border-t border-gray-200 pt-2">
                      <p className="text-base  text-gray-900 line-clamp-2">Using Traditional Ghanaian Fishing Canoes to Monitor...</p>
                      <button className="text-gray-600  text-base mt-1 hover:underline">Read Article</button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-[49%]">
              {/* Project Photo */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="flex border-b border-gray-100 items-center justify-between p-4">
                  <h3 className="font-bold text-[#4D4D4D] text-lg">Project Photo</h3>
                  <button className="text-[#4D4D4D] flex justify-between items-center gap-4  text-base bg-gray-200 py-2 px-6 rounded-lg"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.22222 20C1.61111 20 1.08797 19.7824 0.652779 19.3472C0.21759 18.912 0 18.3889 0 17.7778V2.22223C0 1.61108 0.21759 1.08795 0.652779 0.652771C1.08797 0.21759 1.61111 0 2.22222 0H10V2.22223H2.22222V17.7778H17.7778V10H20V17.7778C20 18.3889 19.7824 18.912 19.3472 19.3472C18.912 19.7824 18.3889 20 17.7778 20H2.22222ZM7.44445 14.1111L5.88889 12.5555L16.2222 2.22223H12.2222V0H20V7.77777H17.7778V3.77777L7.44445 14.1111Z" fill="#888888"/>
                    </svg> View All</button>
                </div>
                <div className=" flex items-center justify-center overflow-hidden p-[25px]">
                  <img src="/images/projectphoto.jpg" alt="Project" />
                </div>
              </div>
              {/* Field Notes */}
              <div className="bg-white rounded-lg shadow-sm mt-4">
                <div className="flex border-b border-gray-100 items-center justify-between p-4">
                  <h3 className="font-bold text-[#4D4D4D] text-lg">Field Notes</h3>
                  <div className="flex items-center gap-[20px]">
                  <button className="text-[#4D4D4D]  text-base bg-gray-200 py-2 px-6 rounded-lg">Save Note</button>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.22222 20C1.61111 20 1.08797 19.7824 0.652779 19.3472C0.21759 18.912 0 18.3889 0 17.7778V2.22223C0 1.61108 0.21759 1.08795 0.652779 0.652771C1.08797 0.21759 1.61111 0 2.22222 0H10V2.22223H2.22222V17.7778H17.7778V10H20V17.7778C20 18.3889 19.7824 18.912 19.3472 19.3472C18.912 19.7824 18.3889 20 17.7778 20H2.22222ZM7.44445 14.1111L5.88889 12.5555L16.2222 2.22223H12.2222V0H20V7.77777H17.7778V3.77777L7.44445 14.1111Z" fill="#888888"/>
                    </svg>
                  </div>
                </div>
                <div className="text-base p-[25px]">
                 <button className="text-gray-600 block ml-auto  text-base">12/29/2025 8:00 am EST</button>
                 <div className="text-center py-[25px]">
                  <p className="text-base text-gray-600">Enter note by typing</p>
                  <p className="text-gray-600 text-base">or</p>
                  <p className="text-gray-600 text-base">Drag and drop file, images, or photos</p>
                  <p className="text-gray-600 text-base">or</p>
                  <button className="text-gray-600  text-base">Browse and Select</button>
                  </div>
                </div>
              </div>
             
            </div>
             {/* Event Monitor */}
            <div className="bg-white w-full rounded-lg shadow-sm mb-4">
              <div className="envir-field flex border-b border-gray-100 items-center justify-between p-4">
                <h3 className="font-bold text-[#4D4D4D] text-lg">Event Monitor</h3>
                <div className="flex items-center gap-[20px]">
                  <button className="text-lg flex justify-between items-center gap-6 text-[#4D4D4D]  bg-gray-200 py-2 px-6 rounded-lg"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M8.88889 20V13.3333H11.1111V15.5555H20V17.7778H11.1111V20H8.88889ZM0 17.7778V15.5555H6.66666V17.7778H0ZM4.44444 13.3333V11.1111H0V8.88885H4.44444V6.66663H6.66666V13.3333H4.44444ZM8.88889 11.1111V8.88885H20V11.1111H8.88889ZM13.3333 6.66663V0H15.5556V2.22223H20V4.4444H15.5556V6.66663H13.3333ZM0 4.4444V2.22223H11.1111V4.4444H0Z" fill="#4D4D4D"/>
</svg>
 Filter</button>
                  <button className="p-1 text-[#4D4D4D] hover:bg-gray-100 rounded transition">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.22222 17.7778H3.80556L14.6667 6.91663L13.0833 5.33337L2.22222 16.1945V17.7778ZM0 20V15.2778L14.6667 0.638916C14.8889 0.435181 15.1342 0.277832 15.4028 0.166687C15.6713 0.055542 15.9537 0 16.25 0C16.5463 0 16.8333 0.055542 17.1111 0.166687C17.3889 0.277832 17.6296 0.444458 17.8333 0.666626L19.3611 2.22223C19.5833 2.4259 19.7454 2.66669 19.8472 2.94446C19.9491 3.22223 20 3.5 20 3.77777C20 4.0741 19.9491 4.35651 19.8472 4.625C19.7454 4.89355 19.5833 5.13892 19.3611 5.36108L4.72221 20H0ZM13.8611 6.13892L13.0833 5.33337L14.6667 6.91663L13.8611 6.13892Z" fill="#888888"/>
                    </svg>
                  </button>
                </div>
              </div>
              <div className="p-[25px]">
              <table className="w-full text-base text-gray-600">
                <thead className="border-b border-gray-100 text-gray-600">
                  <tr>
                    <th className="text-left pb-3 px-3 font-semibold text-gray-700">#</th>
                    <th className="text-left pb-3 px-3 font-semibold text-gray-700">Name</th>
                    <th className="text-left pb-3 px-3 font-semibold text-gray-700">Type</th>
                    <th className="text-left pb-3 px-3 font-semibold text-gray-700">Status</th>
                    <th className="text-left pb-3 px-3 font-semibold text-gray-700">Event Time</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100 text-gray-600">
                    <td className="py-3 px-3">1</td>
                    <td className="py-3 px-3">Low Power</td>
                    <td className="py-3 px-3">Event</td>
                    <td className="py-3 px-3"><span className="text-red-600">●</span> Trigger</td>
                    <td className="py-3 px-3">2026-01-08 19:00:00</td>
                  </tr>
                  <tr className="border-b border-gray-100 text-gray-600">
                    <td className="py-3 px-3">2</td>
                    <td className="py-3 px-3">Low Dissolved Oxygen</td>
                    <td className="py-3 px-3">Event</td>
                    <td className="py-3 px-3"><span className="text-[#56B565]">● </span> Trigger</td>
                    <td className="py-3 px-3">2025-07-29 14:19:16</td>
                  </tr>
                  <tr className="border-b border-gray-100 text-gray-600">
                    <td className="py-3 px-3">3</td>
                    <td className="py-3 px-3">High Turbidity</td>
                    <td className="py-3 px-3">Event</td>
                    <td className="py-3 px-3"><span className="text-[#56B565]">● </span> Normal</td>
                    <td className="py-3 px-3">2025-12-29 03:00:00</td>
                  </tr>
                </tbody>
              </table>
              </div>
              </div>
              </div>
              {/* Support our Project */}
              <div className="supportour w-[32%]">
              <div className="bg-white rounded-lg shadow-sm">
                <div className="flex border-b border-gray-100 items-center justify-between p-4">
                  <h3 className="font-bold text-[#4D4D4D] text-lg">Support our Project</h3>
                </div>
                <div className=" flex items-center flex-wrap justify-center overflow-hidden p-[25px]">
                <button className="text-[#0866AB] block mr-auto  text-base underline mb-6">Donate Now</button>
                  <img src="/images/supportour.png" alt="Project" />
                </div>
                </div>
                {/* Project Description and Disclaimer Row */}
            <div className=" mt-4">
              {/* Project Description */}
              <div className="bg-white rounded-lg shadow-sm">
                <div className="flex border-b border-gray-100 items-center justify-between p-4">
                  <h3 className="font-bold text-[#4D4D4D] text-lg">Project Description</h3>
                  <button className="p-1 text-[#4D4D4D] hover:bg-gray-100 rounded transition">
                     <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.22222 17.7778H3.80556L14.6667 6.91663L13.0833 5.33337L2.22222 16.1945V17.7778ZM0 20V15.2778L14.6667 0.638916C14.8889 0.435181 15.1342 0.277832 15.4028 0.166687C15.6713 0.055542 15.9537 0 16.25 0C16.5463 0 16.8333 0.055542 17.1111 0.166687C17.3889 0.277832 17.6296 0.444458 17.8333 0.666626L19.3611 2.22223C19.5833 2.4259 19.7454 2.66669 19.8472 2.94446C19.9491 3.22223 20 3.5 20 3.77777C20 4.0741 19.9491 4.35651 19.8472 4.625C19.7454 4.89355 19.5833 5.13892 19.3611 5.36108L4.72221 20H0ZM13.8611 6.13892L13.0833 5.33337L14.6667 6.91663L13.8611 6.13892Z" fill="#888888"/>
                    </svg>
                  </button>
                </div>
                <div className="text-gray-600 text-base leading-relaxed p-[25px]">
                  <p className="text-gray-400">Write project description here...</p>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="bg-white rounded-lg shadow-sm mt-4">
                <div className="flex border-b border-gray-100 items-center justify-between p-4">
                  <h3 className="font-bold text-[#4D4D4D] text-lg">Disclaimer</h3>
                  <button className="p-1 text-gray-600 hover:bg-gray-100 rounded transition">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.22222 17.7778H3.80556L14.6667 6.91663L13.0833 5.33337L2.22222 16.1945V17.7778ZM0 20V15.2778L14.6667 0.638916C14.8889 0.435181 15.1342 0.277832 15.4028 0.166687C15.6713 0.055542 15.9537 0 16.25 0C16.5463 0 16.8333 0.055542 17.1111 0.166687C17.3889 0.277832 17.6296 0.444458 17.8333 0.666626L19.3611 2.22223C19.5833 2.4259 19.7454 2.66669 19.8472 2.94446C19.9491 3.22223 20 3.5 20 3.77777C20 4.0741 19.9491 4.35651 19.8472 4.625C19.7454 4.89355 19.5833 5.13892 19.3611 5.36108L4.72221 20H0ZM13.8611 6.13892L13.0833 5.33337L14.6667 6.91663L13.8611 6.13892Z" fill="#888888"/>
                    </svg>
                  </button>
                </div>
                <div className="text-gray-600 text-base leading-relaxed space-y-2 p-[25px]">
                  <p>
                    Uncertainty and potential for error can be associated with environmental monitoring data. Data users are cautioned to consider carefully the provisional nature of the information before using it for decisions that concern personal or public safety or the conduct of business that involves substantial monetary or operational consequences. No warranty, express or implied, is given as to the accuracy, reliability, utility or completeness of the data hosted on this datacenter, and this organization shall not be held liable for improper or incorrect use of the data described or information contained on these pages.
                  </p>
                </div>
              </div>
            </div>
              </div>
            </div>

            {/* Template Dashboard Grid */}
            {currentProject?.id && (
              <div className="mt-4">
                <TemplateDashboardGrid projectId={currentProject.id} />
              </div>
            )}
                  </>
                )}

                {/* Parameter Comparison Content — shared DynamicGraphCard */}
                {(templates[currentMenuIndex]?.template_type === 'parameter_comparison' || templates[currentMenuIndex]?.name === 'Parameter Comparison') && (
                  <DynamicGraphCard
                    title="Graph"
                    selectedParameters={selectedParameters}
                    dateRange={dateRangeGraph}
                    timezone="UTC"
                    activePeriod={activePeriod}
                    onPeriodClick={handlePeriodClick}
                    onDateChange={(field, value) => { setActivePeriod(''); setDateRangeGraph(prev => ({ ...prev, [field]: value })) }}
                    storageKey="wq_param_comparison_graph"
                    colors={PARAM_CHART_COLORS}
                    emptyMessage="Select one or more parameters from the sidebar to compare them here."
                  />
                )}

                {/* Project Map: render whichever widgets the backend has
                    assigned to this template — same pattern as Overview.
                    The template ships with a single GPS widget which, on
                    the React side, renders the full Leaflet map + Edit
                    button + Map Editor popup (see GPSWidget in
                    DashboardWidgets.jsx). If an admin assigns additional
                    widgets to this template later they'll appear here
                    without any frontend change. */}
                {templates[currentMenuIndex]?.name === 'Project Map' && (
                  <>
                    {currentProject?.id ? (
                      <TemplateDashboardGrid
                        ref={gridRef}
                        projectId={currentProject.id}
                        templateId={templates[currentMenuIndex]?.id}
                        isEditing={isCustomizingDashboard}
                        droppingWidget={draggingWidget}
                        onDropComplete={() => { setDraggingWidget(null); setAddWidgetModalHidden(false); setShowAddWidgetsModal(false) }}
                        containerWidth={gridContainerWidth}
                      />
                    ) : (
                      <div className="flex items-center justify-center py-20 text-gray-400 text-sm">
                        Select a project to view the dashboard
                      </div>
                    )}
                  </>
                )}

                {/* Current Conditions Content — dynamic (Option A: latest value per parameter) */}
                {(templates[currentMenuIndex]?.template_type === 'current_conditions' || templates[currentMenuIndex]?.name === 'Current Conditions') && (
                  <CurrentConditionsView
                    projectId={currentProject?.id}
                    sites={projectSites}
                    selectedParameters={selectedParameters}
                    onTogglePanel={toggleCheckbox}
                    dateRange={dateRangeGraph}
                    timezone="UTC"
                    projectImage={projectImage}
                  />
                )}

                {/* System Performance Content — dynamic */}
                {(templates[currentMenuIndex]?.template_type === 'system_performance' || templates[currentMenuIndex]?.name === 'System Performance') && (
                  <SystemPerformanceView
                    sites={projectSites}
                    selectedParameters={selectedParameters}
                    dateRange={dateRangeGraph}
                    timezone="UTC"
                    activePeriod={activePeriod}
                    onPeriodClick={handlePeriodClick}
                    onDateChange={(field, value) => { setActivePeriod(''); setDateRangeGraph(prev => ({ ...prev, [field]: value })) }}
                    projectId={currentProject?.id}
                  />
                )}

                {/* All Data — flat grid of cards, one per selectedParameter */}
                {templates[currentMenuIndex]?.name === 'All Data' && (
                  <AllDataView
                    selectedParameters={selectedParameters}
                    projectId={currentProject?.id}
                    paramConfigVersion={paramConfigVersion}
                  />
                )}

            {/* NexSens website banner — shown at the bottom of every default
                (predefined) dashboard, matching the XD. Links to nexsens.com. */}
            {(() => {
              const t = templates[currentMenuIndex]
              const DEFAULT_TYPES = ['overview', 'weather_station', 'parameter_comparison', 'current_conditions', 'system_performance', 'all_data']
              const DEFAULT_NAMES = ['Overview', 'Weather Station', 'Parameter Comparison', 'Current Conditions', 'System Performance', 'All Data']
              const isDefault = t && (DEFAULT_TYPES.includes(t.template_type) || DEFAULT_NAMES.includes(t.name))
              return isDefault ? <NexSensBanner /> : null
            })()}

              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar */}
        
      </div>

      {/* Customize Data Report Modal */}
      {showCustomizeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl mx-4">
            {/* Modal Header with Apply/Cancel */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-800">Customize Data Report</h2>
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => setShowCustomizeModal(false)}
                  className="px-5 py-1.5 text-white rounded text-sm  hover:opacity-90 transition"
                  style={{ backgroundColor: '#358ECD' }}
                >
                  Apply
                </button>
                <button 
                  onClick={() => setShowCustomizeModal(false)}
                  className="px-5 py-1.5 text-gray-600 text-sm  hover:text-gray-800 transition"
                >
                  Cancel
                </button>
              </div>
            </div>

            {/* Modal Tabs */}
            <div className="flex border-b border-gray-200 px-6 overflow-x-auto">
              {['Parameters', 'Data', 'View', 'Filter', 'Header/Footer', 'PDF Format', 'Settings'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setCustomizeTab(tab)}
                  className={`px-4 py-3 text-sm  border-b-2 transition whitespace-nowrap ${
                    customizeTab === tab
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-900'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto" style={{ minHeight: '400px', maxHeight: '60vh' }}>
              {customizeTab === 'Parameters' && (
                <div className="flex gap-8">
                  {/* Left - Select Parameters */}
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 mb-4">Select Parameters</h3>
                    
                    {/* Land Based Station */}
                    <div className="mb-5">
                      <h4 className=" text-gray-800 mb-2">Land Based Station</h4>
                      <div className="ml-4 space-y-1">
                        <div className="text-sm text-gray-600 mb-1">X3-4G-01080 Data Logger</div>
                        <label className="flex items-center gap-2 text-sm text-gray-600 ml-4 py-0.5">
                          <input type="checkbox" className="w-4 h-4 rounded border-gray-300" />
                          <span>Gill MaxiMet GMX200</span>
                          <ChevronDown className="w-3 h-3 text-gray-400" />
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-600 ml-4 py-0.5">
                          <input type="checkbox" className="w-4 h-4 rounded border-gray-300" />
                          <span>Solinst Levelogger 5</span>
                          <ChevronDown className="w-3 h-3 text-gray-400" />
                        </label>
                      </div>
                    </div>

                    {/* Pond Buoy */}
                    <div className="mb-5">
                      <h4 className=" text-gray-800 mb-2">Pond Buoy</h4>
                      <div className="ml-4 space-y-1">
                        <div className="text-sm text-gray-600 mb-1">X3-4G-01078 Data Logger</div>
                        <label className="flex items-center gap-2 text-sm text-gray-600 ml-4 py-0.5">
                          <input type="checkbox" className="w-4 h-4 rounded border-gray-300" />
                          <span>YSI EXO2 Sonde</span>
                          <ChevronDown className="w-3 h-3 text-gray-400" />
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-600 ml-4 py-0.5">
                          <input type="checkbox" className="w-4 h-4 rounded border-gray-300" />
                          <span>NexSens NX260</span>
                          <ChevronDown className="w-3 h-3 text-gray-400 rotate-180" />
                        </label>
                        {/* Expanded NexSens NX260 sub-items */}
                        <div className="ml-12 space-y-1">
                          <label className="flex items-center gap-2 text-sm text-gray-600 py-0.5">
                            <input type="checkbox" className="w-4 h-4 rounded border-gray-300" />
                            <span>NX Temperature</span>
                          </label>
                          <label className="flex items-center gap-2 text-sm text-gray-600 py-0.5">
                            <input type="checkbox" className="w-4 h-4 rounded border-gray-300" />
                            <span>NX DO</span>
                          </label>
                          <label className="flex items-center gap-2 text-sm text-gray-600 py-0.5">
                            <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                            <span>NX Turbidity</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Weather Station */}
                    <div className="mb-5">
                      <h4 className=" text-gray-800 mb-2">Weather Station</h4>
                      <div className="ml-4 space-y-1">
                        <div className="text-sm text-gray-600 mb-1">X2-C-VZ4G-01284 Data Logger</div>
                        <label className="flex items-center gap-2 text-sm text-gray-600 ml-4 py-0.5">
                          <input type="checkbox" className="w-4 h-4 rounded border-gray-300" />
                          <span>YSI H-3401</span>
                          <ChevronDown className="w-3 h-3 text-gray-400" />
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Right - Selected Parameters */}
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 mb-4">Selected Parameters</h3>
                    <div className="border border-gray-200 rounded p-4 min-h-[300px]">
                      <p className="text-sm text-gray-700">Pond Buoy: NX Turbidity</p>
                    </div>
                  </div>
                </div>
              )}
              {customizeTab === 'Data' && (
                <div>
                  {/* Date Range Section */}
                  <h3 className="font-semibold text-gray-800 mb-4">Date Range</h3>
                  <div className="space-y-4 max-w-lg">
                    <div className="flex items-center gap-4">
                      <label className="text-sm text-gray-700 w-16">Range</label>
                      <select className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500">
                        <option>Today</option>
                        <option>Yesterday</option>
                        <option>Last 7 Days</option>
                        <option>Last 30 Days</option>
                        <option>This Month</option>
                        <option>Last Month</option>
                        <option>Custom</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="text-sm text-gray-700 w-16">From</label>
                      <input 
                        type="datetime-local" 
                        defaultValue="2026-03-16T00:00" 
                        className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500" 
                      />
                      <label className="text-sm text-gray-700 w-8 text-center">To</label>
                      <input 
                        type="datetime-local" 
                        defaultValue="2026-03-16T11:59" 
                        className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500" 
                      />
                    </div>
                  </div>

                  {/* Condense Section */}
                  <h3 className="font-semibold text-gray-800 mt-8 mb-4">Condense</h3>
                  <div className="max-w-lg">
                    <div className="flex items-center gap-4">
                      <label className="text-sm text-gray-700 w-16">Mode</label>
                      <select className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500">
                        <option>None</option>
                        <option>Hourly</option>
                        <option>Daily</option>
                        <option>Weekly</option>
                        <option>Monthly</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
              {customizeTab === 'Filter' && (
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4">Filter Sites and Parameters</h3>
                  
                  {/* Search Bar */}
                  <div className="mb-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search sites, loggers, parameters"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Sites Tree Structure */}
                  <div className="border border-gray-200 rounded p-4 max-h-[400px] overflow-y-auto">
                    {getFilteredSites().length > 0 ? (
                      <div className="space-y-1">
                        {getFilteredSites().map((site) => (
                          <div key={site.id}>
                            {/* Site Header */}
                            <button
                              onClick={() => toggleMenu(site.id)}
                              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded transition"
                            >
                              {expandedMenus.includes(site.id) ? (
                                <ChevronDown className="w-4 h-4 text-gray-600" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-600" />
                              )}
                              <span className="text-sm  text-gray-800">{site.name}</span>
                            </button>

                            {/* Devices under Site */}
                            {expandedMenus.includes(site.id) && site.devices && (
                              <div className="ml-4 space-y-1">
                                {site.devices.map((device) => (
                                  <div key={device.id}>
                                    {/* Device Header */}
                                    <button
                                      onClick={() => toggleMenu(`device-${device.id}`)}
                                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded transition"
                                    >
                                      {expandedMenus.includes(`device-${device.id}`) ? (
                                        <ChevronDown className="w-4 h-4 text-gray-600" />
                                      ) : (
                                        <ChevronRight className="w-4 h-4 text-gray-600" />
                                      )}
                                      <span className="text-sm text-gray-700">{device.name}</span>
                                    </button>

                                    {/* Sensors under Device */}
                                    {expandedMenus.includes(`device-${device.id}`) && device.sensors && (
                                      <div className="ml-4 space-y-1">
                                        {device.sensors.map((sensor) => (
                                          <div key={sensor.id}>
                                            {/* Sensor Header */}
                                            <button
                                              onClick={() => toggleMenu(`sensor-${sensor.id}`)}
                                              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded transition"
                                            >
                                              {expandedMenus.includes(`sensor-${sensor.id}`) ? (
                                                <ChevronDown className="w-4 h-4 text-gray-600" />
                                              ) : (
                                                <ChevronRight className="w-4 h-4 text-gray-600" />
                                              )}
                                              <span className="text-sm text-gray-700">{sensor.name}</span>
                                            </button>

                                            {/* Parameters under Sensor */}
                                            {expandedMenus.includes(`sensor-${sensor.id}`) && sensor.parameters && (
                                              <div className="ml-4 space-y-1">
                                                {sensor.parameters.map((parameter) => (
                                                  <label key={parameter.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded transition cursor-pointer">
                                                    <input 
                                                      type="checkbox" 
                                                      checked={checkedItems[parameter.id] || false}
                                                      onChange={() => toggleCheckbox(parameter.id)}
                                                      className="w-4 h-4 rounded border-gray-300 accent-blue-600"
                                                    />
                                                    <span className="text-sm text-gray-700">{parameter.name}</span>
                                                  </label>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-sm text-gray-500">No sites or parameters found</p>
                      </div>
                    )}
                  </div>

                  {/* Filter Parameter Min/Max */}
                  <div className="mt-6">
                    <h4 className="font-semibold text-gray-800 mb-3">Filter Parameter Range</h4>
                    <div className="space-y-3 max-w-sm">
                      <div className="flex items-center gap-3">
                        <label className="text-sm text-gray-700 w-10">Min</label>
                        <input type="number" className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500" />
                      </div>
                      <div className="flex items-center gap-3">
                        <label className="text-sm text-gray-700 w-10">Max</label>
                        <input type="number" className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {customizeTab === 'View' && (
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4">Include Sections</h3>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3">
                      <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                      <span className="text-sm text-gray-700">Header</span>
                    </label>
                    <label className="flex items-center gap-3">
                      <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                      <span className="text-sm text-gray-700">Stat Table</span>
                    </label>
                    <div className="flex items-center gap-3">
                      <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                      <span className="text-sm text-gray-700">Plot</span>
                      <select className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500">
                        <option>Combine Plots</option>
                        <option>Separate Plots</option>
                      </select>
                    </div>
                    <label className="flex items-center gap-3">
                      <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                      <span className="text-sm text-gray-700">Data Table</span>
                    </label>
                  </div>
                </div>
              )}
              {customizeTab === 'Header/Footer' && (
                <div className="flex gap-12">
                  {/* Header Section */}
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 mb-4">Header</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                        <label className="text-sm text-gray-700 w-28">Top Title</label>
                        <input type="text" defaultValue="Environmental Field Station 2025" className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500" />
                      </div>
                      <div className="flex items-center gap-3">
                        <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                        <label className="text-sm text-gray-700 w-28">Report Title</label>
                        <input type="text" defaultValue="Template First Site" className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500" />
                      </div>
                      <div className="flex items-center gap-3">
                        <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                        <label className="text-sm text-gray-700 w-28">Subtitle</label>
                        <input type="text" defaultValue="March 16, 2026" className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500" />
                      </div>
                      <div className="flex items-center gap-3">
                        <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                        <label className="text-sm text-gray-700 w-28">Date Prepared</label>
                        <select className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500">
                          <option>March 16, 2026</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-3">
                        <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                        <label className="text-sm text-gray-700 w-28">Time Prepared</label>
                        <select className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500">
                          <option>12-hour</option>
                          <option>24-hour</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Footer Section */}
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 mb-4">Footer</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <input type="checkbox" defaultChecked className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                        <label className="text-sm text-gray-700 w-28">Page Number</label>
                        <input type="text" defaultValue="Page 1 of 2" className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500" />
                      </div>
                      <div className="flex items-center gap-3">
                        <input type="checkbox" className="w-4 h-4 rounded border-gray-300 accent-blue-600" />
                        <label className="text-sm text-gray-700 w-28">Extra Footer</label>
                        <input type="text" defaultValue="Extra Footer" className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-400 focus:outline-none focus:border-blue-500" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {customizeTab === 'PDF Format' && (
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4">Page Format</h3>
                  <div className="space-y-4 max-w-md">
                    <div className="flex items-center gap-4">
                      <label className="text-sm text-gray-700 w-24">Size</label>
                      <select className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500">
                        <option>Letter (8.5 x 11 in)</option>
                        <option>Legal (8.5 x 14 in)</option>
                        <option>Tabloid (11 x 17 in)</option>
                        <option>A4 (210 x 297 mm)</option>
                        <option>A3 (297 x 420 mm)</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="text-sm text-gray-700 w-24">Orientation</label>
                      <select className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500">
                        <option>Portrait</option>
                        <option>Landscape</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
              {customizeTab === 'Settings' && (
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4">Settings</h3>
                  <div className="space-y-4 max-w-md">
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Report Name</label>
                      <input type="text" defaultValue="Field Station Turbidity" className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Description</label>
                      <textarea defaultValue="Report for turbidity at the field station" rows="3" className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500 resize-none" />
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="text-sm text-gray-700 w-32">Timezone</label>
                      <select className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500">
                        <option>(UTC-4:00) America/New_York</option>
                        <option>(UTC-5:00) America/Chicago</option>
                        <option>(UTC-6:00) America/Denver</option>
                        <option>(UTC-7:00) America/Los_Angeles</option>
                        <option>(UTC+0:00) UTC</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="text-sm text-gray-700 w-32">Show Error Value</label>
                      <select className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500">
                        <option>No</option>
                        <option>Yes</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upload to Gallery Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-7xl mx-4 flex flex-col" style={{maxHeight: '85vh'}}>

            {/* Header */}
            <div className="envir-field flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="common-head25 text-lg font-bold text-[#4D4D4D]">Upload to Gallery</h2>
              <div className="flex items-center gap-4">
                <button
                  onClick={handleStartUpload}
                  disabled={uploadItems.length === 0 || uploadItems.every(i => i.done)}
                  className="bg-[#358ECD] text-white px-5 py-2 rounded-md text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Start Upload
                </button>
                <button
                  onClick={() => { setShowUploadModal(false); setUploadItems([]) }}
                  className="text-sm text-[#4D4D4D] px-3 py-2 border border-[#F1F2F4] rounded-md transition"
                >
                  Cancel
                </button>
              </div>
            </div>

            {/* Drop Zone */}
            <div
              className="mx-6 mt-5 mb-5 border-2 border-dashed border-gray-300 rounded p-8 text-center cursor-pointer hover:border-[#358ECD] transition flex-shrink-0"
              onClick={() => document.getElementById('upload-gallery-input').click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleUploadFilesSelect(e.dataTransfer.files) }}
            >
              <input
                id="upload-gallery-input"
                type="file"
                multiple
                accept="image/gif,image/png,image/jpeg,image/jpg,video/mp4,video/quicktime,video/x-flv,video/x-matroska,video/x-msvideo"
                className="hidden"
                onChange={(e) => handleUploadFilesSelect(e.target.files)}
              />
              <p className="ad-fs text-[#AFAFAF] text-sm font-bold">
                Drag Attachments or{' '}
                <span className="text-[#AFAFAF] font-bold cursor-pointer underline">Click Here to Add Files</span>
              </p>
              <p className="text-xs text-[#AFAFAF] mt-1 common-size15">Supports image files: gif, png, jpg, and jpeg</p>
              <p className="text-xs text-[#AFAFAF] common-size15">Supports video files: mp4, mov, flv, mkv, and avi</p>
              <p className="text-xs text-[#AFAFAF] common-size15">Maximum file size of 50.00 MB</p>
            </div>

            {/* Scrollable file list */}
            <div className="flex-1 overflow-y-auto px-6 pb-5 space-y-3 min-h-0">
              {uploadItems.length === 0 ? (
                <p className="text-center text-sm text-gray-400 py-4">No files added yet. Use the drop zone above.</p>
              ) : uploadItems.map(item => (
                <div key={item.id} className="p-0 pb-4 border-b border-gray-200 bg-white">
                  <div className="flex gap-4">

                  {/* Thumbnail + file info */}
                  <div className="flex-shrink-0 w-28">
                    <div className="w-28 h-24 rounded overflow-hidden bg-gray-100 flex items-center justify-center border border-gray-200">
                      {item.previewUrl
                        ? <img src={item.previewUrl} alt={item.file.name} className="w-full h-full object-cover" />
                        : <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.723v6.554a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" /></svg>
                      }
                    </div>
                    <p className="text-xs text-gray-600 mt-1.5 break-all leading-tight" title={item.file.name}>{item.file.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{(item.file.size / 1024 / 1024).toFixed(1)} MB</p>
                  </div>

                  {/* Form fields — 3-col grid with labels */}
                  <div className="flex-1 grid grid-cols-3 gap-x-4 gap-y-3 content-start">
                    {/* Row 1 labels */}
                    <div>
                      {/* Album, Title and Location are REQUIRED — the upload
                          button stays disabled until all three are set (see
                          uploadItemMissing / handleUploadItem). */}
                      <label className="common-size15 font-semibold block text-xs text-[#4D4D4D] mb-1">
                        Album <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={item.album_id}
                        onChange={(e) => setUploadItems(prev => prev.map(i => i.id === item.id ? { ...i, album_id: e.target.value, error: '' } : i))}
                        className="common-size15 w-full px-2 py-1.5 border border-[#D6D6D6] rounded-md text-[#4D4D4D text-sm focus:outline-none focus:border-[#358ECD] bg-white"
                      >
                        <option value="">Select Album</option>
                        {projectAlbums.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="common-size15 font-semibold block text-xs text-[#4D4D4D] mb-1">
                        Title <span className="text-red-500">*</span>
                      </label>
                      <SecureInput
                        type="text"
                        placeholder="Enter a title"
                        value={item.title}
                        onChange={(e) => setUploadItems(prev => prev.map(i => i.id === item.id ? { ...i, title: e.target.value, error: '' } : i))}
                        className="common-size15 w-full px-2 py-1.5 border border-[#D6D6D6] rounded-md text-[#4D4D4D text-sm focus:outline-none focus:border-[#358ECD]"
                      />
                    </div>
                    <div>
                      <label className="common-size15 font-semibold block text-xs text-[#4D4D4D] mb-1">Tags</label>
                      <SecureInput
                        type="text"
                        placeholder="Separate by comma"
                        value={item.tags}
                        onChange={(e) => setUploadItems(prev => prev.map(i => i.id === item.id ? { ...i, tags: e.target.value } : i))}
                        className="common-size15 w-full px-2 py-1.5 border border-[#D6D6D6] rounded-md text-[#4D4D4D text-sm focus:outline-none focus:border-[#358ECD]"
                      />
                    </div>
                    {/* Row 2 labels */}
                    <div>
                      <label className="common-size15 font-semibold block text-xs text-[#4D4D4D] mb-1">
                        Location <span className="text-red-500">*</span>
                      </label>
                      {/* The project's real sites — the chosen one is stored on
                          the photo and is what gives the info panel's Location
                          row and its map something to point at. */}
                      <select
                        value={item.location}
                        onChange={(e) => setUploadItems(prev => prev.map(i => i.id === item.id ? { ...i, location: e.target.value, error: '' } : i))}
                        className="common-size15 w-full px-2 py-1.5 border border-[#D6D6D6] rounded-md text-[#4D4D4D] text-sm focus:outline-none focus:border-[#358ECD] bg-white"
                      >
                        <option value="">Select Location</option>
                        {projectSites.map(site => (
                          <option key={site.id} value={site.id}>Site - {site.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="common-size15 font-semibold block text-xs text-[#4D4D4D] mb-1">Description</label>
                      <SecureInput
                        type="text"
                        value={item.description}
                        onChange={(e) => setUploadItems(prev => prev.map(i => i.id === item.id ? { ...i, description: e.target.value } : i))}
                        className="common-size15 w-full px-2 py-1.5 border border-[#D6D6D6] rounded-md text-[#4D4D4D text-sm focus:outline-none focus:border-[#358ECD]"
                      />
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-col gap-2 flex-shrink-0 justify-start pt-5">
                    <button
                      onClick={() => handleUploadItem(item.id)}
                      disabled={item.uploading || item.done || uploadItemMissing(item).length > 0}
                      title={item.done ? 'Uploaded'
                        : uploadItemMissing(item).length ? `Select ${uploadItemMissing(item).join(', ')} first`
                        : 'Upload this file'}
                      className="w-9 h-9 bg-[#358ECD] text-white rounded flex items-center justify-center hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed transition"
                    >
                      {item.uploading
                        ? <Loader className="w-4 h-4 animate-spin" />
                        : item.done
                          ? <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                          : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                      }
                    </button>
                    <button
                      onClick={() => setUploadItems(prev => prev.filter(i => i.id !== item.id))}
                      title="Remove"
                      className="w-9 h-9 bg-gray-100 text-gray-500 rounded flex items-center justify-center hover:bg-gray-200 transition"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </div>
                  </div>
                  {item.error && <p className="text-xs text-red-500 mt-3">{item.error}</p>}
                  {item.done && <p className="text-xs text-green-600 mt-3">✓ Uploaded</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Edit Album Name Modal */}
      {editAlbumModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Rename Album</h2>
            <SecureInput
              type="text"
              value={editAlbumName}
              onChange={(e) => setEditAlbumName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleEditAlbumSave() }}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#358ECD] mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <button onClick={() => setEditAlbumModal(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition">Cancel</button>
              <button
                onClick={handleEditAlbumSave}
                disabled={!editAlbumName.trim()}
                className="px-4 py-2 text-sm bg-[#358ECD] text-white rounded hover:bg-blue-600 disabled:opacity-50 transition"
              >Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Media Information Modal */}
      {showEditMediaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Edit Media Information</h2>
              <button onClick={() => { setShowEditMediaModal(false); setEditPhotoFile(null); setEditPhotoPreview(null) }} className="text-gray-400 hover:text-gray-600 transition">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">

              {/* Image preview + change */}
              <div className="flex items-start gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="w-24 h-20 rounded overflow-hidden bg-gray-200 flex-shrink-0 flex items-center justify-center">
                  {editPhotoPreview || editingPhoto?.src ? (
                    <img src={editPhotoPreview || editingPhoto?.src} alt="preview" className="w-full h-full object-cover" />
                  ) : (
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 truncate mb-1">{editPhotoFile ? editPhotoFile.name : (editingPhoto?.originalFilename || 'No file')}</p>
                  {editPhotoFile && <p className="text-xs text-green-600 mb-2">New file selected</p>}
                  <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 rounded text-xs  text-gray-600 hover:bg-gray-50 cursor-pointer transition">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                    Change Photo
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0]
                        if (f) {
                          setEditPhotoFile(f)
                          setEditPhotoPreview(URL.createObjectURL(f))
                        }
                      }}
                    />
                  </label>
                  {editPhotoFile && (
                    <button onClick={() => { setEditPhotoFile(null); setEditPhotoPreview(null) }} className="ml-2 text-xs text-red-500 hover:underline">Remove</button>
                  )}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm  text-gray-700 mb-1">Title</label>
                <SecureInput
                  type="text"
                  value={editFormData.title}
                  onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter title"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm  text-gray-700 mb-1">Description</label>
                <SecureTextarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter description"
                  rows="3"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm  text-gray-700 mb-1">Tags</label>
                <SecureInput
                  type="text"
                  value={editFormData.tags}
                  onChange={(e) => setEditFormData({ ...editFormData, tags: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Separate By Comma"
                />
              </div>

              {/* Album */}
              <div>
                <label className="block text-sm  text-gray-700 mb-1">Album</label>
                <select
                  value={editFormData.album}
                  onChange={(e) => setEditFormData({ ...editFormData, album: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select an album</option>
                  {projectAlbums.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
                </select>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm  text-gray-700 mb-1">Location</label>
                <select
                  value={editFormData.siteId}
                  onChange={(e) => setEditFormData({ ...editFormData, siteId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a Location</option>
                  {projectSites.map(site => (
                    <option key={site.id} value={site.id}>Site - {site.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => { setShowEditMediaModal(false); setEditPhotoFile(null); setEditPhotoPreview(null) }}
                className="flex-1 px-4 py-2 text-sm  text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePhotoInfo}
                className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-[#358ECD] rounded-md hover:bg-blue-600 transition"
              >
                SAVE CHANGES
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Configure Site Modal */}
      {showAddDeviceModal && (() => {
        const configureSite = modalProjectSites.find(s => s.id === selectedSiteForDevice)
        const siteDevices = configureSite?.devices || []

        // Adds a claimed device to the site/unassigned list.
        const addClaimedDevice = (backendDevice) => {
          const deviceName = backendDevice.name || backendDevice.body_tag || backendDevice.claim_code || String(backendDevice.id)
          // Check by ID or by name — catches same-name devices with different IDs (e.g. inactive duplicate)
          const alreadyUnassigned = unassignedDevices.some(d => d.id === backendDevice.id || d.name === deviceName)
          if (alreadyUnassigned) {
            setDeviceError(`"${deviceName}" is already in the Unassigned Devices list.`)
            setClaimMatches([])
            return
          }
          const assignedSite = modalProjectSites.find(s => (s.devices || []).some(d => d.id === backendDevice.id || d.name === deviceName))
          if (assignedSite) {
            setDeviceError(`"${deviceName}" is already assigned to site "${assignedSite.name}".`)
            setClaimMatches([])
            return
          }

          if (selectedSiteForDevice) {
            const deviceToAdd = { id: backendDevice.id, name: backendDevice.name || backendDevice.body_tag || backendDevice.claim_code || `Device ${backendDevice.id}`, uid: backendDevice.uid, sensors: backendDevice.sensors || [] }
            setModalProjectSites(prev => prev.map(s => s.id === selectedSiteForDevice ? { ...s, devices: [...(s.devices || []), deviceToAdd] } : s))
            setDeviceAssignments(prev => ({ ...prev, [backendDevice.id]: selectedSiteForDevice }))
            setSuccessMessage(`Device "${deviceToAdd.name}" added!`)
          } else {
            setUnassignedDevices(prev => [...prev, backendDevice])
            saveUnassignedDevicesToStorage(currentProject.id, [...unassignedDevices, backendDevice])
          }
          setNewDeviceName('')
          setClaimMatches([])
          setTimeout(() => setSuccessMessage(''), 3000)
        }

        const handleAddDevice = async () => {
          if (!newDeviceName.trim()) return
          const _devSecErr = scanInput(newDeviceName)
          if (_devSecErr) { setDeviceError(_devSecErr); return }
          setIsCreatingDevice(true)
          setDeviceError('')
          setSuccessMessage('')
          setClaimMatches([])
          try {
            const resp = await projectsApi.createDevice(currentProject.id, {
              claim_code: newDeviceName.trim(),
              site_id: selectedSiteForDevice || undefined,
            })
            if (resp.multiple && resp.devices?.length) {
              // Multiple active devices share this claim code — let user pick
              setClaimMatches(resp.devices)
            } else if (resp.already_assigned) {
              // Backend confirmed device is already in this project
              setDeviceError(resp.error || 'Device is already assigned to a site in this project.')
            } else {
              const backendDevice = resp.device || resp
              addClaimedDevice(backendDevice)
            }
          } catch (error) {
            const msg = error.response?.data?.error || error.response?.data?.message || error.response?.data?.detail || error.message || 'Error adding device'
            setDeviceError(msg)
          } finally {
            setIsCreatingDevice(false)
          }
        }

        const handleSaveChanges = async () => {
          setIsSavingDevices(true)
          setDeviceError('')
          setSuccessMessage('')
          try {
            const assignmentEntries = Object.entries(deviceAssignments)
            if (assignmentEntries.length > 0) {
              await Promise.all(assignmentEntries.map(([deviceId, siteId]) =>
                projectsApi.assignDeviceToSite(parseInt(deviceId), siteId ? parseInt(siteId) : null)
              ))
              const assignedIds = new Set(assignmentEntries.map(([id]) => id))
              const updatedUnassigned = unassignedDevices.filter(d => !assignedIds.has(String(d.id)))
              setUnassignedDevices(updatedUnassigned)
              saveUnassignedDevicesToStorage(currentProject.id, updatedUnassigned)
              setDeviceAssignments({})
              try {
                const refreshedSites = await projectsApi.fetchProjectSites(currentProject.id)
                setProjectSites(refreshedSites || [])
              } catch {}
              setSuccessMessage(`Saved ${assignmentEntries.length} change${assignmentEntries.length !== 1 ? 's' : ''}!`)
              setTimeout(() => { setShowAddDeviceModal(false); setSuccessMessage('') }, 1500)
            } else {
              setShowAddDeviceModal(false)
            }
          } catch (error) {
            setDeviceError('Error saving changes: ' + error.message)
          } finally {
            setIsSavingDevices(false)
          }
        }

        return (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[10000]">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 flex flex-col" style={{ maxHeight: '85vh' }}>
              {/* Header */}
              <div className="px-6 pt-5 border-b pb-4 flex items-center justify-between flex-shrink-0 mb-3">
                <h2 className="common-head25 text-lg font-bold text-[#4D4D4D]">Configure {configureSite?.name || 'Site'}</h2>
                <button
                  onClick={() => { setShowAddDeviceModal(false); setDeviceError(''); setSuccessMessage(''); setClaimMatches([]) }}
                  className="text-gray-400 hover:text-gray-600 transition p-1 rounded hover:bg-gray-100"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>

              {/* Tabs */}
              <div className="px-6 flex gap-0 border-gray-200 flex-shrink-0 mb-4">
                {['Devices', 'Sensors'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setConfigureModalTab(tab)}
                    className={`common-size16 font-semibold px-4 py-2 text-sm  border-b-4 transition -mb-px ${
                      configureModalTab === tab
                        ? 'border-[#358ECD] text-[#4D4D4D]'
                        : 'border-transparent text-[#4D4D4D]'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Messages */}
              {successMessage && (
                <div className="mx-6 mt-3 px-3 py-2 bg-green-50 border border-green-200 rounded text-sm text-green-700">{successMessage}</div>
              )}
              {deviceError && (
                <div className="mx-6 mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">{deviceError}</div>
              )}

              {/* Body */}
              <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
                {configureModalTab === 'Devices' && (
                  <>
                    {/* Multiple-device picker — shown when a claim code matches more than one active device */}
                    {claimMatches.length > 0 && (
                      <div className="mb-4 border border-yellow-300 rounded bg-yellow-50 p-3">
                        <p className="text-sm  text-yellow-800 mb-2">
                          Multiple devices share this claim code. Select the correct one:
                        </p>
                        <div className="space-y-1">
                          {claimMatches.map(d => (
                            <button
                              key={d.id}
                              onClick={() => addClaimedDevice(d)}
                              className="w-full text-left px-3 py-2 rounded border border-yellow-200 bg-white hover:bg-yellow-100 text-sm text-gray-800 transition"
                            >
                              <span className="">{d.name || d.body_tag || d.claim_code}</span>
                              {d.body_tag && <span className="ml-2 text-xs text-gray-500">Body Tag: {d.body_tag}</span>}
                              <span className="ml-2 text-xs text-gray-400">ID: {d.id}</span>
                            </button>
                          ))}
                        </div>
                        <button
                          onClick={() => { setClaimMatches([]); setNewDeviceName('') }}
                          className="mt-2 text-xs text-gray-500 hover:text-gray-700 underline"
                        >
                          Cancel
                        </button>
                      </div>
                    )}

                    {/* Add New Device */}
                    <div className="mb-4">
                      <p className="text-sm common-size16 font-bold text-[#4D4D4D] mb-2">Add New Device</p>
                      <div className="flex gap-2">
                        <div className="w-half">
                            <SecureInput
                              type="text"
                              value={newDeviceName}
                              onChange={(e) => { setNewDeviceName(e.target.value); setDeviceError('') }}
                              onKeyDown={(e) => e.key === 'Enter' && handleAddDevice()}
                              placeholder="Claim Code"
                              disabled={isCreatingDevice}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#358ECD] focus:border-transparent"
                            />
                        </div>
                        <button
                          onClick={handleAddDevice}
                          disabled={!newDeviceName.trim() || isCreatingDevice}
                          className="px-4 py-2 border border-[#358ECD] text-[#358ECD] text-xs font-bold rounded hover:bg-[#EBF5FB] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                        >
                          {isCreatingDevice && <Loader className="w-3.5 h-3.5 animate-spin" />}
                          ADD DEVICE
                        </button>
                      </div>
                    </div>

                    {/* Drag instruction */}
                    <div className="common-size16 flex items-center justify-center gap-1.5 mb-4 text-sm text-[#4D4D4D] mt-4">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Drag Device Names to Site
                    </div>

                    {/* Two columns: Unassigned + Site */}
                    <div className="grid grid-cols-1 gap-3">
                      {/* Unassigned Devices */}
                      <div>
                        <p className="common-size15 text-xs font-semibold text-[#4D4D4D] mb-2">Unassigned Devices</p>
                        <div
                          className="common-size15 border border-gray-200 rounded overflow-hidden mb-2"
                          style={{ minHeight: '180px', maxHeight: '260px', overflowY: 'auto' }}
                          onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.background = '#EBF5FB' }}
                          onDragLeave={(e) => { e.currentTarget.style.background = '' }}
                          onDrop={(e) => {
                            e.preventDefault()
                            e.currentTarget.style.background = ''
                            if (!draggedDevice || dragSource === 'unassigned') return
                            const deviceToMove = { id: draggedDevice.id, name: draggedDevice.name || `Device ${draggedDevice.id}`, uid: draggedDevice.uid, sensors: draggedDevice.sensors || [] }
                            setModalProjectSites(prev => prev.map(site =>
                              site.devices?.some(d => d.id === draggedDevice.id)
                                ? { ...site, devices: site.devices.filter(d => d.id !== draggedDevice.id) }
                                : site
                            ))
                            setUnassignedDevices(prev => prev.some(d => d.id === deviceToMove.id) ? prev : [...prev, deviceToMove])
                            setDeviceAssignments(prev => ({ ...prev, [draggedDevice.id]: null }))
                            setDraggedDevice(null); setDragSource(null)
                          }}
                        >
                          {unassignedDevices.length === 0 ? (
                            <p className="common-size15 px-3 py-4 text-xs text-[#4D4D4D] text-center">No unassigned devices</p>
                          ) : (
                            unassignedDevices.map(device => (
                              <div
                                key={device.id || `u-${Math.random()}`}
                                draggable
                                onDragStart={() => { setDraggedDevice(device); setDragSource('unassigned') }}
                                onDragEnd={() => { setDraggedDevice(null); setDragSource(null) }}
                                className={`common-size15 px-3 py-2 text-xs border-b border-gray-100 last:border-b-0 cursor-move select-none ${draggedDevice?.id === device.id ? 'opacity-40 bg-blue-50' : 'hover:bg-gray-50'}`}
                              >
                                <span className="">{device.name || device.uid || `Device #${device.id}`}</span>
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      {/* Site Devices */}
                      <div>
                        <p className="common-size15 text-xs font-semibold text-[#4D4D4D] mb-2">Site: {configureSite?.name}</p>
                        <div
                          className="common-size15 border border-gray-200 rounded overflow-hidden"
                          style={{ minHeight: '180px', maxHeight: '260px', overflowY: 'auto' }}
                          onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.background = '#EBF5FB' }}
                          onDragLeave={(e) => { e.currentTarget.style.background = '' }}
                          onDrop={(e) => {
                            e.preventDefault()
                            e.currentTarget.style.background = ''
                            if (!draggedDevice || dragSource === selectedSiteForDevice || !selectedSiteForDevice) return
                            const deviceToMove = { id: draggedDevice.id, name: draggedDevice.name || `Device ${draggedDevice.id}`, uid: draggedDevice.uid, sensors: draggedDevice.sensors || [] }
                            if (dragSource === 'unassigned') {
                              setModalProjectSites(prev => prev.map(s => s.id === selectedSiteForDevice ? { ...s, devices: [...(s.devices || []), deviceToMove] } : s))
                              setUnassignedDevices(prev => prev.filter(d => d.id !== draggedDevice.id))
                            } else {
                              setModalProjectSites(prev => prev.map(site => {
                                if (site.id === dragSource) return { ...site, devices: site.devices.filter(d => d.id !== draggedDevice.id) }
                                if (site.id === selectedSiteForDevice) return { ...site, devices: [...(site.devices || []), deviceToMove] }
                                return site
                              }))
                            }
                            setDeviceAssignments(prev => ({ ...prev, [draggedDevice.id]: selectedSiteForDevice }))
                            setDraggedDevice(null); setDragSource(null)
                          }}
                        >
                          {siteDevices.length === 0 ? (
                            <p className="common-size15 px-3 py-4 text-xs text-[#4D4D4D] text-center">No devices assigned</p>
                          ) : (
                            siteDevices.map(device => (
                              <div
                                key={device.id}
                                draggable
                                onDragStart={() => { setDraggedDevice(device); setDragSource(selectedSiteForDevice) }}
                                onDragEnd={() => { setDraggedDevice(null); setDragSource(null) }}
                                className={`common-size15 px-3 py-2 text-xs border-b border-gray-100 last:border-b-0 cursor-move select-none ${draggedDevice?.id === device.id ? 'opacity-40 bg-blue-50' : 'hover:bg-gray-50'}`}
                              >
                                {device.name}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {configureModalTab === 'Sensors' && (() => {
                  const siteDeviceList = configureSite?.devices || []
                  const activeSensorDevice = sensorSelectedDevice || siteDeviceList[0]?.name || ''

                  // ── Add Sensor Profile sub-form (XD design) ──
                  if (showSensorProfileForm) {
                    const selSet = new Set(sensorSelectedParams)
                    return (
                      <div className="space-y-4">
                        {/* Title */}
                        <div className="text-sm  text-gray-800">
                          Add SDI-12 Sensor Profile to{' '}
                          <span className="text-[#358ECD] font-semibold">{activeSensorDevice || 'Device'}</span>
                        </div>

                        {/* Form grid */}
                        <div className="flex gap-4">
                          <div className="flex-1 grid grid-cols-2 gap-x-4 gap-y-3">
                            {/* Manufacturer */}
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Manufacturer</label>
                              <div className="relative">
                                <select value={sensorFormManufacturer} onChange={e => setSensorFormManufacturer(e.target.value)}
                                  className="w-full appearance-none px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#358ECD] pr-7">
                                  {['Sea-Bird','YSI','Solinst','Onset','Campbell','In-Situ','NexSens'].map(m => <option key={m}>{m}</option>)}
                                </select>
                                <ChevronDown className="absolute right-1.5 top-2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                              </div>
                            </div>
                            {/* Model */}
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Model</label>
                              <div className="relative">
                                <select value={sensorFormModel} onChange={e => setSensorFormModel(e.target.value)}
                                  className="w-full appearance-none px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#358ECD] pr-7">
                                  {['HydroCAT SDI12','SBE 37','SBE 39','EXO2','EXO1'].map(m => <option key={m}>{m}</option>)}
                                </select>
                                <ChevronDown className="absolute right-1.5 top-2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                              </div>
                            </div>
                            {/* SDI-12 Address */}
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">SDI-12 Address</label>
                              <div className="relative">
                                <select value={sensorFormAddress} onChange={e => setSensorFormAddress(e.target.value)}
                                  className="w-full appearance-none px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#358ECD] pr-7">
                                  {['0','1','2','3','4','5','6','7'].map(a => <option key={a}>{a}</option>)}
                                </select>
                                <ChevronDown className="absolute right-1.5 top-2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                              </div>
                            </div>
                            {/* Meas Cmd */}
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Meas Cmd</label>
                              <div className="relative">
                                <select value={sensorFormMeasCmd} onChange={e => setSensorFormMeasCmd(e.target.value)}
                                  className="w-full appearance-none px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#358ECD] pr-7">
                                  {['M','MC','M1','M2','M3','C','CC'].map(c => <option key={c}>{c}</option>)}
                                </select>
                                <ChevronDown className="absolute right-1.5 top-2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                              </div>
                            </div>
                            {/* Port */}
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Port</label>
                              <div className="relative">
                                <select value={sensorFormPort} onChange={e => setSensorFormPort(e.target.value)}
                                  className="w-full appearance-none px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#358ECD] pr-7">
                                  {['P1','P2','P3','P4'].map(p => <option key={p}>{p}</option>)}
                                </select>
                                <ChevronDown className="absolute right-1.5 top-2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                              </div>
                            </div>
                            {/* Power */}
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Power</label>
                              <div className="relative">
                                <select value={sensorFormPower} onChange={e => setSensorFormPower(e.target.value)}
                                  className="w-full appearance-none px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#358ECD] pr-7">
                                  {['Switch','Always On','Off'].map(p => <option key={p}>{p}</option>)}
                                </select>
                                <ChevronDown className="absolute right-1.5 top-2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                              </div>
                            </div>
                            {/* Warmup */}
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Warmup (sec)</label>
                              <div className="relative">
                                <select value={sensorFormWarmup} onChange={e => setSensorFormWarmup(e.target.value)}
                                  className="w-full appearance-none px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#358ECD] pr-7">
                                  {['0','5','10','15','30','60','120'].map(w => <option key={w}>{w}</option>)}
                                </select>
                                <ChevronDown className="absolute right-1.5 top-2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                              </div>
                            </div>
                          </div>
                          {/* Sensor image placeholder */}
                          <div className="w-28 flex-shrink-0 border border-gray-200 rounded flex items-center justify-center bg-gray-50">
                            <div className="text-center text-gray-300 p-2">
                              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" className="mx-auto mb-1">
                                <rect x="2" y="2" width="20" height="20" rx="2" stroke="#ccc" strokeWidth="1.5"/>
                                <circle cx="12" cy="10" r="3" stroke="#ccc" strokeWidth="1.5"/>
                                <path d="M6 18c0-3.314 2.686-4 6-4s6 .686 6 4" stroke="#ccc" strokeWidth="1.5" strokeLinecap="round"/>
                              </svg>
                              <span className="text-xs">Sensor Image</span>
                            </div>
                          </div>
                        </div>

                        {/* Drag instruction */}
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                            <path d="M18 11V6.5a2.5 2.5 0 0 0-5 0V12m-5 0V4.5a2.5 2.5 0 0 1 5 0V12m0 0v-2m0 2a2.5 2.5 0 0 1 5 0v1.5" stroke="#aaa" strokeWidth="1.5" strokeLinecap="round"/>
                            <path d="M3 12.5a2.5 2.5 0 0 1 5 0V17a7 7 0 0 0 14 0v-2.5" stroke="#aaa" strokeWidth="1.5" strokeLinecap="round"/>
                          </svg>
                          Drag Parameters to Select, Deselect, and Reorder
                        </div>

                        {/* Available / Selected parameters */}
                        <div className="grid grid-cols-2 gap-3">
                          {/* Available */}
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-semibold text-gray-600">Available Parameters</span>
                              <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
                                <input type="checkbox" checked={sensorFormShowAll} onChange={e => setSensorFormShowAll(e.target.checked)} className="w-3 h-3" />
                                Show All
                              </label>
                            </div>
                            <div
                              className="border border-gray-300 rounded overflow-hidden"
                              style={{ minHeight: '120px', maxHeight: '160px', overflowY: 'auto' }}
                              onDragOver={e => e.preventDefault()}
                              onDrop={e => {
                                e.preventDefault()
                                if (!draggedSensorParam || draggedSensorParamSource !== 'selected') return
                                setSensorSelectedParams(prev => prev.filter(p => p !== draggedSensorParam))
                                setSensorAvailableParams(prev => prev.includes(draggedSensorParam) ? prev : [...prev, draggedSensorParam])
                                setDraggedSensorParam(null); setDraggedSensorParamSource(null)
                              }}
                            >
                              {sensorAvailableParams.map(param => (
                                <div
                                  key={param}
                                  draggable
                                  onDragStart={() => { setDraggedSensorParam(param); setDraggedSensorParamSource('available') }}
                                  onDragEnd={() => { setDraggedSensorParam(null); setDraggedSensorParamSource(null) }}
                                  onClick={() => {
                                    if (selSet.has(param)) return
                                    setSensorSelectedParams(prev => [...prev, param])
                                    setSensorAvailableParams(prev => prev.filter(p => p !== param))
                                  }}
                                  className={`px-3 py-1.5 text-xs cursor-pointer border-b border-gray-100 last:border-b-0 select-none transition ${
                                    draggedSensorParam === param ? 'opacity-40' : 'hover:bg-[#EBF5FB]'
                                  } ${selSet.has(param) ? 'bg-[#358ECD] text-white' : 'text-gray-700'}`}
                                >
                                  {param}
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Selected */}
                          <div>
                            <span className="text-xs font-semibold text-gray-600 block mb-1">Selected Parameters</span>
                            <div
                              className="border border-gray-300 rounded overflow-hidden"
                              style={{ minHeight: '120px', maxHeight: '160px', overflowY: 'auto' }}
                              onDragOver={e => e.preventDefault()}
                              onDrop={e => {
                                e.preventDefault()
                                if (!draggedSensorParam || draggedSensorParamSource !== 'available') return
                                setSensorAvailableParams(prev => prev.filter(p => p !== draggedSensorParam))
                                setSensorSelectedParams(prev => prev.includes(draggedSensorParam) ? prev : [...prev, draggedSensorParam])
                                setDraggedSensorParam(null); setDraggedSensorParamSource(null)
                              }}
                            >
                              {sensorSelectedParams.length === 0 ? (
                                <p className="px-3 py-4 text-xs text-gray-400 text-center">Drop parameters here</p>
                              ) : (
                                sensorSelectedParams.map(param => (
                                  <div
                                    key={param}
                                    draggable
                                    onDragStart={() => { setDraggedSensorParam(param); setDraggedSensorParamSource('selected') }}
                                    onDragEnd={() => { setDraggedSensorParam(null); setDraggedSensorParamSource(null) }}
                                    onClick={() => {
                                      setSensorSelectedParams(prev => prev.filter(p => p !== param))
                                      setSensorAvailableParams(prev => prev.includes(param) ? prev : [...prev, param])
                                    }}
                                    className={`px-3 py-1.5 text-xs cursor-pointer border-b border-gray-100 last:border-b-0 select-none text-gray-700 transition ${draggedSensorParam === param ? 'opacity-40' : 'hover:bg-red-50'}`}
                                  >
                                    {param}
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Temperature unit */}
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
                            <input type="radio" name="tempUnit" value="C" checked={sensorFormTempUnit === 'C'} onChange={() => setSensorFormTempUnit('C')} className="w-3.5 h-3.5 accent-[#358ECD]" />
                            C
                          </label>
                          <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
                            <input type="radio" name="tempUnit" value="F" checked={sensorFormTempUnit === 'F'} onChange={() => setSensorFormTempUnit('F')} className="w-3.5 h-3.5 accent-[#358ECD]" />
                            F
                          </label>
                        </div>

                        {/* Form footer buttons */}
                        <div className="flex justify-end gap-2 pt-1 border-t border-gray-100">
                          <button
                            onClick={() => setShowSensorProfileForm(false)}
                            className="px-5 py-1.5 border border-gray-300 text-gray-600 text-sm rounded hover:bg-gray-50 transition"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => {
                              const newProfile = {
                                id: Date.now(),
                                name: `${sensorFormManufacturer} ${sensorFormModel}`,
                                manufacturer: sensorFormManufacturer,
                                model: sensorFormModel,
                                address: sensorFormAddress,
                                measCmd: sensorFormMeasCmd,
                                port: sensorFormPort,
                                power: sensorFormPower,
                                warmup: sensorFormWarmup,
                                tempUnit: sensorFormTempUnit,
                                parameters: sensorSelectedParams,
                              }
                              setSensorProfiles(prev => [...prev, newProfile])
                              setSelectedSensorProfile(newProfile)
                              setSensorProfileName(newProfile.name)
                              setShowSensorProfileForm(false)
                            }}
                            className="px-5 py-1.5 bg-[#358ECD] hover:bg-[#2d7ab5] text-white text-sm font-semibold rounded transition"
                          >
                            Save Sensor Profile
                          </button>
                        </div>
                      </div>
                    )
                  }

                  // ── Default Sensors tab view ──
                  return (
                    <div className="space-y-5">
                      {/* Select Device */}
                      <div>
                        <p className="text-sm  text-gray-700 mb-2">Select Device</p>
                        <div className="relative">
                          <select
                            value={activeSensorDevice}
                            onChange={(e) => setSensorSelectedDevice(e.target.value)}
                            className="w-full appearance-none px-3 py-2 border border-gray-300 rounded text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#358ECD] focus:border-transparent pr-8"
                          >
                            {siteDeviceList.length === 0 ? (
                              <option value="">No devices assigned</option>
                            ) : (
                              siteDeviceList.map(d => (
                                <option key={d.id} value={d.name}>{d.name}</option>
                              ))
                            )}
                          </select>
                          <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-500 pointer-events-none" />
                        </div>
                      </div>

                      {/* Sensor Profile List row */}
                      <div className="border border-gray-200 rounded p-3">
                        <div className="sensore-f flex items-center gap-2">
                          <span className="text-sm text-gray-600 whitespace-nowrap">Sensor Profile List</span>
                          <input
                            type="text"
                            value={sensorProfileName}
                            onChange={(e) => setSensorProfileName(e.target.value)}
                            className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#358ECD] focus:border-transparent"
                          />
                          <button
                            onClick={() => {
                              const found = sensorProfiles.find(p => p.name === sensorProfileName)
                              if (found) setSelectedSensorProfile(found)
                            }}
                            className="px-4 py-1.5 border border-[#358ECD] text-[#358ECD] text-sm rounded hover:bg-[#EBF5FB] transition whitespace-nowrap"
                          >
                            Load
                          </button>
                          <button
                            onClick={() => {
                              if (!sensorProfileName.trim()) return
                              const existing = sensorProfiles.find(p => p.name === sensorProfileName.trim())
                              if (!existing) {
                                const newProfile = { id: Date.now(), name: sensorProfileName.trim() }
                                setSensorProfiles(prev => [...prev, newProfile])
                                setSelectedSensorProfile(newProfile)
                              }
                            }}
                            className="px-4 py-1.5 border border-gray-300 text-gray-600 text-sm rounded hover:bg-gray-50 transition whitespace-nowrap"
                          >
                            Save
                          </button>
                        </div>

                        {/* Action buttons */}
                        <div className="flex items-center gap-2 mt-3">
                          <button
                            onClick={() => {
                              setSensorAvailableParams(['Temperature', 'ODOSat', 'Sample No'])
                              setSensorSelectedParams(['Conductivity (S/m)', 'Pressure (dbar)', 'ODO (mg/L)', 'Salinity (PPT)', 'Sound Speed (m/s)', 'Sp Cond (S/m)'])
                              setSensorFormManufacturer('Sea-Bird')
                              setSensorFormModel('HydroCAT SDI12')
                              setSensorFormAddress('0')
                              setSensorFormMeasCmd('M')
                              setSensorFormPort('P1')
                              setSensorFormPower('Switch')
                              setSensorFormWarmup('30')
                              setSensorFormTempUnit('C')
                              setShowSensorProfileForm(true)
                            }}
                            className="px-4 py-1.5 border border-[#358ECD] text-[#358ECD] text-sm rounded hover:bg-[#EBF5FB] transition"
                          >
                            Add
                          </button>
                          <button
                            disabled={!selectedSensorProfile}
                            onClick={() => selectedSensorProfile && setShowSensorProfileForm(true)}
                            className="px-4 py-1.5 border border-gray-300 text-gray-600 text-sm rounded hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              if (!selectedSensorProfile) return
                              setSensorProfiles(prev => prev.filter(p => p.id !== selectedSensorProfile.id))
                              setSelectedSensorProfile(null)
                              setSensorProfileName('Default')
                            }}
                            disabled={!selectedSensorProfile}
                            className="px-4 py-1.5 border border-gray-300 text-gray-600 text-sm rounded hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Delete
                          </button>
                          <div className="flex-1" />
                          <button
                            disabled={!selectedSensorProfile || !activeSensorDevice}
                            className="px-4 py-1.5 border border-gray-300 text-gray-600 text-sm rounded hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Deploy
                          </button>
                        </div>
                      </div>

                      {/* Profile list */}
                      {sensorProfiles.length > 0 && (
                        <div className="border border-gray-200 rounded overflow-hidden" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                          {sensorProfiles.map(profile => (
                            <div
                              key={profile.id}
                              onClick={() => { setSensorProfileName(profile.name); setSelectedSensorProfile(profile) }}
                              className={`px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 cursor-pointer transition ${
                                selectedSensorProfile?.id === profile.id
                                  ? 'bg-[#EBF5FB] text-[#358ECD] '
                                  : 'hover:bg-gray-50 text-gray-700'
                              }`}
                            >
                              {profile.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })()}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 flex justify-end flex-shrink-0 border-t border-gray-100">
                <button
                  onClick={handleSaveChanges}
                  disabled={isSavingDevices}
                  className="common-size15 px-6 py-2 bg-[#358ECD] hover:bg-[#2d7ab5] text-white text-sm font-semibold rounded-md transition disabled:opacity-60 flex items-center gap-2"
                >
                  {isSavingDevices && <Loader className="w-4 h-4 animate-spin" />}
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Create Site Modal */}
      {/* Delete Site Confirmation Modal */}
      {showDeleteSiteConfirm && siteToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-base font-bold text-gray-800">Delete Site</h2>
            </div>
            <div className="px-6 py-5">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Delete "{siteToDelete.name}"?</p>
                  <p className="text-xs text-gray-500 mt-1">This will permanently remove the site and all its associated data. This action cannot be undone.</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              {siteDeleteError && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 mb-3 w-full text-left">{siteDeleteError}</p>
              )}
              <button
                onClick={() => { setShowDeleteSiteConfirm(false); setSiteToDelete(null); setSiteDeleteError('') }}
                disabled={isDeletingSite}
                className="px-4 py-2 text-sm  text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmSiteDelete}
                disabled={isDeletingSite}
                className="px-4 py-2 text-sm  text-white bg-red-600 rounded hover:bg-red-700 transition flex items-center gap-2"
              >
                {isDeletingSite && <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="white" strokeWidth="4"/><path className="opacity-75" fill="white" d="M4 12a8 8 0 018-8v8z"/></svg>}
                Delete Site
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Logger (device) Confirmation Modal */}
      {showDeleteDeviceConfirm && deviceToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-base font-bold text-gray-800">Remove Logger</h2>
            </div>
            <div className="px-6 py-5">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Remove "{deviceToDelete.name}" from this project?</p>
                  <p className="text-xs text-gray-500 mt-1">The logger leaves this project and its site, but the device and all its historical data are kept — it can be re-assigned later. This does not delete the logger or its data.</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              {deviceDeleteError && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 mb-3 w-full text-left">{deviceDeleteError}</p>
              )}
              <button
                onClick={() => { setShowDeleteDeviceConfirm(false); setDeviceToDelete(null); setDeviceDeleteError('') }}
                disabled={isDeletingDevice}
                className="px-4 py-2 text-sm  text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeviceDelete}
                disabled={isDeletingDevice}
                className="px-4 py-2 text-sm  text-white bg-[#358ECD] rounded hover:bg-[#2d7ab5] transition flex items-center gap-2"
              >
                {isDeletingDevice && <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="white" strokeWidth="4"/><path className="opacity-75" fill="white" d="M4 12a8 8 0 018-8v8z"/></svg>}
                Remove Logger
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move/Reorder Site Confirmation Modal */}
      {showMoveSiteConfirm && pendingSiteMove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-base font-bold text-gray-800">Move Site</h2>
            </div>
            <div className="px-6 py-5">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3M2 12h20M12 2v20"/>
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Move "{pendingSiteMove.fromName}"?</p>
                  <p className="text-xs text-gray-500 mt-1">Reposition this site next to "{pendingSiteMove.toName}" in the sidebar.</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => { setShowMoveSiteConfirm(false); setPendingSiteMove(null) }}
                className="px-4 py-2 text-sm  text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmSiteMove}
                className="px-4 py-2 text-sm  text-white bg-[#358ECD] rounded hover:bg-blue-600 transition"
              >
                Confirm Move
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateSiteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-xl mx-4">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="common-head25 text-xl font-bold text-[#4D4D4D]">{editingSiteId ? 'Edit Site' : 'Create Site'}</h2>
            </div>

            {/* Error/Success Messages */}
            {siteError && (
              <div className="px-6 py-3 bg-red-50 border-b border-red-200">
                <p className="text-sm common-size15 text-[#4D4D4D]">{siteError}</p>
              </div>
            )}
            {siteSuccess && (
              <div className="px-6 py-3 bg-green-50 border-b border-green-200">
                <p className="text-sm common-size15 text-[#4D4D4D]">{siteSuccess}</p>
              </div>
            )}

            {/* Modal Body */}
            <div className="px-6 py-4 space-y-5">
              {/* Site Name */}
              <div>
                <label className="block text-sm common-size15 text-[#4D4D4D] mb-1">
                  Site Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newSiteName}
                  onChange={(e) => { setNewSiteName(e.target.value); setSiteError('') }}
                  placeholder="Site Name"
                  disabled={isCreatingSite}
                  className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 disabled:bg-gray-100 ${
                    _siteNameFieldError ? 'border-red-400 focus:ring-red-400 bg-red-50'
                    : _siteNameVal && !_siteNameInvalid ? 'border-green-400 focus:ring-green-400'
                    : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                {_siteNameFieldError
                  ? <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                      {_siteNameFieldError}
                    </p>
                  : <p className="text-xs text-gray-400 mt-1">Must begin with a letter; may contain letters, digits, spaces, dashes, and underscores.</p>
                }
              </div>

              {/* Site Description */}
              <div>
                <label className="block text-sm common-size15 text-[#4D4D4D] mb-1">
                  Site Description <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={newSiteDescription}
                  onChange={(e) => setNewSiteDescription(e.target.value)}
                  placeholder="Site Description"
                  rows="3"
                  disabled={isCreatingSite}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>

              {/* Coordinates Section */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm common-size15 text-[#4D4D4D] font-semibold mb-3">Coordinates</h3>
                <div className="flex items-start gap-2 mb-4">
                  <input
                    type="checkbox"
                    id="useDeviceGPS"
                    checked={useDeviceGPS}
                    onChange={(e) => setUseDeviceGPS(e.target.checked)}
                    disabled={isCreatingSite}
                    className="w-4 h-4 mt-0.5 rounded border-gray-300 cursor-pointer"
                  />
                  <div>
                    <label htmlFor="useDeviceGPS" className=" common-size15 text-[#4D4D4D] text-sm cursor-pointer ">Use Device GPS</label>
                    <p className="text-xs common-size12 text-[#4D4D4D] mt-0.5">If the logger has GPS, Latitude and Longitude will automatically update each time data arrives from the logger.</p>
                  </div>
                </div>
                {!useDeviceGPS && (
                  <div>
                    <p className="text-xs common-size15 text-[#4D4D4D] mb-4">Enter decimal values, e.g. <span className="font-mono common-size15 text-[#4D4D4D]">39.782875, -83.993666</span></p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs common-size15 text-[#4D4D4D] mb-2">Latitude</label>
                        <input
                          type="text"
                          value={newSiteLatitude}
                          onChange={(e) => { setNewSiteLatitude(e.target.value); setSiteError('') }}
                          placeholder="e.g. 39.782875"
                          disabled={isCreatingSite}
                          className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                            _siteLatError ? 'border-red-400 focus:ring-red-400 bg-red-50'
                            : _siteLatVal && !_siteLatError ? 'border-green-400 focus:ring-green-400'
                            : 'border-gray-300 focus:ring-blue-500'
                          }`}
                        />
                        {_siteLatError && <p className="text-xs common-size15 text-[#4D4D4D] mt-1">{_siteLatError}</p>}
                      </div>
                      <div>
                        <label className="block text-xs common-size15 text-[#4D4D4D] mb-2">Longitude</label>
                        <input
                          type="text"
                          value={newSiteLongitude}
                          onChange={(e) => { setNewSiteLongitude(e.target.value); setSiteError('') }}
                          placeholder="e.g. -83.993666"
                          disabled={isCreatingSite}
                          className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                            _siteLonError ? 'border-red-400 focus:ring-red-400 bg-red-50'
                            : _siteLonVal && !_siteLonError ? 'border-green-400 focus:ring-green-400'
                            : 'border-gray-300 focus:ring-blue-500'
                          }`}
                        />
                        {_siteLonError && <p className="text-xs text-red-500 mt-1">{_siteLonError}</p>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 space-y-3">
              <button
                onClick={async () => {
                  if (!_siteNameVal) { setSiteError('Site name is required.'); return }
                  if (_siteNameInvalid) { setSiteError(_siteNameFieldError); return }
                  if (_siteLatError) { setSiteError(_siteLatError); return }
                  if (_siteLonError) { setSiteError(_siteLonError); return }
                  const _siteSecErr = scanInput(newSiteName) || scanInput(newSiteDescription)
                  if (_siteSecErr) { setSiteError(_siteSecErr); return }
                  setIsCreatingSite(true)
                  setSiteError('')
                  setSiteSuccess('')
                  try {
                    // Use Device GPS on → let the logger own the coordinates
                    // (clear any manual value); off → send the entered lat/long.
                    const lat = useDeviceGPS ? '' : (_siteLatVal || '')
                    const lon = useDeviceGPS ? '' : (_siteLonVal || '')
                    if (editingSiteId) {
                      await projectsApi.updateSite(editingSiteId, {
                        name: _siteNameVal,
                        description: newSiteDescription.trim(),
                        autogps: useDeviceGPS ? 1 : 0,
                        latitude: lat,
                        longitude: lon,
                      })
                      // Merge the changed fields into the sidebar site list.
                      setProjectSites(prev => prev.map(s => s.id === editingSiteId ? {
                        ...s,
                        name: _siteNameVal,
                        description: newSiteDescription.trim(),
                        autogps: useDeviceGPS ? 1 : 0,
                        latitude: lat === '' ? null : parseFloat(lat),
                        longitude: lon === '' ? null : parseFloat(lon),
                      } : s))
                      setSiteSuccess(`Site "${_siteNameVal}" updated successfully!`)
                    } else {
                      const siteData = { name: _siteNameVal, description: newSiteDescription.trim() }
                      if (lat) siteData.latitude = parseFloat(lat)
                      if (lon) siteData.longitude = parseFloat(lon)
                      const response = await projectsApi.createSite(currentProject.id, siteData)
                      const newSite = response.site || response
                      setProjectSites([...projectSites, newSite])
                      setSiteSuccess(`Site "${_siteNameVal}" created successfully!`)
                    }
                    setTimeout(() => { closeSiteModal() }, 1200)
                  } catch (error) {
                    setSiteError(error.response?.data?.message || error.message
                      || (editingSiteId ? 'Error updating site' : 'Error creating site'))
                  } finally {
                    setIsCreatingSite(false)
                  }
                }}
                disabled={!_isSiteFormValid || isCreatingSite}
                className="w-full px-4 py-2 text-sm font-bold text-white bg-[#358ECD] hover:bg-blue-600 rounded transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isCreatingSite
                  ? <><svg className=" common-size15 animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="white" strokeWidth="4"/><path className="opacity-75" fill="white" d="M4 12a8 8 0 018-8v8z"/></svg>{editingSiteId ? 'Saving...' : 'Creating...'}</>
                  : (editingSiteId ? 'SAVE CHANGES' : 'CREATE SITE')
                }
              </button>
              <button
                onClick={closeSiteModal}
                disabled={isCreatingSite}
                className="w-full px-4 py-2 text-sm  common-size15 text-[#358ECD] hover:text-blue-700 transition disabled:opacity-50"
              >Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Parameter Configuration Modal — ported from the legacy
          Dashboard "Parameter Settings" popup. Conditionally rendered so
          state resets every open. */}
      {showParamConfigModal && (
        <ParameterConfigModal
          open={true}
          onClose={async () => {
            setShowParamConfigModal(false)
            // Re-fetch sites so the sidebar reflects any name/visibility changes
            // without requiring a full page reload.
            try {
              const refreshed = await projectsApi.fetchProjectSites(currentProject.id)
              if (refreshed) setProjectSites(refreshed)
            } catch { /* non-critical — sidebar will be stale until next nav */ }
          }}
          onSaved={() => {
            // A parameter's unit Convert / Sensor min-max / precision just
            // changed — bump the version so widgets re-fetch fresh graph
            // data instead of continuing to show the pre-save response.
            // Fires on every successful save, not just Cancel/close, so a
            // no-op "open and cancel" doesn't trigger a redundant re-fetch.
            setParamConfigVersion((v) => v + 1)
          }}
          sites={projectSites}
          initialSiteId={projectSites?.[0]?.id || null}
          initialDeviceId={projectSites?.[0]?.devices?.[0]?.id || null}
        />
      )}

      {/* Configure Dashboards Modal — conditional render so it remounts fresh
          each time (guarantees items state re-initialises from current templates) */}
      <AdminSignInModal
        open={adminSignInOpen}
        email={currentUserEmail || ''}
        onCancel={() => setAdminSignInOpen(false)}
        onSuccess={() => { setAdminSignInOpen(false); setIsCustomizingDashboard(true) }}
      />

      {showConfigureDashboardsModal && (
        <ConfigDashboardsModal
          open={true}
          editMode={isCustomizingDashboard}
          projectId={projectId}
          templates={templates}
          autoRefreshSeconds={autoRefreshSeconds}
          onSaveAutoRefresh={async (seconds) => {
            // Project-level idle-refresh timeout. Clamp to the 300s (5-min)
            // minimum, apply optimistically, then persist to the backend.
            const clamped = Math.max(300, Math.round(Number(seconds) || 600))
            setAutoRefreshSeconds(clamped)
            try {
              await dashboardApi.saveAutoRefreshConfig(projectId, clamped)
            } catch (e) {
              console.error('[ConfigureDashboards] save auto-refresh failed', e)
              window.alert('Failed to save the auto-refresh interval. Please try again.')
            }
            return clamped
          }}
          onClose={() => setShowConfigureDashboardsModal(false)}
          onSaved={(reordered) => {
            // Update templates state → nav dropdown reflects new order immediately.
            const reorderedSig = `${projectId}:${reordered.map((t) => t.id).join(',')}`
            presOrderSigRef.current = reorderedSig  // prevent useEffect re-fetch loop
            const currentTemplateName = templates[currentMenuIndex]?.name
            setTemplates(reordered)
            const newIdx = reordered.findIndex((t) => t.name === currentTemplateName)
            if (newIdx >= 0 && newIdx !== currentMenuIndex) setCurrentMenuIndex(newIdx)
            setShowConfigureDashboardsModal(false)
          }}
          onAddDashboard={() => {
            setShowConfigureDashboardsModal(false)
            setShowAddDashboardModal(true)
            setShowDashboardDetailsModal(false)
            setSelectedTemplateForDetails(null)
            setDashboardName('')
            setDashboardDetails('')
            setSelectedTemplate(null)
            setDashboardError('')
            setDashboardSuccess('')
          }}
          onResetDashboard={async (template) => {
            if (!template?.id || !projectId) return
            if (!window.confirm(`Reset "${template.name}" to its default layout?`)) return
            try {
              await dashboardApi.resetUserLayout(template.id, { project_id: projectId })
              // Force the grid to reload if this is the active template
              if (templates[currentMenuIndex]?.id === template.id) {
                setCurrentMenuIndex(currentMenuIndex)
              }
            } catch (e) {
              console.error('[ConfigureDashboards] reset failed', e)
              window.alert('Failed to reset dashboard. Please try again.')
            }
          }}
          onArchiveChanged={async () => {
            // Refetch the project's templates list so the nav dropdown drops
            // archived dashboards (or picks up restored ones) immediately.
            if (!currentProject?.id) return
            try {
              const res = await dashboardApi.getProjectTemplates(currentProject.id)
              const list = Array.isArray(res.data?.templates || res.data)
                ? (res.data?.templates || res.data)
                : []
              if (list.length || true) {
                setTemplates(list)
                // If the active dashboard was archived, fall back to the first
                // remaining one so the user isn't left on a hidden template.
                const activeId = templates[currentMenuIndex]?.id
                if (activeId && !list.some(t => t.id === activeId)) {
                  setCurrentMenuIndex(0)
                }
              }
            } catch (e) {
              console.warn('[ConfigureDashboards] refresh templates failed', e)
            }
          }}
        />
      )}

      {/* Add Dashboard Modal - Template Selection */}
      {showAddDashboardModal && !showDashboardDetailsModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowAddDashboardModal(false)
            setSelectedTemplate(null)
            setDashboardError('')
            setDashboardSuccess('')
            setAddDashboardSearch('')
          }}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header: title + search + close */}
            <div className="break-mb px-6 pt-5 pb-4 flex items-center gap-4 flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-800">Add Dashboard</h2>
              <div className="flex-1" />
              <div className="relative w-64">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={addDashboardSearch}
                  onChange={(e) => setAddDashboardSearch(e.target.value)}
                  placeholder="Search Dashboards"
                  className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#358ECD]"
                />
              </div>
              <button
                onClick={() => {
                  setShowAddDashboardModal(false)
                  setSelectedTemplate(null)
                  setDashboardError('')
                  setDashboardSuccess('')
                  setAddDashboardSearch('')
                }}
                className="cross-bt text-gray-400 hover:text-gray-600 transition"
                aria-label="Close"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            {/* Modal Body - Template Selection */}
            <div className="overflow-y-auto flex-1 px-6 pb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Templates</h3>

              {loadingPredefinedTemplates ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader className="w-8 h-8 animate-spin text-blue-500 mb-3" />
                  <p className="text-sm text-gray-500">Loading templates...</p>
                </div>
              ) : (() => {
                const q = addDashboardSearch.trim().toLowerCase()
                // Only the canonical predefined templates appear here — not
                // the customer's own previously-added dashboards.
                const visibleTemplates = q
                  ? predefinedTemplates.filter(t => t.name.toLowerCase().includes(q))
                  : predefinedTemplates
                const showBlank = !q || 'blank'.includes(q)
                if (visibleTemplates.length === 0 && !showBlank) {
                  return (
                    <p className="py-8 text-center text-gray-400 text-sm">
                      No templates match your search.
                    </p>
                  )
                }
                return (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {visibleTemplates.map((template) => (
                      <button
                        key={template.id}
                        onClick={() => {
                          // XD flow: opening a template card moves to the
                          // "Provide name and details" step. Pre-fill the
                          // Details field with "Created from <Name> Template"
                          // matching the design screenshot.
                          setSelectedTemplateForDetails(template)
                          setDashboardName('')
                          setDashboardDetails(`Created from ${template.name} Template`)
                          setDashboardError('')
                          setDashboardSuccess('')
                          setShowDashboardDetailsModal(true)
                        }}
                        disabled={isCreatingDashboard}
                        className="p-0 border border-gray-200 rounded-lg transition overflow-hidden bg-white hover:border-[#358ECD] hover:shadow-md cursor-pointer flex flex-col text-left"
                      >
                        <div className="w-full h-32 bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center overflow-hidden border-b border-gray-200">
                          {template.image ? (
                            <img
                              src={template.image}
                              alt={template.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none'
                                e.target.parentElement.innerHTML = '<div class="text-3xl">📊</div>'
                              }}
                            />
                          ) : (
                            <div className="text-3xl">📊</div>
                          )}
                        </div>
                        <div className="px-3 py-2">
                          <p className="text-sm text-gray-800">{template.name}</p>
                        </div>
                      </button>
                    ))}

                    {showBlank && (
                      <button
                        onClick={() => {
                          // Blank uses the same details step; backend treats
                          // template='blank' as a fresh empty dashboard.
                          setSelectedTemplateForDetails({ id: 'blank', name: 'Blank' })
                          setDashboardName('')
                          setDashboardDetails('')
                          setDashboardError('')
                          setDashboardSuccess('')
                          setShowDashboardDetailsModal(true)
                        }}
                        disabled={isCreatingDashboard}
                        className="p-0 border border-dashed border-gray-300 rounded-lg transition overflow-hidden bg-white hover:border-[#358ECD] hover:bg-blue-50 cursor-pointer flex flex-col text-left"
                      >
                        <div className="w-full h-32 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center border-b border-gray-200">
                          <div className="text-4xl">➕</div>
                        </div>
                        <div className="px-3 py-2">
                          <p className="text-sm text-gray-800">Blank</p>
                        </div>
                      </button>
                    )}
                  </div>
                )
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Dashboard Details Modal - After Template Selection */}
      {showDashboardDetailsModal && selectedTemplateForDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-800">Add Dashboard</h2>
            </div>

            {/* Error/Success Messages */}
            {dashboardError && (
              <div className="px-6 py-3 bg-red-50 border-b border-red-200">
                <p className="text-sm  text-red-700">{dashboardError}</p>
              </div>
            )}
            {dashboardSuccess && (
              <div className="px-6 py-3 bg-green-50 border-b border-green-200">
                <p className="text-sm  text-green-700">{dashboardSuccess}</p>
              </div>
            )}

            {/* Modal Body */}
            <div className="px-6 py-4 space-y-4">
              {/* Dashboard Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">Dashboard Name</label>
                <SecureInput
                  type="text"
                  value={dashboardName}
                  onChange={(e) => {
                    setDashboardName(e.target.value)
                    setDashboardError('')
                  }}
                  placeholder="Enter dashboard name..."
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isCreatingDashboard}
                />
              </div>

              {/* Details */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">Details</label>
                <textarea
                  value={dashboardDetails}
                  onChange={(e) => {
                    setDashboardDetails(e.target.value)
                    setDashboardError('')
                  }}
                  placeholder={selectedTemplateForDetails?.id === 'blank'
                    ? 'Describe your dashboard…'
                    : `Created from ${selectedTemplateForDetails.name} Template`}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#358ECD] resize-none"
                  disabled={isCreatingDashboard}
                />
              </div>
            </div>

            {/* Modal Footer: full-width primary button + Cancel link below (matches XD) */}
            <div className="px-6 pb-6 pt-2">
              <button
                onClick={async () => {
                  // Block re-entrant/duplicate submits synchronously — a second
                  // click in the same tick would otherwise create a 2nd dashboard.
                  if (creatingDashboardRef.current) return
                  if (!dashboardName.trim()) {
                    setDashboardError('Please enter a dashboard name')
                    return
                  }
                  const _dbSecErr = scanInput(dashboardName) || scanInput(dashboardDetails)
                  if (_dbSecErr) { setDashboardError(_dbSecErr); return }

                  creatingDashboardRef.current = true
                  setIsCreatingDashboard(true)
                  setDashboardError('')

                  try {
                    const templateRef = selectedTemplateForDetails.id === 'blank'
                      ? 'blank'
                      : selectedTemplateForDetails.id
                    console.log('[Add Dashboard] Creating dashboard:', {
                      name: dashboardName.trim(),
                      template: templateRef,
                      details: dashboardDetails.trim() ||
                        (selectedTemplateForDetails.id === 'blank'
                          ? 'Custom dashboard'
                          : `Created from ${selectedTemplateForDetails.name} Template`),
                    })

                    const response = await projectsApi.createDashboard(currentProject.id, {
                      name: dashboardName.trim(),
                      template: templateRef,
                      description: dashboardDetails.trim() ||
                        (selectedTemplateForDetails.id === 'blank'
                          ? 'Custom dashboard'
                          : `Created from ${selectedTemplateForDetails.name} Template`),
                    })

                    console.log('[Add Dashboard] Dashboard created:', response)

                    // Refresh the project templates so the new dashboard appears
                    // in the nav dropdown immediately. The dashboard create
                    // endpoint returns { dashboard: { id, name, ... } }.
                    try {
                      const tplRes = await dashboardApi.getProjectTemplates(currentProject.id)
                      const list = Array.isArray(tplRes.data?.templates || tplRes.data)
                        ? (tplRes.data?.templates || tplRes.data)
                        : []
                      if (list.length) {
                        setTemplates(list)
                        const newId = response.dashboard?.id ?? response.id
                        const newIdx = list.findIndex(t => t.id === newId)
                        if (newIdx >= 0) setCurrentMenuIndex(newIdx)
                      }
                    } catch (e) {
                      console.warn('[Add Dashboard] refresh templates failed', e)
                    }

                    setDashboardSuccess(`Dashboard "${dashboardName.trim()}" created.`)

                    setTimeout(() => {
                      setShowAddDashboardModal(false)
                      setShowDashboardDetailsModal(false)
                      setSelectedTemplateForDetails(null)
                      setDashboardName('')
                      setDashboardDetails('')
                      setDashboardSuccess('')
                      // Release the guard only once the modal has closed, so the
                      // success window (button visible) can't spawn a 2nd create.
                      creatingDashboardRef.current = false
                    }, 900)
                  } catch (error) {
                    console.error('[Add Dashboard] Error:', error)
                    const detail = error.response?.data?.error
                      || error.response?.data?.message
                      || error.message
                      || 'Error creating dashboard'
                    setDashboardError(detail)
                    // Failed — release the guard so the user can retry.
                    creatingDashboardRef.current = false
                  } finally {
                    setIsCreatingDashboard(false)
                  }
                }}
                disabled={!dashboardName.trim() || isCreatingDashboard}
                className="w-full px-4 py-2.5 text-sm font-semibold text-white bg-[#358ECD] hover:bg-[#2c7ab2] rounded transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isCreatingDashboard ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Creating…
                  </>
                ) : (
                  'Add Dashboard to Project'
                )}
              </button>
              <button
                onClick={() => {
                  setShowDashboardDetailsModal(false)
                  setSelectedTemplateForDetails(null)
                  setDashboardName('')
                  setDashboardDetails('')
                  setDashboardError('')
                }}
                disabled={isCreatingDashboard}
                className="w-full mt-3 text-sm text-[#358ECD] hover:underline disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Widgets Modal */}
      {showAddWidgetsModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]"
          style={addWidgetModalHidden ? { opacity: 0, pointerEvents: 'none' } : {}}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-6xl max-h-[90vh] overflow-auto w-full mx-4">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="common-head25 font-bold text-[#4D4D4D]">Add Widgets</h2>
              <input 
                type="text" 
                placeholder="Search Widgets" 
                value={searchWidgetsQuery}
                onChange={(e) => setSearchWidgetsQuery(e.target.value.toLowerCase())}
                className="max-w-sm px-3 py-1 border border-gray-300 rounded text-sm flex-1 ml-auto mr-4 "
              />
              <button 
                onClick={() => setShowAddWidgetsModal(false)}
                className="p-1 text-gray-500 hover:bg-gray-100 rounded transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content - Widget Categories */}
            <div className="px-6 py-4">
              {/* Loading State */}
              {loadingWidgetCategories && (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
                    <p className="text-gray-600">Loading widgets...</p>
                  </div>
                </div>
              )}

              {/* Error State */}
              {widgetCategoriesError && !loadingWidgetCategories && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  <p className="font-semibold">Error loading widgets</p>
                  <p className="text-sm mt-1">{widgetCategoriesError}</p>
                  <button
                    onClick={() => loadWidgetCategories()}
                    className="mt-3 px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {/* Widget Categories */}
              {!loadingWidgetCategories && !widgetCategoriesError && (
                <>
                  {dynamicWidgetCategories.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <p>No widget categories available</p>
                    </div>
                  ) : (
                    dynamicWidgetCategories
                      // Panels categories (Weather / Water / Soil / Other)
                      // belong to the Current Conditions "+ Add Panel"
                      // modal, not this general Add Widgets flow. Hide
                      // them here so users don't see the same tiles
                      // twice in two different modals.
                      .filter((cg) => {
                        const key = (cg.category?.category_key || '').toLowerCase()
                        const name = (cg.category?.name || '').toLowerCase()
                        if (['weather', 'water', 'soil', 'other'].includes(key)) return false
                        if (name.startsWith('panels')) return false
                        return true
                      })
                      .map((categoryGroup) => {
                      const category = categoryGroup.category
                      const widgets = categoryGroup.widgets || []

                      // Filter widgets by search query
                      const filteredWidgets = widgets.filter(widget =>
                        !searchWidgetsQuery ||
                        widget.name.toLowerCase().includes(searchWidgetsQuery) ||
                        widget.widget_type.toLowerCase().includes(searchWidgetsQuery) ||
                        (widget.description && widget.description.toLowerCase().includes(searchWidgetsQuery))
                      )

                      // Skip empty categories after filtering
                      if (filteredWidgets.length === 0 && searchWidgetsQuery) {
                        return null
                      }

                      return (
                        <div key={category.id || category.name} className="mb-6 border-b pb-4">
                          <h3 className="common-size18 text-sm font-bold text-[#4D4D4D] mb-3">
                            {category.name} ({widgets.length})
                          </h3>
                          <div className="grid grid-cols-5 gap-3">
                            {filteredWidgets.map((widget) => (
                              <div
                                key={widget.id}
                                draggable
                                unselectable="on"
                                onDragStart={(e) => {
                                  e.dataTransfer.effectAllowed = 'copy'
                                  e.dataTransfer.setData('text/plain', String(widget.id))
                                  setDraggingWidget(widget)
                                  // Hide (not unmount) the modal so the grid is exposed as drop target
                                  // while keeping drag source in DOM to prevent premature dragend
                                  setAddWidgetModalHidden(true)
                                }}
                                onDragEnd={() => {
                                  // If drop didn't fire (drag cancelled), clean up
                                  setDraggingWidget(null)
                                  setAddWidgetModalHidden(false)
                                  setShowAddWidgetsModal(false)
                                }}
                                onClick={() => {
                                  if (widget.widget_type === 'wind_rose') {
                                    // Show editor first; widget is placed after the user saves config.
                                    setPendingWindRoseWidget(widget)
                                    setShowAddWidgetsModal(false)
                                  } else if (Object.prototype.hasOwnProperty.call(PRE_ADD_CONFIG_PREFIX, widget.widget_type)) {
                                    // Same "configure before placing" pattern as Wind Rose, for the
                                    // other widget types that have per-instance settings.
                                    setPendingConfigWidget(widget)
                                    setShowAddWidgetsModal(false)
                                  } else {
                                    gridRef.current?.addWidget(widget)
                                    setShowAddWidgetsModal(false)
                                  }
                                }}
                                className="flex flex-col items-center justify-center p-3 rounded border border-gray-300 hover:border-[#358ECD] hover:bg-blue-50 transition cursor-grab active:cursor-grabbing h-32 select-none"
                                title={`Click to add · Drag to place: ${widget.name}`}
                              >
                                {widget.preview_image ? (
                                  <img
                                    src={widget.preview_image}
                                    alt={widget.name}
                                    className="w-16 h-16 object-contain mb-1 rounded pointer-events-none"
                                  />
                                ) : (
                                  <div className="text-2xl mb-1 pointer-events-none">{widget.icon || '📦'}</div>
                                )}
                                <div className="common-size15 text-xs text-center text-[#4D4D4D] line-clamp-2 pointer-events-none mt-4">{widget.name}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    })
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Wind Rose pre-configuration modal — shown when user clicks Wind Rose in the Add Widgets catalog.
          Settings are written to the widget config cache before the widget is placed so that the
          WindRoseWidget reads them immediately on first mount. */}
      <WindRoseEditorModal
        open={pendingWindRoseWidget != null}
        projectId={currentProject?.id}
        initialSettings={{}}
        onClose={() => setPendingWindRoseWidget(null)}
        onSave={(settings) => {
          if (!pendingWindRoseWidget) return
          const newId = `wnew_${Date.now()}`
          // Store settings in cache + localStorage so WindRoseWidget finds them on first mount.
          try { localStorage.setItem(`wq_windrose_p${currentProject?.id || 'noproject'}_w${newId}`, JSON.stringify(settings)) } catch {}
          setWidgetConfig('wq_windrose', currentProject?.id, newId, settings)
          gridRef.current?.addWidget({ ...pendingWindRoseWidget, _preAssignedId: newId })
          setPendingWindRoseWidget(null)
        }}
      />

      {/* Configure-before-placing flow for Gauge Series, Bullet Graph, Solid Gauge,
          Support Our Project, Project Photo, Gallery, Webcam, and Field Notes — same
          pattern as Wind Rose above: settings are written to the widget config cache
          before the widget is placed so it reads them immediately on first mount. */}
      {(() => {
        const pendingType = pendingConfigWidget?.widget_type
        const prefix = pendingType != null ? PRE_ADD_CONFIG_PREFIX[pendingType] : undefined

        // Scoped-config widgets (everything except the rich-content types) share one
        // commit path: generate the widget's id up front, seed its config cache under
        // that id, then place it with that id pre-assigned.
        const commitScopedWidget = (settings) => {
          if (!pendingConfigWidget || !prefix) return
          const newId = `wnew_${Date.now()}`
          try { localStorage.setItem(`${prefix}_p${currentProject?.id || 'noproject'}_w${newId}`, JSON.stringify(settings)) } catch {}
          setWidgetConfig(prefix, currentProject?.id, newId, settings)
          gridRef.current?.addWidget({ ...pendingConfigWidget, _preAssignedId: newId })
          setPendingConfigWidget(null)
        }

        // Custom HTML / Project Description / Disclaimer don't use the scoped
        // localStorage-config channel — their content lives directly on the widget
        // object (title/description/rich_content, mirrored into custom_* fields so
        // "Save Customization" persists it), same as post-add edits via
        // WidgetConfigModal (TemplateDashboardGrid.jsx's onSaved handler).
        const commitRichContentWidget = (settings) => {
          if (!pendingConfigWidget) return
          const newId = `wnew_${Date.now()}`
          gridRef.current?.addWidget({
            ...pendingConfigWidget,
            _preAssignedId: newId,
            title: settings.title || pendingConfigWidget.name,
            description: settings.description || '',
            rich_content: settings.richContent || '',
            custom_title: settings.title || '',
            custom_description: settings.description || '',
            custom_rich_content: settings.richContent || '',
          })
          setPendingConfigWidget(null)
        }

        return (
          <>
            <GaugeSeriesEditorModal
              open={pendingType === 'gauge_series'}
              projectId={currentProject?.id}
              initialSettings={{}}
              onClose={() => setPendingConfigWidget(null)}
              onSave={commitScopedWidget}
            />
            <BulletGraphEditorModal
              open={pendingType === 'bullet_graph'}
              projectId={currentProject?.id}
              initialSettings={{}}
              onClose={() => setPendingConfigWidget(null)}
              onSave={commitScopedWidget}
            />
            <SolidGaugeEditorModal
              open={pendingType === 'solid_gauge'}
              projectId={currentProject?.id}
              initialSettings={{}}
              onClose={() => setPendingConfigWidget(null)}
              onSave={commitScopedWidget}
            />
            {/* No pre-add prompt for data_box any more: the Data Box row is
                driven by the sidebar selection, so there is nothing to pick
                when the widget is added. */}
            <SupportProjectEditorModal
              open={pendingType === 'support_project' || pendingType === 'donation'}
              initialDonateUrl=""
              initialSponsors={[]}
              onClose={() => setPendingConfigWidget(null)}
              onSave={commitScopedWidget}
            />
            <ChangeProjectPhotoModal
              open={pendingType === 'project_photo'}
              currentUrl=""
              onClose={() => setPendingConfigWidget(null)}
              onSave={commitScopedWidget}
            />
            <GalleryEditorModal
              open={pendingType === 'gallery'}
              projectId={currentProject?.id}
              initialAlbumId={null}
              onClose={() => setPendingConfigWidget(null)}
              onSave={commitScopedWidget}
            />
            <ChangeWebcamModal
              open={pendingType === 'webcam'}
              projectId={currentProject?.id}
              initialWebcamId={null}
              onClose={() => setPendingConfigWidget(null)}
              onSave={commitScopedWidget}
            />
            <FieldNotesPreAddModal
              open={pendingType === 'field_notes'}
              initialSettings={{}}
              onClose={() => setPendingConfigWidget(null)}
              onSave={commitScopedWidget}
            />
            <RichContentPreAddModal
              open={RICH_CONTENT_PRE_ADD_TYPES.has(pendingType)}
              widgetType={pendingType || 'custom_html'}
              initialSettings={{
                title: pendingConfigWidget?.name || '',
                description: pendingConfigWidget?.description || '',
                richContent: pendingConfigWidget?.rich_content || '',
              }}
              onClose={() => setPendingConfigWidget(null)}
              onSave={commitRichContentWidget}
            />
          </>
        )
      })()}

      {/* Reset Dashboard Modal */}
      {showResetDashboardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            {/* Modal Header */}
            <div className="px-6 pt-8 border-gray-200 text-center">
              <h2 className="common-head25 text-lg font-bold text-[#4D4D4D]">Reset Dashboard to Default</h2>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-6">
              <p className="text-[#45566C] text-center font-semibold mb-2 common-size18">
                Are you sure you want to reset the dashboard to default settings? This action CANNOT be undone once completed.
              </p>
            </div>

            {/* Modal Footer */}
            <div className="px-6 pb-6 border-gray-200 flex gap-3 justify-center">
              <button
                onClick={() => setShowResetDashboardModal(false)}
                className="common-size18 flex-1 px-4 py-2 text-sm font-bold text-[#358ECD] border-2 border-[#358ECD] rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await gridRef.current?.reset()
                  setShowResetDashboardModal(false)
                  setIsCustomizingDashboard(false)
                }}
                className="common-size18 flex-1 px-4 py-2 text-sm font-bold text-white bg-[#D94141] rounded-lg transition"
              >
                Reset to Default
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Presentation Settings modal (Symfony parity). On save we refresh
          the local config + rotation so Play picks up the new values without
          a page reload. */}
      <PresentationSettingsModal
        open={showPresentationSettings}
        projectId={projectId}
        onClose={() => setShowPresentationSettings(false)}
        onSaved={(cfg) => {
          setPresentationConfig({
            pauseSeconds: Math.max(30, cfg.pause_seconds || 30),
            transition: cfg.transition || 'fade',
            randomOrder: !!cfg.random_order,
          })

          // Reorder templates to match the saved presentation order so the
          // nav dropdown immediately reflects the new order.
          const orderedDashboards = cfg.dashboards || []
          const reordered = []
          orderedDashboards.forEach((d) => {
            const t = templates.find((t) => t.name === d.name || t.id === d.id)
            if (t) reordered.push(t)
          })
          templates.forEach((t) => {
            if (!reordered.find((r) => r.id === t.id)) reordered.push(t)
          })

          const reorderedSig = `${projectId}:${reordered.map((t) => t.id).join(',')}`
          presOrderSigRef.current = reorderedSig // prevent useEffect from re-fetching

          // Build rotation using the NEW template order
          const included = orderedDashboards.filter((d) => d.included)
          const rotation = included
            .map((d) => {
              let idx = reordered.findIndex((t) => t.name === d.name)
              if (idx < 0) idx = reordered.findIndex((t) => t.id === d.id)
              return idx
            })
            .filter((idx) => idx >= 0)
          setPresentationRotation(rotation)

          // Apply the new template order, preserving the currently viewed tab
          const currentTemplateName = templates[currentMenuIndex]?.name
          setTemplates(reordered)
          const newIdx = reordered.findIndex((t) => t.name === currentTemplateName)
          if (newIdx >= 0 && newIdx !== currentMenuIndex) {
            setCurrentMenuIndex(newIdx)
          }
        }}
      />

    </div>
    </DashboardContext.Provider>
  )
}